import { push } from "connected-react-router";

import numeral from "numeral";
import moment from "moment";

import { toast } from "react-toastify";
import isEqual from "lodash/isEqual";

import { runModels } from "api/ModelAPI";
import * as DatasetAPI from "api/DatasetAPI";

import { SegmentMetric } from "utils/optimizer/ProjectOptimizer";

import { getIsDefaultAccount } from "containers/Accounts/reducer";
import { updateEnableButton, updateStep, updateLastRunId } from "containers/CreateWizard/reducer";
import { getDataset, getFieldType } from "containers/Home/reducer";
import { pollJob, getJob, getSpecialIndex, updateJob } from "containers/Run/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as transformReducer from "containers/Dataset/reducer";
import { updateLastRunId as updateLastProjectRunId } from "containers/OldProject/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as wizardReducer from "containers/Wizard/reducer";
import * as runReducer from "containers/Run/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as unifiedProjectReducer from "containers/UnifiedProject/reducer";
import * as projectOptimizer from "utils/optimizer/ProjectOptimizer";
import * as globalReducer from "containers/App/reducer";
import * as deploymentsReducer from "containers/Deployments/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as galleryReducer from "containers/Gallery/GalleryReducer";

import * as datasetTypes from "types/dataset";
import { ConfusionMatrix, ConfusionMatrixFactoryFactory } from "utils/stats";

import { isEnabled } from "utils/featureFlags";
import { setup } from "components/Group/style.module.css";
import { conditionListHolder } from "components/UnifiedProject/SelectData/style.module.css";

import { ConditionType, ComparisonCondition } from "types/condition";
import { comparisonCondition } from "types/expression";

export const UPDATE_SETUP = "setup/UPDATE_SETUP";
export const UPDATE_SETUPS = "setup/UPDATE_SETUPS";
export const UPDATE_FIELD = "setup/UPDATE_FIELD";
export const UPDATE_FIELDS = "setup/UPDATE_FIELDS";
export const DELETE_FIELD = "setup/DELETE_FIELD";
export const UPDATE_COSTS = "setup/UPDATE_COSTS";
export const UPDATE_COUNTS = "setup/UPDATE_COUNTS";
export const UPDATE_IGNORED = "setup/UPDATE_IGNORED";
export const UPDATE_MODELS = "setup/UPDATE_MODELS";
export const UPDATE_VALUE_IGNORED = "setup/UPDATE_VALUE_IGNORED";
export const UPDATE_CUSTOM_VALUE = "setup/UPDATE_CUSTOM_VALUE";
export const UPDATE_BASELINE_CUSTOM_VALUE = "setup/UPDATE_BASELINE_CUSTOM_VALUE";
export const UPDATE_CUSTOM_MAPPING = "setup/UPDATE_CUSTOM_MAPPING";
export const DELETE_CUSTOM_MAPPING = "setup/DELETE_CUSTOM_MAPPING";
export const UPDATE_FIELD_TYPE = "setup/UPDATE_FIELD_TYPE";
export const UPDATE_ADVANCED_INPUT = "setup/UPDATE_ADVANCED_INPUT";
export const UPDATE_CUSTOM_FIELD = "setup/UPDATE_CUSTOM_FIELD";
export const DELETE_CUSTOM_FIELD = "setup/DELETE_CUSTOM_FIELD";

const MIN_AI_READY_FIELDS = 1;
const AI_SIGNAL_RANGE = {
  min: 0.5,
  max: 2.0,
  shapMin: 0.02
};

export const HIGH_CORRELATION_CUTOFF = 0.95;

export const AI_READINESS_PREDICTOR_WARNING = {
  count: 5,
  value: 0.7
};

export const AI_READINESS_BREAKPOINT = {
  NO_SIGNAL: 0.05,
  LOW_SIGNAL: 0.2,
  HIGH_SIGNAL: 0.9,
  SUSPECT_SIGNAL: Infinity
};

const PROBLEMATIC_PREDICTOR_RANGE = {
  min: 0.3,
  max: 2.0
};

export const DEFAULT_LABELS = {
  transactionLabel: "Transactions",
  notPursuedTransactionLabel: "Records",
  outcomeLabel: "Success",
  trueAction: "Action",
  falseAction: "Ignore",
  unitType: "$",
  advancedCostLabel: "Effort",
  advancedRevenueLabel: "Success",
  netImpactLabel: "Impact",
  numberPredicted: "Price",
  contAction: "Sell",
  underPredictAction: "Underestimate Penalty",
  overPredictAction: "Overestimate Penalty"
};

export const BusinessConstraintMode = {
  Percent: "percent",
  Number: "number"
};

export const BusinessConstraintDirection = {
  Above: "min",
  Below: "max",
  Between: "between",
  None: "none"
};

const initialState = {};

export const defaultSetup = {
  datasetId: undefined,

  conversionRate: 5,
  maximumConversionRate: 5,

  conversionGain: 1,
  conversionCost: -1,
  conversionMissed: 0,
  conversionSaved: 0,
  advancedRevenue: null,
  advancedCost: null,

  threshold: [0, 0],

  targeted: 2000,
  total: 20000,

  truePositive: 0,
  falsePositive: 0,
  trueNegative: 0,
  falseNegative: 0,

  overestimateLabel: "Overestimate",
  underestimateLabel: "Underestimate",

  truePositiveLabel: "True Positive",
  falsePositiveLabel: "False Positive",
  trueNegativeLabel: "True Negative",
  falseNegativeLabel: "False Negative",

  processLimit: 0,
  aimToHit: 800,
  aiTrainingBudget: 200,

  useCase: "Sales Prediction",

  hideCosts: false,
  isQuickRun: false,

  outcome: undefined,
  success: undefined,
  goal: "Maximize",

  label: "New Project",

  timePeriod: "Month",

  runType: undefined,

  mode: 2,

  showBusiness: true,

  models: [],
  modelOptions: [],

  humanUnderstandable: false,
  auditable: false,
  realTimeResults: false,

  ignored: {},
  ignoredModels: {},
  baselineCustomValues: {},
  customValues: {},
  customMappings: {},
  advancedInput: {},

  fieldReplace: {},

  fieldTypes: {},

  isBlueprint: false,

  endState: [],

  selectedMiniBlueprint: undefined,
  selectedBlueprintId: undefined,
  academicResultsStep: 0,
  isForceOptimized: false,

  linkedProjectId: undefined,
  linkedScenarioId: undefined,
  linkedProjectOutputMode: undefined,
  hasLinkedProject: false,

  businessSubgroups: undefined,

  enableFairnessMetrics: false,
  fairnessObjective: projectReducer.FAIRNESS_OBJECTIVE.NONE,
  businessObjective: projectReducer.BUSINESS_OBJECTIVE.PROFIT,
  biasReduction: projectReducer.BIAS_REDUCTION.IMPACT,
  businessConstraints: [
    {
      type: SegmentMetric.Profit,
      mode: BusinessConstraintMode.Percent
    }
  ],
  fairnessToleranceBound: 1,

  ...DEFAULT_LABELS
};

// This is the label appended to an academic run
const ACADEMIC_LABEL = " - Academic";
const OPTIMIZED_LABEL = " - Optimized";

export const TIME_PERIOD_OPTIONS = [
  { value: "Month", label: "Month" },
  { value: "Day", label: "Day" },
  { value: "Week", label: "Week" },
  { value: "Quarter", label: "Quarter" },
  { value: "Year", label: "Year" }
];

export const TIME_PERIOD_TRANSLATION = {
  Month: "Monthly",
  Week: "Weekly",
  Day: "Daily",
  Quarter: "Quarterly",
  Year: "Yearly"
};

function setupReducer(state = { ["new"]: { ...defaultSetup } }, action) {
  switch (action.type) {
    case UPDATE_SETUPS:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_SETUP:
      return {
        ...state,
        [action.id]: {
          ...action.data
        }
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    case UPDATE_FIELDS:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ...action.data
        }
      };
    case UPDATE_COSTS:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          conversionGain: action.conversionGain,
          conversionCost: action.conversionCost,
          conversionSaved: action.conversionSaved,
          conversionMissed: action.conversionMissed
        }
      };
    case UPDATE_COUNTS:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          truePositive: action.truePositive,
          falsePositive: action.falsePositive,
          trueNegative: action.trueNegative,
          falseNegative: action.falseNegative
        }
      };

    case DELETE_FIELD:
      const retSetup = { ...state[action.id] };
      delete retSetup[action.key];
      return {
        ...state,
        [action.id]: retSetup
      };
    case UPDATE_IGNORED:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["ignored"]: {
            ...state[action.id].ignored,
            [action.key]: action.data
          }
        }
      };
    case UPDATE_MODELS:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["modelOptions"]: {
            ...state[action.id].modelOptions,
            [action.key]: {
              ...state[action.id].modelOptions[action.key],
              ...action.data
            }
          }
        }
      };
    case UPDATE_CUSTOM_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["customValues"]: {
            ...state[action.id]["customValues"],
            [action.key]: action.data
          }
        }
      };
    case UPDATE_BASELINE_CUSTOM_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["baselineCustomValues"]: {
            ...state[action.id]["baselineCustomValues"],
            [action.key]: action.data
          }
        }
      };
    case UPDATE_CUSTOM_MAPPING:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["customMappings"]: {
            ...state[action.id]["customMappings"],
            [action.key]: action.data
          }
        }
      };
    case DELETE_CUSTOM_MAPPING:
      const cMappings = { ...state[action.id]["customMappings"] };
      delete cMappings[action.key];
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["customMappings"]: cMappings
        }
      };
    case UPDATE_FIELD_TYPE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["fieldTypes"]: {
            ...state[action.id]["fieldTypes"],
            [action.key]: action.data
          }
        }
      };
    case UPDATE_ADVANCED_INPUT:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["advancedInputs"]: {
            ...(state[action.id] || {})["advancedInputs"],
            [action.key]: action.data
          }
        }
      };
    case UPDATE_CUSTOM_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["customFields"]: {
            ...(state[action.id] || {})["customFields"],
            [action.key]: action.data
          }
        }
      };
    case DELETE_CUSTOM_FIELD:
      var cFields = { ...state[action.id]["customFields"] };
      delete cFields[action.key];
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["customFields"]: cFields
        }
      };
    default:
      return state;
  }
}

/* Selectors */

export function getSetup(state, id, useLocalCosts = false, projectId) {
  const setup = state && state.setup && state.setup[id] ? state.setup[id] : {};

  return setup;
}

export function updateAdvancedInput(id, inputId, evaluator, blueprintId) {
  return (dispatch, getState) => {
    const projectId = projectReducer.getProjectIdFromRunId(getState(), id);
    const overallRunId = projectReducer.getOverallRunId(getState(), projectId);

    let checkOtherRuns = undefined;

    if (overallRunId === id) {
      const project = projectReducer.getProject(getState(), projectId);
      checkOtherRuns = false;
      project &&
        project.segments &&
        project.segments.forEach((segment) => {
          const runId = segment?.job?.job_id;

          if (id !== runId) {
            dispatch(updateAdvancedInput(runId, inputId, evaluator, blueprintId));
          }
        });
    }

    const setup = getSetup(getState(), id);
    const setupAInputs = setup?.advancedInputs;
    if (!setupAInputs) {
      const bpInputs = blueprintReducer.getAdvancedInputs(getState(), setup?.blueprintId);
      dispatch({
        type: UPDATE_FIELD,
        id: id,
        key: "advancedInputs",
        data: {
          ...bpInputs,
          [inputId]: evaluator
        }
      });
    } else {
      dispatch({
        type: UPDATE_ADVANCED_INPUT,
        id: id,
        data: evaluator,
        key: inputId
      });
    }

    dispatch(blueprintReducer.checkEvaluator(blueprintId, id, undefined, checkOtherRuns));
  };
}

export function getAdvancedInputs(state, id) {
  const setup = getSetup(state, id);

  return setup?.advancedInputs ?? {};
}

export function getCustomFields(state, id) {
  const setup = getSetup(state, id);

  return setup?.customFields ?? {};
}

export function getCustomFieldsTransformed(state, id) {
  const advancedInputs = getAdvancedInputs(state, id) || {};

  const customFields = getCustomFields(state, id);

  return customFields
    ? Object.keys(customFields)
        .filter((c) => customFields[c] !== undefined)
        .map((c) => ({
          ...customFields[c],
          isBenefit:
            (advancedInputs["truePositive"] && advancedInputs["truePositive"].indexOf(c) !== -1) ||
            (advancedInputs["trueNegative"] && advancedInputs["trueNegative"].indexOf(c) !== -1),
          isCost:
            (advancedInputs["falsePositive"] && advancedInputs["falsePositive"].indexOf(c) !== -1) ||
            (advancedInputs["falseNegative"] && advancedInputs["falseNegative"].indexOf(c) !== -1)
        }))
    : [];
}

export function updateCustomField(id, customField, nextId = undefined) {
  return (dispatch, getState) => {
    const setup = getSetup(getState(), id);
    const setupCFields = setup?.customFields;
    if (!setupCFields) {
      const bpFields = blueprintReducer.getCustomFields(getState(), setup?.blueprintId);
      dispatch({
        type: UPDATE_FIELD,
        id: id,
        key: "customFields",
        data: {
          ...bpFields,
          [customField.id]: customField
        }
      });
    } else {
      dispatch(updateCustomFieldById(id, customField.id, customField));
    }

    if (nextId !== undefined) {
      const projectId = nextReducer.getProjectId(getState(), nextId);
      const project = projectReducer.getProject(getState(), projectId);

      const overallRunId = projectReducer.getOverallRunId(getState(), projectId);

      project &&
        project.segments &&
        project.segments.forEach((segment) => {
          const runId = segment?.job?.job_id;

          if (id === overallRunId && id !== runId) {
            dispatch(updateCustomField(runId, customField));
          }

          let customValues = {
            ...getCustomValues(getState(), runId)
          };

          customValues[customField.id] = 0;

          dispatch(updateCustomValues(runId, customValues));
        });
    }

    return Promise.resolve();
  };
}

export function updateCustomFieldById(id, cId, customField) {
  return {
    type: UPDATE_CUSTOM_FIELD,
    id: id,
    data: customField,
    key: cId
  };
}

export function deleteCustomField(id, cId) {
  return {
    type: DELETE_CUSTOM_FIELD,
    id: id,
    key: cId
  };
}

export function deleteCustomFieldById(id, cId) {
  return (dispatch, getState) => {
    const setup = getSetup(getState(), id);
    const setupCFields = setup?.customFields;
    if (!setupCFields) {
      const bpFields = blueprintReducer.getCustomFields(getState(), setup?.blueprintId);
      dispatch({
        type: UPDATE_FIELD,
        id: id,
        key: "customFields",
        data: {
          ...bpFields
        }
      });
    }
    dispatch(deleteCustomField(id, cId));
    dispatch(validateCustomValues(id));

    const projectId = projectReducer.getProjectIdFromRunId(getState(), id);
    const overallRunId = projectReducer.getOverallRunId(getState(), projectId);

    if (overallRunId === id) {
      const project = projectReducer.getProject(getState(), projectId);
      project &&
        project.segments &&
        project.segments.forEach((segment) => {
          const runId = segment?.job?.job_id;

          if (id !== runId) {
            dispatch(deleteCustomFieldById(runId, cId));
          }
        });
    }
  };
}

export function getDatasetRefreshSchedule(state, id) {
  return getSetup(state, id)?.datasetRefreshSchedule;
}

export function updateDatasetRefreshSchedule(id, val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    id: id,
    key: "datasetRefreshSchedule"
  };
}

export function getIsForecast(state, id) {
  return (getSetup(state, id) || {}).isForecast;
}

export function getForecastModeH(state, id) {
  return (getSetup(state, id) || {}).forecastMode;
}

export function getForecastTimeUnit(state, id) {
  return (getSetup(state, id) || {}).forecastTimeUnit || "Days";
}
export function getForecastTime(state, id) {
  return (getSetup(state, id) || {}).forecastTime || 1;
}

export function getUseForecastLocalRatio(state, id) {
  return (getSetup(state, id) || {}).useForecastLocalRatio;
}

export function getForecastLocalRatio(state, id) {
  const localRatio = (getSetup(state, id) || {}).forecastLocalRatio;
  return localRatio !== undefined ? localRatio : getCurrentRatio(state, id);
}

export function getForecastModelSelection(state, id) {
  return (getSetup(state, id) || {}).forecastModelSelection;
}

export function updateUseForecastLocalRatio(id, val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    id: id,
    key: "useForecastLocalRatio"
  };
}

export function updateForecastLocalRatio(id, val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    id: id,
    key: "forecastLocalRatio"
  };
}

export function getForecastTimeOptions(state, id) {
  return [
    { label: "Days", value: "Days" },
    { label: "Weeks", value: "Weeks" },
    { label: "Months", value: "Months" },
    { label: "Years", value: "Years" }
  ];
}

export function updateForecastTime(id, val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    id: id,
    key: "forecastTime"
  };
}

export function updateForecastTimeUnit(id, val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    id: id,
    key: "forecastTimeUnit"
  };
}

export function updateIsForecastH(id, val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    id: id,
    key: "isForecast"
  };
}

export function updateForecastModeH(id, val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    id: id,
    key: "forecastMode"
  };
}

export function updateForecastTimeCol(id, val) {
  return {
    type: UPDATE_FIELD,
    data: val,
    id: id,
    key: "forecastTimeCol"
  };
}

export function getForecastTimeCol(state, id) {
  return (getSetup(state, id) || {}).forecastTimeCol;
}

export function updateForecastModelSelection(id, forecastModelSelection) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "forecastModelSelection",
    data: forecastModelSelection
  };
}

export function getComparisonOptions(state, id) {
  return getSetup(state, id)?.comparisonOptions;
}

export function getMadLibs(state, id) {
  const setup = getSetup(state, id);

  return {
    numberPredicted: setup.numberPredicted ? setup.numberPredicted : DEFAULT_LABELS.numberPredicted,
    contAction: setup.contAction ? setup.contAction : DEFAULT_LABELS.contAction,
    underPredictAction: setup.underPredictAction ? setup.underPredictAction : DEFAULT_LABELS.underPredictAction,
    overPredictAction: setup.overPredictAction ? setup.overPredictAction : DEFAULT_LABELS.overPredictAction
  };
}

export function getProcessLimit(state, id) {
  const projectId = projectReducer.getProjectIdFromRunId(state, id);
  const maxBudget = projectReducer.getSegmentMaxBudgetByRunId(state, projectId, id);
  const total =
    getTruePositive(state, id) + getFalsePositive(state, id) + getTrueNegative(state, id) + getFalseNegative(state, id);

  const conversionCost = getConversionCost(state, id);

  return maxBudget && conversionCost ? Math.abs(maxBudget / conversionCost) : total;
}

export function getProcessLimitCount(state, id) {
  const total =
    getTruePositive(state, id) + getFalsePositive(state, id) + getTrueNegative(state, id) + getFalseNegative(state, id);

  return getSetup(state, id).processLimitCount ? getSetup(state, id).processLimitCount : total;
}

export function getProcessLimitPct(state, id) {
  const processLimit = getProcessLimit(state, id);
  const total =
    getTruePositive(state, id) + getFalsePositive(state, id) + getTrueNegative(state, id) + getFalseNegative(state, id);

  return processLimit / total;
}

export function getBlueprintFromSetup(state, id) {
  const blueprintId = getBlueprintId(state, id);

  return blueprintId === undefined ? undefined : blueprintReducer.getBlueprint(state, blueprintId);
}

export function getBlueprintId(state, id) {
  return getSetup(state, id).blueprintId;
}

export function getAimToHit(state, id) {
  return getSetup(state, id).aimToHit;
}

export function getAITrainingBudget(state, id) {
  return getSetup(state, id).aiTrainingBudget;
}

export function getConversionRate(state, id) {
  return getSetup(state, id).conversionRate;
}

export function getMaximumConversionRate(state, id) {
  return getSetup(state, id).maximumConversionRate;
}

export function getBusinessObjective(state, id) {
  return getSetup(state, id).businessObjective;
}

export function getEnableFairnessMetrics(state, id, getStoredValue = false) {
  if (!isEnabled("fairness")) {
    return false;
  }
  const currentEnable = getSetup(state, id).enableFairnessMetrics;
  const currentObjective = getFairnessObjective(state, id);
  if (currentEnable !== undefined || currentObjective === undefined || getStoredValue) {
    return currentEnable;
  }

  // This is the fallback for older fairness projects;
  return getFairnessObjective(state, id) !== projectReducer.FAIRNESS_OBJECTIVE.NONE;
}

export function getFairnessObjective(state, id) {
  return getSetup(state, id).fairnessObjective;
}

export function getFairnessToleranceBound(state, id) {
  return getSetup(state, id).fairnessToleranceBound;
}

export function getBiasReduction(state, id) {
  return getSetup(state, id).biasReduction;
}

export function getCostErrors(state, id) {
  return getSetup(state, id).costErrors;
}

export function getLinkedProjectId(state, id) {
  return getSetup(state, id).linkedProjectId;
}

export function getLinkedProjectOutputMode(state, id) {
  return getSetup(state, id).linkedProjectOutputMode;
}

export function getHasLinkedProject(state, id) {
  return getSetup(state, id).hasLinkedProject;
}

export function getConversionGain(state, id, useLocalCosts = false, skipConvert = false, setupOverride) {
  const retSetup = setupOverride ? setupOverride : getSetup(state, id);
  const conversionGain = retSetup && retSetup.conversionGain;
  return skipConvert ? conversionGain : Number(conversionGain);
}

export function getConversionCost(state, id, useLocalCosts = false, skipConvert = false, setupOverride) {
  const retSetup = setupOverride ? setupOverride : getSetup(state, id);
  const conversionCost = retSetup && retSetup.conversionCost;
  return skipConvert ? conversionCost : Number(conversionCost);
}

export function getConversionMissed(state, id, useLocalCosts = false, skipConvert = false, setupOverride) {
  const retSetup = setupOverride ? setupOverride : getSetup(state, id);
  const conversionMissed = retSetup && retSetup.conversionMissed;
  return skipConvert ? conversionMissed : Number(conversionMissed);
}

export function getConversionSaved(state, id, useLocalCosts = false, skipConvert = false, setupOverride) {
  const retSetup = setupOverride ? setupOverride : getSetup(state, id);
  const conversionSaved = retSetup && retSetup.conversionSaved;
  return skipConvert ? conversionSaved : Number(conversionSaved);
}

export function getAdvancedRevenue(state, id, skipConvert = false, setupOverride) {
  const retSetup = setupOverride ? setupOverride : getSetup(state, id);
  const advancedRevenue = retSetup && retSetup.advancedRevenue;
  return skipConvert ? advancedRevenue : Number(advancedRevenue);
}

export function getAdvancedCost(state, id, skipConvert = false, setupOverride) {
  const retSetup = setupOverride ? setupOverride : getSetup(state, id);
  const advancedCost = retSetup && retSetup.advancedCost;
  return skipConvert ? advancedCost : Number(advancedCost);
}

export function getBaselineAdvancedRevenue(state, id, skipConvert = false) {
  const advRevenue =
    getIsBaselineRealityHammer(state, id) && getSetup(state, id).baselineAdvancedRevenue !== undefined
      ? getSetup(state, id).baselineAdvancedRevenue
      : getSetup(state, id).advancedRevenue;

  return skipConvert ? advRevenue : Number(advRevenue);
}

export function getBaselineAdvancedCost(state, id, skipConvert = false) {
  const advCost =
    getIsBaselineRealityHammer(state, id) && getSetup(state, id).baselineAdvancedCost !== undefined
      ? getSetup(state, id).baselineAdvancedCost
      : getSetup(state, id).advancedCost;

  return skipConvert ? advCost : Number(advCost);
}

export function getCurrentAdvancedCost(state, id, skipConvert = false) {
  const advCost = getSetup(state, id).advancedCost;

  return skipConvert ? advCost : Number(advCost);
}

export function getThreshold(state, id) {
  return getSetup(state, id).threshold;
}

export function getCurrentRatio(state, id) {
  return getConversionGain(state, id) / getConversionCost(state, id);
}

export function getTargeted(state, id) {
  return getTruePositive(state, id) + getFalsePositive(state, id);
}

export function getScaledTotal(state, id) {
  return getSetup(state, id).scaledTotal ? getSetup(state, id).scaledTotal : getTotal(state, id);
}

export function getOutcomePercentage(state, id) {
  return getSetup(state, id).outcomePercentage;
}

export function getTotal(state, id) {
  return (
    Number(getSetup(state, id).truePositive) +
    Number(getSetup(state, id).trueNegative) +
    Number(getSetup(state, id).falsePositive) +
    Number(getSetup(state, id).falseNegative)
  );
}

export function getDefaultTotal(state, id) {
  const setup = getSetup(state, id);

  if (setup && setup.defaultSetup && setup.defaultSetup.data) {
    return (
      Number(setup.defaultSetup.data.truePositive) +
      Number(setup.defaultSetup.data.trueNegative) +
      Number(setup.defaultSetup.data.falsePositive) +
      Number(setup.defaultSetup.data.falseNegative)
    );
  } else {
    return getTotal(state, id);
  }
}

export function getTotalScale(state, id) {
  return getScaledTotal(state, id) / getTotal(state, id);
}

export function hasNoCounts(state, id) {
  return getTotal(state, id) === 0;
}

let wtf = 0;

export function getCount(state, id, countType) {
  const setup = getSetup(state, id);

  if (setup && setup.linkedProjectId && setup.linkedRuns) {
    const scalar = Object.keys(setup.linkedRuns).reduce(
      (a, b) => a + (getTargeted(state, setup.linkedRuns[b].linkedRunId) * setup.linkedRuns[b].percentage) / 100,
      0
    );

    const total = getTotal(state, id);

    wtf % 1000 === 0 && console.log(scalar);

    wtf++;

    const scaleCount = (Number(setup[countType]) * scalar) / total;

    return scaleCount;

    //console.log(scalar);
  } else {
    return undefined;
  }
}

export function getTruePositive(state, id, useLocalCosts = false) {
  return useLocalCosts === true && getLocal(id, "truePositive") !== undefined
    ? getLocal(id, "truePositive")
    : Number(getSetup(state, id).truePositive);
}

export function getTrueNegative(state, id, useLocalCosts = false) {
  return useLocalCosts === true && getLocal(id, "trueNegative") !== undefined
    ? getLocal(id, "trueNegative")
    : Number(getSetup(state, id).trueNegative);
}

export function getFalsePositive(state, id, useLocalCosts = false) {
  return useLocalCosts === true && getLocal(id, "falsePositive") !== undefined
    ? getLocal(id, "falsePositive")
    : Number(getSetup(state, id).falsePositive);
}

export function getFalseNegative(state, id, useLocalCosts = false) {
  return useLocalCosts === true && getLocal(id, "falseNegative") !== undefined
    ? getLocal(id, "falseNegative")
    : Number(getSetup(state, id).falseNegative);
}

export function getParentLabel(state, id) {
  return getSetup(state, id).parentLabel;
}

export function getDefaultTruePositiveImpact(state, id) {
  return getSetup(state, id).defaultTruePositiveImpact;
}

export function getDefaultFalsePositiveImpact(state, id) {
  return getSetup(state, id).defaultFalsePositiveImpact;
}

export function getDefaultTrueNegativeImpact(state, id) {
  return getSetup(state, id).defaultTrueNegativeImpact;
}

export function getDefaultFalseNegativeImpact(state, id) {
  return getSetup(state, id).defaultFalseNegativeImpact;
}

export function getFalseNegativeLabel(state, id) {
  return getSetup(state, id).falseNegativeLabel;
}

export function getFalsePositiveLabel(state, id) {
  return getSetup(state, id).falsePositiveLabel;
}

export function getTrueNegativeLabel(state, id) {
  return getSetup(state, id).trueNegativeLabel;
}

export function getOverestimateLabel(state, id) {
  return getSetup(state, id).overestimateLabel;
}

export function getUnderestimateLabel(state, id) {
  return getSetup(state, id).underestimateLabel;
}

export function getTruePositiveLabel(state, id) {
  const label = getSetup(state, id).truePositiveLabel;
  return label === undefined ? "Success" : label;
}

export function getAdvancedRevenueLabel(state, id) {
  const label = getSetup(state, id).advancedRevenueLabel;
  return label === undefined ? DEFAULT_LABELS.advancedRevenueLabel : label;
}

export function getAdvancedCostLabel(state, id) {
  const label = getSetup(state, id).advancedCostLabel;
  return label === undefined ? DEFAULT_LABELS.advancedCostLabel : label;
}

export function getNetImpactLabel(state, id) {
  const label = getSetup(state, id).netImpactLabel;
  return label === undefined ? DEFAULT_LABELS.netImpactLabel : label;
}

export function getDefaultLabels(setup) {
  if (setup) {
    return {
      transactionLabel: setup.transactionLabel,
      notPursuedTransactionLabel: setup.notPursuedTransactionLabel,
      outcomeLabel: setup.outcomeLabel,
      trueAction: setup.trueAction,
      falseAction: setup.falseAction,
      unitType: setup.unitType,
      advancedCostLabel: setup.advancedCostLabel,
      advancedRevenueLabel: setup.advancedRevenueLabel,
      netImpactLabel: setup.netImpactLabel,
      numberPredicted: setup.numberPredicted,
      contAction: setup.contAction,
      underPredictAction: setup.underPredictAction,
      overPredictAction: setup.overPredictAction
    };
  } else {
    return {
      ...DEFAULT_LABELS
    };
  }
}

// Use the same time period across the entire project
export function getTimePeriod(state, id, overrideOverall = false) {
  let retId = id;
  if (!overrideOverall && isFinite(retId)) {
    const projectId = projectReducer.getProjectIdFromRunId(state, id);
    retId = projectReducer.getOverallRunId(state, projectId);
  }
  return getSetup(state, retId).timePeriod || defaultSetup.timePeriod;
}

export function getTimePeriodSelection(state, id, timePeriodOverride) {
  const targetTimePeriod = timePeriodOverride || getTimePeriod(state, id);
  return TIME_PERIOD_OPTIONS.find((elem) => elem.value === targetTimePeriod);
}

export function getMode(state, id) {
  return getSetup(state, id).mode;
}

export function getIsSimpleCosts(state, id) {
  return getSetup(state, id).isSimpleCosts;
}

export function getIsHideCounts(state, id) {
  return getSetup(state, id).isHideCounts;
}

export function getLabels(state, id) {
  return {
    falseNegativeLabel: getFalseNegativeLabel(state, id),
    falsePositiveLabel: getFalsePositiveLabel(state, id),
    trueNegativeLabel: getTrueNegativeLabel(state, id),
    truePositiveLabel: getTruePositiveLabel(state, id)
  };
}

export function getNonTargetedConversionRate(state, id) {
  return Number(getSetup(state, id).nonTargetedConversionRate);
}

export function getNonTargetedConversionCount(state, id) {
  return Number(getSetup(state, id).nonTargeted) * getNonTargetedConversionRate(state, id);
}

export function getTotalPossibleHits(state, id) {
  return Number(getSetup(state, id).falseNegative) + getTruePositive(state, id);
}

export function getUseModelList(state, id) {
  return getSetup(state, id) ? getSetup(state, id).useModelList : false;
}

export function getDefaultProcessLimit(state, id) {
  const setup = getSetup(state, id);

  return setup && setup.defaultSetup ? setup.defaultSetup.data.processLimit : undefined;
}

export function getCurrentROI(state, id) {
  const conversionGain = getConversionGain(state, id);
  const conversionCost = getConversionCost(state, id);
  const conversionSaved = getConversionSaved(state, id);
  const conversionMissed = getConversionMissed(state, id);

  const processLimit = getProcessLimit(state, id);
  const totalPossibleHits = getTotalPossibleHits(state, id);
  const total = getTotal(state, id);

  let truePositive = getTruePositive(state, id);
  let trueNegative = getTrueNegative(state, id);
  let falsePositive = getFalsePositive(state, id);
  let falseNegative = getFalseNegative(state, id);

  const hideCosts = getHideCosts(state, id);

  if (truePositive + falsePositive > processLimit) {
    const precision = truePositive + falsePositive === 0 ? 0 : truePositive / (truePositive + falsePositive);

    truePositive = processLimit * precision;
    falsePositive = processLimit * (1 - precision);
    falseNegative = totalPossibleHits - truePositive;
    trueNegative = total - truePositive - falsePositive - falseNegative;
  }

  return hideCosts
    ? truePositive * conversionGain + falsePositive * conversionCost
    : truePositive * conversionGain +
        falsePositive * conversionCost +
        trueNegative * conversionSaved +
        falseNegative * conversionMissed;
}

export function getDesiredROI(state, id) {
  const conversionGain = getConversionGain(state, id);
  const conversionCost = getConversionCost(state, id);
  const conversionSaved = getConversionSaved(state, id);
  const conversionMissed = getConversionMissed(state, id);

  const truePositive = getTruePositive(state, id);
  const trueNegative = getTrueNegative(state, id);
  const falsePositive = getFalsePositive(state, id);
  const falseNegative = getFalseNegative(state, id);

  const aimToHit = getAimToHit(state, id);
  const processLimit = getProcessLimit(state, id);

  const hideCosts = getHideCosts(state, id);
  const total = getTotal(state, id);

  return hideCosts
    ? aimToHit * (conversionGain - conversionCost) - (processLimit - aimToHit) * conversionCost
    : aimToHit * (conversionGain - conversionCost) -
        (processLimit - aimToHit) * conversionCost +
        (total - aimToHit - (processLimit - aimToHit) - (truePositive + falseNegative - aimToHit)) * conversionSaved -
        (truePositive + falseNegative - aimToHit) * (conversionMissed - conversionSaved);
}

export function getMaximumROI(state, id) {
  const conversionGain = getConversionGain(state, id);
  const conversionCost = getConversionCost(state, id);

  const totalGain = (getTruePositive(state, id) + getFalseNegative(state, id)) * conversionGain;
  const totalCost = getTargeted(state, id) * conversionCost;

  return totalGain - totalCost;
}

export function getHideCosts(state, id) {
  const outcomeType = getOutcomeType(state, id);

  return outcomeType === "number" ? true : getSetup(state, id).hideCosts;
}

export function getUseCase(state, id) {
  return getSetup(state, id).useCase;
}

export function getRunType(state, id) {
  return getSetup(state, id).runType;
}

export function getIsQuickRun(state, id) {
  return (getRunType(state, id) === "QuickRun" || getSetup(state, id).isQuickRun) && !getIsForceOptimized(state, id);
}

export function getIsTwoMinuteRun(state, id) {
  return getRunType(state, id) === "TwoMinute";
}

export function getDatasetId(state, id) {
  return getSetup(state, id).datasetId;
}

export function getDatasetFromSetup(state, id) {
  return getDataset(state, getSetup(state, id).datasetId);
}

export function getDatasetSelection(state, id) {
  const dataset = getDatasetFromSetup(state, id);

  return dataset ? { value: dataset.id, label: dataset.label } : undefined;
}

export function getIsForceOptimized(state, id) {
  return getSetup(state, id).isForceOptimized;
}

export function getSelectedMiniBlueprint(state, id) {
  return getSetup(state, id).selectedMiniBlueprint;
}

export function getSelectedBlueprintId(state, id) {
  return getSetup(state, id).selectedBlueprintId;
}

export function getAcademicResultsStep(state, id) {
  return getSetup(state, id).academicResultsStep;
}

export function getBusinessSubgroups(state, id) {
  return getSetup(state, id).businessSubgroups;
}

export function getBusinessSubgroupOptions(state, id) {
  const datasetId = getDatasetId(state, id);
  const fields = homeReducer.getFieldsArr(state, datasetId);
  const outcome = homeReducer.getOutcome(state, datasetId);
  const businessSubgroups = getBusinessSubgroups(state, id);
  return fields
    .filter((field) => {
      const currField = field.label;
      if (currField === outcome) {
        return false;
      }
      if (businessSubgroups) {
        for (let i = 0; i < businessSubgroups.length; i++) {
          if (businessSubgroups[i] && currField === businessSubgroups[i].value) {
            return false;
          }
        }
      }
      return true;
    })
    .map((field) => ({ label: field.label, value: field.label }));
}

export function getGoalOptions(state, id) {
  return getSetup(state, id).goalOptions;
}

export function getGoal(state, id) {
  return getSetup(state, id).goal;
}

export function getOutcome(state, id) {
  return getSetup(state, id).outcome;
}

export function getModels(state, id) {
  return getSetup(state, id).models;
}

export function getLinkedRunId(state, id) {
  return getSetup(state, id).linkedRunId;
}

export function getLinkedRuns(state, id) {
  return getSetup(state, id).linkedRuns;
}

export function getLinkedScenarioId(state, id) {
  return getSetup(state, id).linkedScenarioId;
}

export function getLinkedCount(state, id) {
  return getSetup(state, id).linkedCount === undefined ? 0 : getSetup(state, id).linkedCount;
}

export function getValueReplace(state, id) {
  return getSetup(state, id).valueReplace;
}

export function getIsBlueprint(state, id) {
  return getSetup(state, id).isBlueprint;
}

export function getModelOptions(state, id) {
  return getSetup(state, id).modelOptions;
}

export function getEndState(state, id) {
  return getSetup(state, id).endState ? getSetup(state, id).endState : [];
}

export function getUniqueId(state, id) {
  return getSetup(state, id).uniqueId;
}

export function getPursuedRatio(state, id) {
  return getSetup(state, id).pursuedRatio;
}

export function getNotPursuedRatio(state, id) {
  return getSetup(state, id).notPursuedRatio;
}

export function getIsBYOMModal(state, id) {
  return getSetup(state, id).isBYOMModal;
}

export function getBYOMURL(state, id) {
  return getSetup(state, id).BYOMURL;
}

export function getOptimizeBy(state, id) {
  return getSetup(state, id).optimize_by;
}

export function getOptimizeByOptions(state, id) {
  const ignored = getIgnored(state, id);
  const splitColumn = getSplitColumn(state, id);
  const dataset = getDatasetFromSetup(state, id);
  const discarded = getDiscardedColumns(state, id);

  return dataset && dataset.fields
    ? Object.keys(dataset.fields)
        .filter(
          (f) =>
            f !== dataset.outcome &&
            f !== splitColumn &&
            !(ignored && ignored[dataset.fields[f].label]) &&
            !(discarded && discarded.hasOwnProperty(dataset.fields[f].label))
        )
        .map((f) => ({
          label: dataset.fields[f].label,
          value: dataset.fields[f].label
        }))
    : [];
}

export function getOptimizeGoal(state, id) {
  return getSetup(state, id).optimize_goal;
}

export function getPredictionConstraints(state, id) {
  return getSetup(state, id)?.predictionConstraints;
}

export function getMinimumBound(state, id) {
  return getPredictionConstraints(state, id)?.min;
}

export function getMaximumBound(state, id) {
  return getPredictionConstraints(state, id)?.max;
}

export function getOptimizeGoalOptions(state, id) {
  const optimizeGoalOptions = [
    { label: "Maximize", value: "MAXIMIZE" },
    { label: "Minimize", value: "MINIMIZE" }
  ];

  return optimizeGoalOptions;
}

export function getNOpts(state, id) {
  return getSetup(state, id).n_opts;
}

export function getNOptsOptions(state, id) {
  const nOptOptions = [
    { label: "1", value: 1 },
    { label: "2", value: 2 },
    { label: "3", value: 3 }
  ];

  return nOptOptions;
}

export function getFrontierPercentProcessedMin(state, id, defaultVal) {
  const min = getSetup(state, id).frontierPercentProcessedMin;

  if (min === undefined) {
    return defaultVal || 0;
  }

  return min;
}

export function getFrontierPercentProcessedMax(state, id, defaultVal) {
  const max = getSetup(state, id).frontierPercentProcessedMax;

  if (max === undefined) {
    return defaultVal || 1;
  }

  return max;
}

export function getTrueAction(state, id) {
  const trueAction = getSetup(state, id).trueAction;

  return trueAction === undefined ? DEFAULT_LABELS.trueAction : trueAction;
}

export function getFalseAction(state, id) {
  return getSetup(state, id).falseAction ? getSetup(state, id).falseAction : DEFAULT_LABELS.falseAction;
}

export function getTransactionLabel(state, id) {
  const transactionLabel = getSetup(state, id).transactionLabel;
  return transactionLabel === undefined ? DEFAULT_LABELS.transactionLabel : transactionLabel;
}

export function getNotPursuedTransactionLabel(state, id) {
  const label = getSetup(state, id).notPursuedTransactionLabel;
  return label === undefined ? DEFAULT_LABELS.notPursuedTransactionLabel : label;
}

export function getOutcomeLabel(state, id) {
  const setup = getSetup(state, id);
  return setup.outcomeLabel === undefined
    ? setup.success && setup.success.label !== undefined
      ? setup.success.label
      : getTruePositiveLabel(state, id)
    : setup.outcomeLabel;
}

export function getModelOptionsArr(state, id) {
  const outcomeType = getOutcomeType(state, id);
  const modelOptions = getSetup(state, id).modelOptions;

  return modelOptions
    ? Object.keys(modelOptions)
        .filter(
          (m) =>
            (modelOptions[m].type === "CLASSIFICATION" && outcomeType === "categorical") ||
            (modelOptions[m].type === "REGRESSION" && outcomeType === "number")
        )
        .map((m) => modelOptions[m])
    : [];
}

export function getSetupLabels(state, id) {
  return {
    truePositiveLabel: getTruePositiveLabel(state, id),
    falsePositiveLabel: getFalsePositiveLabel(state, id),
    trueNegativeLabel: getTrueNegativeLabel(state, id),
    falseNegativeLabel: getFalseNegativeLabel(state, id),
    transactionLabel: getTransactionLabel(state, id),
    outcomeLabel: getOutcomeLabel(state, id),
    notPursuedTransactionLabel: getNotPursuedTransactionLabel(state, id),
    costLabel: getAdvancedCostLabel(state, id),
    revenueLabel: getAdvancedRevenueLabel(state, id),
    profitLabel: getNetImpactLabel(state, id),
    trueAction: getTrueAction(state, id),
    falseAction: getFalseAction(state, id),
    unitType: getUnitType(state, id),

    //numberPredicted,
    //contAction,
    //underPredictAction,
    //overPredictAction,
    ...getMadLibs(state, id)
  };
}

export function getPriceColumn(state, id) {
  return getSetup(state, id).priceColumn;
}

export function getSplitColumn(state, id) {
  return getSetup(state, id).splitColumn;
}

export function getIsRealityHammer(state, id) {
  return getSetup(state, id).isRealityHammer === true ? true : false;
}

function getDiscardedColumnFromWarning(warning) {
  if (!warning) {
    return;
  }

  const warningCheck = (warning + "").toLowerCase();
  if (warningCheck.toLowerCase().includes("be discarded")) {
    if (warning.includes("is all missing")) {
      return warning.replace("Column '", "").split("' is all missing")[0];
    } else if (warning.includes("has only one unique value")) {
      return warning.replace("Column '", "").split("' has only one unique value")[0];
    } else if (warning.includes("has too few recurring values")) {
      return warning.replace("Column ", "").split(" has too few recurring values")[0];
    } else if (warning.includes("has too few observed values")) {
      return warning.replace("Column '", "").split("' has too few observed values")[0];
    } else if (warning.includes("has insufficient variation")) {
      return warning.replace("Column '", "").split("' has insufficient variation")[0];
    } else if (warning.includes("has too few distinct values")) {
      return warning.replace("Column '", "").split("' has too few distinct values")[0];
    }

    return;
  }
}

// Columns discarded on ML side
export function getDiscardedColumns(state, id) {
  const dataset = getDatasetFromSetup(state, id);
  const warnings = dataset && dataset.warnings;
  const retDiscarded = {};
  if (warnings) {
    warnings.forEach((warning) => {
      const column = getDiscardedColumnFromWarning(warning.message);
      if (column) {
        retDiscarded[column] = true;
      }
    });
  }
  return retDiscarded;
}

export function getSplitOptions(state, id) {
  const dataset = getDatasetFromSetup(state, id);
  const ignored = getIgnored(state, id);
  const discarded = getDiscardedColumns(state, id);

  return dataset && dataset.fields
    ? Object.keys(dataset.fields)
        .filter(
          (f) =>
            dataset.fields[f].type === "categorical" &&
            f !== dataset.outcome &&
            dataset.fields[f].data &&
            Math.max.apply(
              null,
              dataset.fields[f].data.map((x) => x.count)
            ) > projectReducer.MIN_SEGMENT_ROWS
        )
        .filter(
          (f) =>
            !(ignored && ignored[dataset.fields[f].label]) &&
            !(discarded && discarded.hasOwnProperty(dataset.fields[f].label))
        )
        .map((f) => ({
          label: dataset.fields[f].label,

          value: dataset.fields[f].label
        }))
    : [];
}

export function getSuccessStrings(state, id) {
  const outcome = getOutcomeFromDataset(state, id);

  const success = getSuccess(state, id)
    ? typeof getSuccess(state, id) === "object" && getSuccess(state, id).constructor === Array
      ? getSuccess(state, id).map((o) => o.label)
      : [getSuccess(state, id).value]
    : [];

  const options = getSuccessOptions(state, id) ? getSuccessOptions(state, id).map((o) => o.label) : [];

  const negStr = outcome + " is " + options.filter((o) => success.indexOf(o) === -1).join(", ");

  const successStr = outcome + " is " + success.join(", ");

  return {
    positive: successStr,
    negative: negStr
  };
}

export function getConvergeError(state, id) {
  const TP = getConversionGain(state, id);
  const FP = getConversionCost(state, id);
  const TN = getConversionSaved(state, id);
  const FN = getConversionMissed(state, id);

  const outcome = getOutcomeFromDataset(state, id);

  const success = getSuccess(state, id)
    ? typeof getSuccess(state, id) === "object" && getSuccess(state, id).constructor === Array
      ? getSuccess(state, id).map((o) => o.label)
      : [getSuccess(state, id).value]
    : [];

  const options = getSuccessOptions(state, id) ? getSuccessOptions(state, id).map((o) => o.label) : [];

  const negStr = options.filter((o) => success.indexOf(o) === -1).join(", ") || "do not pursue transaction";

  const successStr = success.join(", ") || "pursue transaction";

  return TP === FN && FP === TN
    ? "Congratulations, you don't need a model. It makes no difference if you predict " +
        successStr +
        " or " +
        negStr +
        "."
    : TP >= TN && TP >= FN && FP >= TN && FP >= FN
      ? "Congratulations, you don't need a model. Always predicting " + successStr + " will maximize business impact."
      : TP <= TN && TP <= FN && FP <= TN && FP <= FN
        ? "Congratulations, you don't need a model. Always predicting " + negStr + " will maximize business impact."
        : undefined;
}

export function getOutcomeValue(state, id) {
  return getOutcome(state, id) ? getOutcome(state, id).value : undefined;
}

export function getOutcomeFromDataset(state, id) {
  return getDatasetFromSetup(state, id) ? getDatasetFromSetup(state, id).outcome : undefined;
}

export function getSuccessOptions(state, id) {
  const selectedDataset = getDatasetFromSetup(state, id);

  const retOptions =
    selectedDataset &&
    selectedDataset.outcome &&
    selectedDataset.fields &&
    selectedDataset.fields[selectedDataset.outcome] &&
    selectedDataset.fields[selectedDataset.outcome].data
      ? selectedDataset.fields[selectedDataset.outcome].data
          .map((field) => ({
            value: field.value,
            label: field.value,
            count: field.count
          }))
          .filter((f) => f.value !== "{Missing}" && f.value !== "{Other}")
      : [];
  return retOptions.length > 2 ? retOptions.filter((option) => option.value !== "{Missing}") : retOptions;
}

export function getSuccessOptionsWarning(state, id) {
  const selectedDataset = getDatasetFromSetup(state, id);

  if (
    selectedDataset &&
    selectedDataset.fields &&
    selectedDataset.fields[selectedDataset.outcome] &&
    selectedDataset.fields[selectedDataset.outcome].data &&
    selectedDataset.fields[selectedDataset.outcome].data.length > 2 &&
    selectedDataset.fields[selectedDataset.outcome].warnings &&
    selectedDataset.fields[selectedDataset.outcome].warnings.length
  ) {
    const missingWarning = selectedDataset.fields[selectedDataset.outcome].warnings.find(
      (w) => w.code === "MissingValuesWarning"
    );
    return missingWarning
      ? "The outcome column has " +
          (missingWarning.metadata && missingWarning.metadata.percentage
            ? Number(missingWarning.metadata.percentage).toFixed(2) + "%"
            : "") +
          " rows with missing values, they will be discarded from the analysis."
      : undefined;
  } else {
    return undefined;
  }
}

export function getSuccess(state, id) {
  return getSetup(state, id).success;
}

export function getSuccessValue(state, id) {
  const success = getSuccess(state, id);

  return success ? (Array.isArray(success) ? success.map((v) => v.value) : success.value) : undefined;
}

export function getSuccessPercent(state, id) {
  const success = getSuccess(state, id);
  const totalRows = getDatasetFromSetup(state, id) ? getDatasetFromSetup(state, id).rowCount : undefined;

  return totalRows && success
    ? Array.isArray(success)
      ? success.reduce((t, a) => t + a.count, 0) / totalRows
      : success.count / totalRows
    : undefined;
}

export function getFieldNames(state, id) {
  const dataset = getDatasetFromSetup(state, id);

  return dataset && dataset.fields
    ? Object.keys(dataset.fields)
        .filter((f) => f !== dataset.outcome)
        .map((f) => f)
    : [];
}

export function getFieldValueCounts(state, id) {
  // TODO: Bring this back
  // We can't use dataset fields if there's subgroups, but we can calculate once and then store in state
  // Store in run reducer on initial project load?
  return;
  const datasetId = getDatasetId(state, id);
  const dataset = homeReducer.getDataset(state, datasetId);

  if (!dataset?.fields) {
    return;
  }

  const job = runReducer.getJob(state, id);
  const insightFactory = homeReducer.getInsightFactory(state, datasetId, job);

  const splitColumn = getSplitColumn(state, id);
  const ignoredValue = getIgnoredValue(state, id);

  const columnValue = Object.keys(ignoredValue?.[splitColumn] ?? {}).find((value) => {
    return Boolean(ignoredValue[splitColumn][value]);
  });

  if (!insightFactory) {
    return;
  }

  const retFieldsCount = {};

  Object.keys(dataset?.fields).forEach((col) => {
    retFieldsCount[col] = {};
    dataset?.fields?.[col]?.data?.forEach((valObj) => {
      const val = valObj?.value;

      const filter = {
        [splitColumn]: columnValue,
        [col]: val
      };

      const count = insightFactory.getSummary(filter)?.getN();
      retFieldsCount[col][val] = count;
    });
  });

  return retFieldsCount;
}

export function getFields(state, id) {
  const dataset = getDatasetFromSetup(state, id);

  return dataset ? dataset.fields : undefined;
}

export function getOutcomeType(state, id) {
  const datasetId = getDatasetId(state, id);
  const outcome = getOutcomeFromDataset(state, id);

  return getFieldType(state, datasetId, outcome);
}

export function getIsContinuous(state, id) {
  const isContinuous = getOutcomeType(state, id) === "number";

  return isContinuous;
}

export function getIsBaselineRealityHammer(state, id) {
  return (getSetup(state, id) || {}).isBaselineRealityHammer;
}

export function getBaselineCustomValues(state, id) {
  return getSetup(state, id).baselineCustomValues ? getSetup(state, id).baselineCustomValues : {};
}

export function getCustomValues(state, id) {
  return getSetup(state, id).customValues;
}

export function getBaselineCounts(state, id) {
  return getSetup(state, id).baselineCounts;
}

export function getBaselineConversionGain(state, id) {
  return getIsBaselineRealityHammer(state, id) && getSetup(state, id).baselineConversionGain !== undefined
    ? getSetup(state, id).baselineConversionGain
    : getConversionGain(state, id);
}

export function getCurrentConversionGain(state, id) {
  return getConversionGain(state, id);
}

export function getBaselineConversionCost(state, id) {
  return getIsBaselineRealityHammer(state, id) && getSetup(state, id).baselineConversionCost !== undefined
    ? getSetup(state, id).baselineConversionCost
    : getConversionCost(state, id);
}

export function getCurrentConversionCost(state, id) {
  return getConversionCost(state, id);
}

export function getBaselineConversionMissed(state, id) {
  return getIsBaselineRealityHammer(state, id) && getSetup(state, id).baselineConversionMissed !== undefined
    ? getSetup(state, id).baselineConversionMissed
    : getConversionMissed(state, id);
}

export function getCurrentConversionMissed(state, id) {
  return getConversionMissed(state, id);
}

export function getBaselineConversionSaved(state, id) {
  return getIsBaselineRealityHammer(state, id) && getSetup(state, id).baselineConversionSaved !== undefined
    ? getSetup(state, id).baselineConversionSaved
    : getConversionSaved(state, id);
}

export function getShowImpact(state, id) {
  const showImpact = getSetup(state, id).showImpact;

  const baselineCosts = getBaselineCosts(state, id);

  return showImpact ||
    Math.abs(baselineCosts["baselineConversionGain"]) !== 1 ||
    Math.abs(baselineCosts["baselineConversionCost"]) !== 1 ||
    Math.abs(baselineCosts["baselineConversionMissed"]) !== 1 ||
    Math.abs(baselineCosts["baselineConversionSaved"]) !== 1
    ? true
    : false;
}

export function getCurrentConversionSaved(state, id) {
  return getConversionSaved(state, id);
}

export function getBaselineCosts(state, id) {
  return {
    baselineConversionGain: getBaselineConversionGain(state, id),
    baselineConversionCost: getBaselineConversionCost(state, id),
    baselineConversionMissed: getBaselineConversionMissed(state, id),
    baselineConversionSaved: getBaselineConversionSaved(state, id)
  };
}

export function getIgnoredModels(state, id) {
  return getSetup(state, id).ignoredModels !== undefined ? getSetup(state, id).ignoredModels : {};
}

export function getProjectHasCustomMappings(state, projectId) {
  return projectReducer.getProjectSegments(state, projectId).some((segment) => {
    if (!segment) {
      return false;
    }
    const runId = projectReducer.getSegmentRunId(state, projectId, segment.id);
    return getHasCustomMappings(state, runId);
  });
}

export function getHasCustomMappings(state, id, setupOverride) {
  return getCustomMappingsArr(state, id, setupOverride).some((m) => m.value !== undefined && m.value !== null);
}

export function getCustomMappings(state, id, setupOverride) {
  const setup = setupOverride ?? getSetup(state, id);
  return setup?.customMappings;
}

export function getCustomMappingsArr(state, id, setupOverride) {
  const mappings = getCustomMappings(state, id, setupOverride);
  return mappings ? Object.keys(mappings).map((key) => ({ value: mappings[key], label: key })) : [];
}

export function getCustomMapping(state, id, key) {
  return (getCustomMappings(state, id) || {})[key];
}

export function getCustomMappingOptions(state, id) {
  const datasetId = getDatasetId(state, id);
  const fields = homeReducer.getFieldsArr(state, datasetId);
  return fields
    ? fields
        .filter((field) => field.type === "number")
        .map((field) => ({
          value: field.label,
          label: field.label + " (" + numeral(field.mean).format(field.mean > 100 ? "0.[0]a" : "0.[00]") + ")"
        }))
    : [];
}

export function getIsValidCustomMappings(state, id) {
  const mappings = getCustomMappingsArr(state, id);
  if (!mappings || !mappings.length) {
    return true;
  }
  return !mappings.some((item) => !item.value || item.value === true);
}

export function getSetupLabel(state, id) {
  return (getSetup(state, id) || {}).label;
}

export function getCapacityPct(state, id) {
  return (getSetup(state, id) || {}).capactiyPct;
}

export function getBaselineCountContinuous(state, id, projectId, ignoreOverall = false) {
  const retProjectId =
    ignoreOverall || projectId !== undefined ? projectId : projectReducer.getProjectIdFromRunId(state, id);

  const isOverallSegment = ignoreOverall ? false : projectReducer.getIsOverallSegment(state, retProjectId, id);

  if (isOverallSegment) {
    const project = projectReducer.getProject(state, retProjectId);

    let totalCount = 0;

    project.segments
      .filter((s) => s.conditions.length > 0)
      .forEach((s) => {
        if (s && s.job && s.job.job_id !== undefined) {
          const currProfit = getBaselineCountContinuous(state, s.job.job_id, undefined, true);
          totalCount += isFinite(currProfit) ? currProfit : 0;
        }
      });

    return totalCount;
  }

  const currCount = (getSetup(state, id) || {}).baselineCountContinuous;
  if (currCount === undefined) {
    return runReducer.getContinuousCount(state, id);
  }

  return isFinite(currCount) ? Number(currCount) : 0;
}

export function getBaselineValueContinuous(state, id, projectId, ignoreOverall = false) {
  const retProjectId =
    ignoreOverall || projectId !== undefined ? projectId : projectReducer.getProjectIdFromRunId(state, id);

  const isOverallSegment = ignoreOverall ? false : projectReducer.getIsOverallSegment(state, retProjectId, id);

  if (isOverallSegment) {
    const project = projectReducer.getProject(state, retProjectId);

    let totalCount = 0;
    let totalSum = 0;

    project.segments
      .filter((s) => s.conditions.length > 0)
      .forEach((s) => {
        if (s && s.job && s.job.job_id !== undefined) {
          const mean = getBaselineValueContinuous(state, s.job.job_id, undefined, true);
          const count = getBaselineCountContinuous(state, s.job.job_id, undefined, true);

          totalCount += count;
          totalSum += count * mean;
        }
      });

    return totalSum / (totalCount || 1);
  }

  const currValue = (getSetup(state, id) || {}).baselineValueContinuous;
  if (currValue === undefined) {
    return runReducer.getContinuousValue(state, id);
  }

  return isFinite(currValue) ? Number(currValue) : 0;
}

export function getExpectedCountContinuous(state, id, projectId, ignoreOverall = false) {
  const retProjectId =
    ignoreOverall || projectId !== undefined ? projectId : projectReducer.getProjectIdFromRunId(state, id);

  const isOverallSegment = ignoreOverall ? false : projectReducer.getIsOverallSegment(state, retProjectId, id);

  if (isOverallSegment) {
    const project = projectReducer.getProject(state, retProjectId);

    let totalCount = 0;

    project.segments
      .filter((s) => s.conditions.length > 0)
      .forEach((s) => {
        if (s && s.job && s.job.job_id !== undefined) {
          const currProfit = getBaselineCountContinuous(state, s.job.job_id, undefined, true);
          totalCount += isFinite(currProfit) ? currProfit : 0;
        }
      });

    return totalCount;
  }

  const isRealityHammer = getIsRealityHammer(state, id);

  const expectedCount = (getSetup(state, id) || {}).expectedCountContinuous;

  if (isRealityHammer && expectedCount !== undefined) {
    return isFinite(expectedCount) ? Number(expectedCount) : expectedCount;
  }

  return getBaselineCountContinuous(state, id);
}

export function getExpectedValueContinuous(state, id, projectId, ignoreOverall = false) {
  const retProjectId =
    ignoreOverall || projectId !== undefined ? projectId : projectReducer.getProjectIdFromRunId(state, id);

  const isOverallSegment = ignoreOverall ? false : projectReducer.getIsOverallSegment(state, retProjectId, id);

  if (isOverallSegment) {
    const project = projectReducer.getProject(state, retProjectId);

    let totalCount = 0;
    let totalSum = 0;

    project.segments
      .filter((s) => s.conditions.length > 0)
      .forEach((s) => {
        if (s && s.job && s.job.job_id !== undefined) {
          const mean = getExpectedValueContinuous(state, s.job.job_id, undefined, true);
          const count = getExpectedCountContinuous(state, s.job.job_id, undefined, true);

          totalCount += count;
          totalSum += count * mean;
        }
      });

    return totalSum / (totalCount || 1);
  }

  const isRealityHammer = getIsRealityHammer(state, id);

  const expectedValue = (getSetup(state, id) || {}).expectedValueContinuous;

  if (isRealityHammer && expectedValue !== undefined) {
    return isFinite(expectedValue) ? Number(expectedValue) : expectedValue;
  }

  return getBaselineValueContinuous(state, id);
}

export function getCurrentContinuousProfit(state, runId, projectId, ignoreOverall = false) {
  const retProjectId =
    ignoreOverall || projectId !== undefined ? projectId : projectReducer.getProjectIdFromRunId(state, runId);

  const isOverallSegment = ignoreOverall ? false : projectReducer.getIsOverallSegment(state, retProjectId, runId);

  if (isOverallSegment) {
    const project = projectReducer.getProject(state, retProjectId);

    let totalProfit = 0;

    project.segments
      .filter((s) => s.conditions.length > 0)
      .forEach((s) => {
        if (s && s.job && s.job.job_id !== undefined) {
          const currProfit = getCurrentContinuousProfit(state, s.job.job_id, undefined, true);
          totalProfit += isFinite(currProfit) ? currProfit : 0;
        }
      });

    return totalProfit;
  }

  const currProfit = (getSetup(state, runId) || {}).currentProfit;

  const estimatedCurrProfit =
    getBaselineCountContinuous(state, runId, projectId, ignoreOverall) *
    getBaselineValueContinuous(state, runId, projectId, ignoreOverall);

  return isFinite(currProfit) ? Number(currProfit) : isFinite(estimatedCurrProfit) ? estimatedCurrProfit : 0;
}

export function getCurrentContinuousPercent(state, runId, projectId, ignoreOverall = false) {
  const retProjectId =
    ignoreOverall || projectId !== undefined ? projectId : projectReducer.getProjectIdFromRunId(state, runId);

  const isOverallSegment = ignoreOverall ? false : projectReducer.getIsOverallSegment(state, retProjectId, runId);

  if (isOverallSegment) {
    const project = projectReducer.getProject(state, retProjectId);

    let totalNum = 0;
    let totalCount = 0;

    project.segments
      .filter((s) => s.conditions.length > 0)
      .forEach((s) => {
        if (s && s.job && s.job.job_id !== undefined) {
          const currPercent = getCurrentContinuousPercent(state, s.job.job_id, undefined, true);
          const currCount = getExpectedCountContinuous(state, s.job.job_id);
          const currNum = (currPercent || 1) * currCount;

          totalCount += isFinite(currCount) ? currCount : 0;
          totalNum += isFinite(currNum) ? currNum : 0;
        }
      });

    const retTotalPercent = totalNum / (totalCount || 1);
    return isFinite(retTotalPercent) ? Number(retTotalPercent) : 1;
  }

  const currPercent = (getSetup(state, runId) || {}).continuousPercent;
  return isFinite(currPercent) ? Number(currPercent) : 1;
}

export function getTargetPositiveAllocation(state, id) {
  const pctOfRows = runReducer.getPctOfRows(state, id);
  const dAlloc = (pctOfRows * 100).toFixed(2);

  return (getSetup(state, id) || {}).targetPositiveAllocation === undefined
    ? dAlloc
    : (getSetup(state, id) || {}).targetPositiveAllocation;
}

export function getLabel(state, id) {
  const job = getJob(state, id);
  return job && job.name ? job.name : getSetup(state, id).label;
}

export function getCostSliderRatio(state, id) {
  return getSetup(state, id).costSliderRatio;
}

export function getSelectedFields(state, id) {
  return getSetup(state, id).selectedFields ? getSetup(state, id).selectedFields : {};
}

export function getDefaultTruePositive(state, id) {
  const success = getSuccess(state, id);

  if (success === undefined) {
    return 1;
  }

  let defaultTruePositive = 0;

  if (Array.isArray(success)) {
    for (const value of success) {
      defaultTruePositive += value.count;
    }
  } else {
    defaultTruePositive += success.count;
  }

  return defaultTruePositive;
}

export function getDefaultFalsePositive(state, id) {
  const success = getSuccess(state, id);

  if (success === undefined) {
    return 1;
  }

  let defaultTruePositive = 0;

  if (Array.isArray(success)) {
    for (const value of success) {
      defaultTruePositive += value.count;
    }
  } else {
    defaultTruePositive += success.count;
  }

  const dataset = getDataset(state, getDatasetId(state, id));

  return dataset !== undefined ? dataset.rowCount - defaultTruePositive : 1;
}

function createOverallIgnored(state, projectId, id, defaultOnly, skipOverallCombination) {
  const segments = projectReducer.getProjectSegments(state, projectId, false);

  const childIgnored = segments.map((s) => {
    const jobId = projectReducer.getSegmentRunId(state, projectId, s.id);
    return getIgnored(state, jobId, undefined, defaultOnly);
  });

  const retIgnored = {};

  childIgnored.forEach((currIgnored) => {
    Object.keys(currIgnored).forEach((col) => {
      retIgnored[col] = currIgnored[col];
    });
  });

  childIgnored.forEach((currIgnored) => {
    Object.keys(retIgnored).forEach((col) => {
      if (Boolean(currIgnored[col]) !== Boolean(retIgnored[col])) {
        retIgnored[col] = skipOverallCombination ? false : "SOME";
      }
    });
  });

  return retIgnored;
}

export function getIgnored(state, id, setupOverride, defaultOnly = false, skipOverallCombination = false) {
  const projectId = projectReducer.getProjectIdFromRunId(state, id);
  if (!setupOverride && projectId && projectReducer.getIsOverallSegment(state, projectId, id)) {
    return createOverallIgnored(state, projectId, id, defaultOnly, skipOverallCombination);
  }

  const currSetup = setupOverride ? setupOverride : getSetup(state, id);
  const defaultSetup = runReducer.getJobSetup(state, id);
  const defaultIgnored = defaultSetup && defaultSetup.ignored;
  const currIgnored = setupOverride || isNaN(id) ? currSetup && currSetup.ignored : runReducer.getIgnored(state, id);

  const retIgnored = {};

  if (defaultIgnored) {
    Object.keys(defaultIgnored).forEach((col) => {
      retIgnored[col] = defaultIgnored[col];
    });
  }

  if (!defaultOnly && currIgnored) {
    Object.keys(currIgnored).forEach((col) => {
      retIgnored[col] = currIgnored[col];
    });
  }

  if (skipOverallCombination) {
    Object.keys(retIgnored).forEach((col) => {
      if (!retIgnored[col]) {
        delete retIgnored[col];
      }
    });
  }

  return retIgnored;
}

function createOverallIgnoredValue(state, projectId, id, defaultOnly, skipOverallCombination) {
  const segments = projectReducer.getProjectSegments(state, projectId, false);

  const childIgnoredValue = segments.map((s) => {
    const jobId = projectReducer.getSegmentRunId(state, projectId, s.id);
    return getIgnoredValue(state, jobId, undefined, defaultOnly, skipOverallCombination);
  });

  const retIgnoredValue = {};

  childIgnoredValue.forEach((currIgnoredValue) => {
    Object.keys(currIgnoredValue).forEach((col) => {
      if (!retIgnoredValue[col]) {
        retIgnoredValue[col] = {};
      }
      Object.keys(currIgnoredValue[col]).forEach((val) => {
        retIgnoredValue[col][val] = currIgnoredValue[col][val];
      });
    });
  });

  childIgnoredValue.forEach((currIgnoredValue) => {
    Object.keys(retIgnoredValue).forEach((col) => {
      Object.keys(retIgnoredValue[col]).forEach((val) => {
        if (Boolean(currIgnoredValue[col] && currIgnoredValue[col][val]) !== Boolean(retIgnoredValue[col][val])) {
          retIgnoredValue[col][val] = skipOverallCombination ? false : "SOME";
        }
      });
    });
  });

  if (skipOverallCombination) {
    Object.keys(retIgnoredValue).forEach((col) => {
      let hasValues = false;
      if (retIgnoredValue[col]) {
        Object.keys(retIgnoredValue[col]).forEach((val) => {
          if (!hasValues && retIgnoredValue[col][val]) {
            hasValues = true;
          }
        });
      }
      if (!hasValues) {
        delete retIgnoredValue[col];
      }
    });
  }

  return retIgnoredValue;
}

export function getIgnoredValue(state, id, setupOverride, defaultOnly = false, skipOverallCombination = false) {
  const projectId = projectReducer.getProjectIdFromRunId(state, id);
  if (!setupOverride && projectId && projectReducer.getIsOverallSegment(state, projectId, id)) {
    return createOverallIgnoredValue(state, projectId, id, defaultOnly, skipOverallCombination);
  }

  const currSetup = setupOverride ? setupOverride : getSetup(state, id);
  const defaultSetup = runReducer.getJobSetup(state, id);
  const defaultIgnoredValue = defaultSetup && defaultSetup.ignoredValue;

  const currIgnoredValue =
    setupOverride || isNaN(id) ? currSetup && currSetup.ignoredValue : runReducer.getIgnoredValue(state, id);

  const retIgnoredValue = {};

  if (defaultIgnoredValue) {
    Object.keys(defaultIgnoredValue).forEach((col) => {
      const retColIgnored = retIgnoredValue[col] ? retIgnoredValue[col] : {};
      if (defaultIgnoredValue[col]) {
        Object.keys(defaultIgnoredValue[col]).forEach((val) => {
          retColIgnored[val] = defaultIgnoredValue[col][val];
        });
        retIgnoredValue[col] = retColIgnored;
      }
    });
  }

  if (!defaultOnly && currIgnoredValue) {
    Object.keys(currIgnoredValue).forEach((col) => {
      const retColIgnored = retIgnoredValue[col] ? retIgnoredValue[col] : {};
      if (currIgnoredValue[col]) {
        Object.keys(currIgnoredValue[col]).forEach((val) => {
          retColIgnored[val] = currIgnoredValue[col][val];
        });
        retIgnoredValue[col] = retColIgnored;
      }
    });
  }

  if (skipOverallCombination) {
    Object.keys(retIgnoredValue).forEach((col) => {
      let hasValues = false;
      if (retIgnoredValue[col]) {
        Object.keys(retIgnoredValue[col]).forEach((val) => {
          if (!hasValues && retIgnoredValue[col][val]) {
            hasValues = true;
          }
        });
      }
      if (!hasValues) {
        delete retIgnoredValue[col];
      }
    });
  }

  return retIgnoredValue;
}

export function getNewDatasetIgnoredFromBp(state, datasetId, blueprintId) {
  const bp = blueprintId && !isNaN(blueprintId) ? blueprintReducer.getBlueprint(state, blueprintId) : null;
  const retIgnored = {};
  const datasetFields = homeReducer.getFields(state, datasetId);
  const outcome = homeReducer.getOutcome(state, datasetId);
  let blueprintIgnored = {};
  if (bp) {
    blueprintIgnored = blueprintReducer.getIgnoredFields(state, blueprintId);
  }
  for (const key in datasetFields) {
    if (outcome !== key && (blueprintIgnored[key] || datasetFields[key].ignored)) {
      retIgnored[key] = true;
    }
  }
  return retIgnored;
}

export function getIsEmptyCounts(state, id) {
  return (
    getTruePositive(state, id) === 0 &&
    getTrueNegative(state, id) === 0 &&
    getFalsePositive(state, id) === 0 &&
    getFalseNegative(state, id) === 0
  );
}

export function getIsEmptyCosts(state, id) {
  return (
    getConversionGain(state, id) === 0 &&
    getConversionCost(state, id) === 0 &&
    getConversionMissed(state, id) === 0 &&
    getConversionSaved(state, id) === 0
  );
}

export function getAllDataTransforms(state) {
  let retTransforms = {};

  state?.setup &&
    Object.keys(state.setup).forEach((sKey) => {
      retTransforms[sKey] = state.setup[sKey].dataTransforms ? [...state.setup[sKey].dataTransforms] : [];
    });

  return retTransforms;
}

export function getDataTransforms(state, id) {
  return getSetup(state, id).dataTransforms || [];
}

export function getAiReadyMetrics(state, id, datasetId, outcomeOverride) {
  const MULTI_CATEGORY_OUTCOME_WARNING = "Coming soon - this outcome has more than two categories.";

  const selectedDataset = datasetId ? getDataset(state, datasetId) : getDatasetFromSetup(state, id);

  if (!selectedDataset) {
    return null;
  }

  const ignored = id ? getIgnored(state, id) : {};

  const outcome = outcomeOverride !== undefined ? outcomeOverride : selectedDataset.outcome;
  const incProbs = selectedDataset.fields[outcome] && selectedDataset.fields[outcome].inclusionProbabilities;
  if (!incProbs) {
    if (selectedDataset.fields[outcome] && selectedDataset.fields[outcome].inclusionProbabilitiesWarning) {
      return {
        isAiReady: false,
        isMultiCategoryOutcome:
          selectedDataset.fields[outcome].inclusionProbabilitiesWarning === MULTI_CATEGORY_OUTCOME_WARNING,
        warning: selectedDataset.fields[outcome].inclusionProbabilitiesWarning
      };
    }
    return null;
  }

  const retMetrics = {
    aiReady: [],
    notAiReady: [],
    isAiReady: null,
    hasIgnoredAiReady: false,
    secondOrder: {
      aiReady: [],
      notAiReady: []
    }
  };

  let { min, max } = AI_SIGNAL_RANGE;

  if (selectedDataset?.options?.target_steps?.find((x) => x == "SHAP_EXPLORE") !== undefined) {
    min = AI_SIGNAL_RANGE.shapMin ?? 0.02;
  }

  [...incProbs]
    .sort((a, b) => b?.value - a?.value)
    .forEach((prob) => {
      if (prob && prob.keys) {
        const currField = {
          label: prob.keys.join(" and "),
          value: prob.value,
          keys: [...prob.keys]
        };
        if (currField.value >= min && currField.value <= max) {
          if (!ignored[currField.label]) {
            retMetrics.aiReady.push(currField);
          } else {
            retMetrics.hasIgnoredAiReady = true;
            retMetrics.notAiReady.push(currField);
          }
        } else {
          retMetrics.notAiReady.push(currField);
        }
      }
    });

  const incProbsInt = selectedDataset.fields[outcome].interactionInclusionProbabilities ?? [];

  [...incProbsInt]
    .sort((a, b) => b?.value - a?.value)
    .forEach((prob) => {
      if (prob && prob.keys) {
        const currField = {
          label: prob.keys.join(" and "),
          value: prob.value,
          keys: [...prob.keys]
        };
        if (currField.value >= min && currField.value <= max) {
          if (!ignored[currField.label]) {
            retMetrics.secondOrder.aiReady.push(currField);
          } else {
            retMetrics.hasIgnoredAiReady = true;
            retMetrics.secondOrder.notAiReady.push(currField);
          }
        } else {
          retMetrics.secondOrder.notAiReady.push(currField);
        }
      }
    });

  retMetrics.isAiReady = retMetrics.aiReady.length >= MIN_AI_READY_FIELDS;

  const aiReady = retMetrics.aiReady ?? [];
  const notAiReady = retMetrics.notAiReady ?? [];

  const aiReadyScores = aiReady.concat(notAiReady).map((x) => x.value);

  const aiReadyScore = aiReadyScores.length == 0 ? 0 : aiReadyScores.reduce((x, y) => x + y) / aiReadyScores.length;

  retMetrics.aiReadinessScore = aiReadyScore;

  if (selectedDataset.interaction_model_metrics !== undefined) {
    const metrics = selectedDataset.interaction_model_metrics_test ?? selectedDataset.interaction_model_metrics;

    if ("confusion_matrix" in metrics) {
      const legacyCM = metrics["confusion_matrix"];

      const cm = new ConfusionMatrix(legacyCM[3], legacyCM[2], legacyCM[1], legacyCM[0]);

      retMetrics.aiReadinessScore = Math.abs(cm.getMatthewsCorrelationCoefficient());
    } else if ("squared_correlation" in metrics) {
      retMetrics.aiReadinessScore = Math.sqrt(metrics["squared_correlation"]);
    }
  }

  retMetrics.aiReadinessStatus =
    retMetrics.aiReadinessScore < AI_READINESS_BREAKPOINT.NO_SIGNAL
      ? "NO_SIGNAL"
      : retMetrics.aiReadinessScore < AI_READINESS_BREAKPOINT.LOW_SIGNAL
        ? "LOW_SIGNAL"
        : retMetrics.aiReadinessScore < AI_READINESS_BREAKPOINT.HIGH_SIGNAL
          ? "HIGH_SIGNAL"
          : "SUSPECT_SIGNAL";

  return retMetrics;
}

const DATE_SUFFIXES = [
  { label: "Month", value: "_month" },
  { label: "Weekday", value: "_weekday" }
];
function getFieldHelper(dataset, column) {
  if (!dataset || !dataset.fields) {
    return undefined;
  }
  if (dataset.fields[column]) {
    return dataset.fields[column];
  } else {
    for (let i = 0; i < DATE_SUFFIXES.length; i++) {
      const currSuffix = DATE_SUFFIXES[i];
      if (column.substring(column.length - currSuffix.value.length) === currSuffix.value) {
        const retColumn = column.substring(0, column.length - currSuffix.value.length);
        if (dataset.fields[retColumn]) {
          return {
            ...dataset.fields[retColumn],
            correlationLabel: dataset.fields[retColumn].label + " (" + currSuffix.label + ")"
          };
        } else {
          return undefined;
        }
      }
    }
    return undefined;
  }
}

export function getCorrelationMetricsFromDataset(state, id, datasetId, outcomeOverride) {
  const selectedDataset = datasetId ? getDataset(state, datasetId) : getDatasetFromSetup(state, id);

  const outcome = outcomeOverride !== undefined ? outcomeOverride : selectedDataset && selectedDataset.outcome;

  const retCor = {
    problematicPredictors: [],
    predictors: []
  };

  const leakageInfo =
    selectedDataset &&
    selectedDataset.fields &&
    selectedDataset.fields[outcome] &&
    selectedDataset.fields[outcome].leakage_info;
  if (leakageInfo) {
    const sortedFields = Object.keys(leakageInfo)
      .filter(
        (key) =>
          leakageInfo[key] &&
          leakageInfo[key]["Abs Correlation"] !== null &&
          leakageInfo[key]["Abs Correlation"] !== undefined &&
          !isNaN(leakageInfo[key]["Abs Correlation"])
      )
      .sort((a, b) => leakageInfo[b]["Abs Correlation"] - leakageInfo[a]["Abs Correlation"]);
    for (let i = 0; i < sortedFields.length; i++) {
      const currCorrelation = leakageInfo[sortedFields[i]]["Abs Correlation"];
      const datasetField = getFieldHelper(selectedDataset, sortedFields[i]);
      if (datasetField) {
        const currField = {
          ...datasetField,
          correlation: currCorrelation
        };
        if (!isNaN(currCorrelation)) {
          if (
            currCorrelation >= PROBLEMATIC_PREDICTOR_RANGE.min &&
            currCorrelation <= PROBLEMATIC_PREDICTOR_RANGE.max
          ) {
            retCor.problematicPredictors.push(currField);
          } else {
            retCor.predictors.push(currField);
          }
        }
      }
    }
  } else if (selectedDataset && selectedDataset.fields) {
    const sortedFields = Object.keys(selectedDataset.fields)
      .filter((key) => selectedDataset.fields[key].correlation !== undefined)
      .sort((a, b) => selectedDataset.fields[b].correlation - selectedDataset.fields[a].correlation);

    for (let i = 0; i < sortedFields.length; i++) {
      const currField = selectedDataset.fields[sortedFields[i]];
      if (!isNaN(currField.correlation)) {
        if (
          currField.correlation >= PROBLEMATIC_PREDICTOR_RANGE.min &&
          currField.correlation <= PROBLEMATIC_PREDICTOR_RANGE.max
        ) {
          retCor.problematicPredictors.push(currField);
        } else {
          retCor.predictors.push(currField);
        }
      }
    }
  }

  return retCor;
}

export function getCorrelationsFromDataset(state, id, datasetId, outcomeOverride) {
  const selectedDataset = datasetId ? getDataset(state, datasetId) : getDatasetFromSetup(state, id);

  const outcome = outcomeOverride !== undefined ? outcomeOverride : selectedDataset && selectedDataset.outcome;
  const leakageInfo =
    selectedDataset &&
    selectedDataset.fields &&
    selectedDataset.fields[outcome] &&
    selectedDataset.fields[outcome].leakage_info;

  let correlations = [];

  if (leakageInfo) {
    const dFields = Object.keys(leakageInfo).filter(
      (key) =>
        leakageInfo[key] &&
        leakageInfo[key]["Abs Correlation"] !== null &&
        leakageInfo[key]["Abs Correlation"] !== undefined
    );

    correlations = dFields
      .filter((fKey) => selectedDataset.fields[fKey])
      .map((fKey) => {
        const currCorrelation = leakageInfo[fKey]["Abs Correlation"];
        const datasetField = getFieldHelper(selectedDataset, fKey);
        if (datasetField) {
          return {
            ...datasetField,
            correlation: currCorrelation
          };
        } else {
          return undefined;
        }
      });
  } else if (selectedDataset && selectedDataset.fields) {
    correlations = Object.keys(selectedDataset.fields).map((fKey) => selectedDataset.fields[fKey]);
  }

  return correlations
    .filter(
      (field) =>
        field &&
        field.correlation >= PROBLEMATIC_PREDICTOR_RANGE.min &&
        field.correlation <= PROBLEMATIC_PREDICTOR_RANGE.max
    )
    .sort((a, b) => b.correlation - a.correlation);
}

export function getSuccessPct(state, id) {
  const defaultTruePositive = getDefaultTruePositive(state, id);
  const defaultFalsePositive = getDefaultFalsePositive(state, id);

  return ((defaultTruePositive / (defaultTruePositive + defaultFalsePositive)) * 100).toFixed(2) + "%";
}

export function getHumanUnderstandable(state, id) {
  return getSetup(state, id).humanUnderstandable;
}

export function getAuditable(state, id) {
  return getSetup(state, id).auditable;
}

export function getRealTimeResults(state, id) {
  return getSetup(state, id).realTimeResults;
}

export function getFieldTypes(state, id) {
  return getSetup(state, id).fieldTypes ? getSetup(state, id).fieldTypes : {};
}

export function getTags(state, id) {
  const humanUnderstandable = getHumanUnderstandable(state, id);
  const auditable = getAuditable(state, id);
  const realTimeResults = getRealTimeResults(state, id);

  let tags = [];

  if (humanUnderstandable) {
    tags.push("HUMAN_UNDERSTANDABLE");
  }
  if (auditable) {
    tags.push("AUDITABLE");
  }
  if (realTimeResults) {
    tags.push("REALTIME_RESULTS");
  }
  if (tags.length === 0) {
    tags.push("ALL_MODELS");
  }

  return tags;
}

function createIgnoredArray(setupIgnored) {
  const retIgnored = Object.keys(setupIgnored)
    .filter((k) => setupIgnored[k])
    .map((k) => `${k} = ${setupIgnored[k]}`)
    .sort();
  return retIgnored.length ? retIgnored : undefined;
}

function createIgnoredValueArray(setupIgnoredValue) {
  const retIgnoredValue = Object.keys(setupIgnoredValue)
    .map((col) => {
      const currCol = Object.keys(setupIgnoredValue[col])
        .filter((k) => setupIgnoredValue[col][k] === true)
        .map((k) => `${k} = ${setupIgnoredValue[col][k]}`)
        .sort();
      return currCol.length ? [col, ...currCol] : undefined;
    })
    .filter((v) => v !== undefined)
    .sort();
  return retIgnoredValue.length ? retIgnoredValue : undefined;
}

export function getSetupIgnoreChanged(state, id) {
  const setup = getSetup(state, id);
  const defaultSetup = runReducer.getJobSetup(state, id);

  if (setup === undefined || defaultSetup === undefined) {
    return false;
  }

  // ignored columns with updates
  const sIgnored = getIgnored(state, id);
  // default ignored columns from run
  const dIgnored = getIgnored(state, id, undefined, true);

  const sIgnoredArr = createIgnoredArray(sIgnored);
  const dIgnoredArr = createIgnoredArray(dIgnored);

  // ignored values with updates
  const sIgnoredValues = getIgnoredValue(state, id);
  // default ignored values from run
  const dIgnoredValues = getIgnoredValue(state, id, undefined, true);

  const sIgnoredValuesArr = createIgnoredValueArray(sIgnoredValues);
  const dIgnoredValuesArr = createIgnoredValueArray(dIgnoredValues);

  return (
    JSON.stringify(sIgnoredArr) !== JSON.stringify(dIgnoredArr) ||
    JSON.stringify(sIgnoredValuesArr) !== JSON.stringify(dIgnoredValuesArr) ||
    !isEqual(setup.fieldTypes, defaultSetup.fieldTypes)
  );
}

export function getSetupChangedNew(state, id) {
  const setup = getSetup(state, id);

  return projectReducer.isSameSetup(setup, setup.defaultSetup);
}

export function getSetupChanged(state, id) {
  let {
    defaultSetup,
    defaultTruePositiveImpact,
    defaultFalsePositiveImpact,
    defaultTrueNegativeImpact,
    defaultFalseNegativeImpact,
    ...setup
  } = getSetup(state, id);

  const run = getJob(state, id);

  defaultSetup =
    defaultSetup && defaultSetup.data
      ? defaultSetup.data
      : run &&
        run.attributes &&
        run.attributes.extra &&
        run.attributes.extra.setupState &&
        JSON.parse(run.attributes.extra.setupState);

  const transform = transformReducer.getTransform(state, id);

  if (id !== "new" && JSON.stringify(setup.transform) !== JSON.stringify(transform)) {
    return true;
  }

  delete setup.frontier;
  delete setup.maxBudget;
  delete setup.minBudget;

  defaultSetup &&
    delete defaultSetup.defaultSetup &&
    delete defaultSetup.defaultTruePositiveImpact &&
    delete defaultSetup.defaultFalsePositiveImpact &&
    delete defaultSetup.defaultTrueNegativeImpact &&
    delete defaultSetup.defaultFalseNegativeImpact &&
    delete defaultSetup.frontier &&
    delete defaultSetup.maxBudget &&
    delete defaultSetup.minBudget &&
    defaultSetup.ignoredValue &&
    Object.keys(defaultSetup.ignoredValue).forEach((c) =>
      Object.keys(defaultSetup.ignoredValue[c]).forEach(
        (v) => defaultSetup.ignoredValue[c][v] === false && delete defaultSetup.ignoredValue[c][v]
      )
    ) &&
    Object.keys(defaultSetup.ignoredValue).forEach(
      (c) => Object.keys(defaultSetup.ignoredValue[c]).length === 0 && delete defaultSetup.ignoredValue[c]
    ) &&
    Object.keys(defaultSetup.ignored).forEach((c) => !defaultSetup.ignored[c] && delete defaultSetup.ignored[c]);

  setup.ignoredValue &&
    Object.keys(setup.ignoredValue).forEach((c) =>
      Object.keys(setup.ignoredValue[c]).forEach(
        (v) => setup.ignoredValue[c][v] === false && delete setup.ignoredValue[c][v]
      )
    );

  setup.ignoredValue &&
    Object.keys(setup.ignoredValue).forEach(
      (c) => Object.keys(setup.ignoredValue[c]).length === 0 && delete setup.ignoredValue[c]
    );

  setup.ignored && Object.keys(setup.ignored).forEach((c) => !setup.ignored[c] && delete setup.ignored[c]);

  return setup && defaultSetup ? JSON.stringify(defaultSetup) !== JSON.stringify(setup) : false;
}

export function getErrors(state, id) {
  const ignored = getIgnored(state, id);
  const ignoredValue = getIgnoredValue(state, id);
  const dataset = getDatasetFromSetup(state, id);

  let error = undefined;

  const ignoredCount = Object.keys(ignored).filter((i) => ignored[i] === true).length;
}

export function getHasSplit(state, id) {
  const splitColumn = getSplitColumn(state, id);

  return splitColumn && splitColumn.hasSplit === true;
}

export function getPrimaryBusinessConstraintType(state, projectId, id) {
  return (getPrimaryBusinessConstraint(state, projectId, id) || {}).type;
}

export function getPrimaryBusinessConstraint(state, projectId, id) {
  const constraints = getBusinessConstraints(state, projectId, id) || [];

  return constraints[0];
}

export function getSecondaryBusinessConstraintType(state, projectId, id) {
  return (getSecondaryBusinessConstraint(state, projectId, id) || {}).type;
}

export function getSecondaryBusinessConstraint(state, projectId, id) {
  const constraints = getBusinessConstraints(state, projectId, id) || [];

  return constraints[1];
}

export function getOverallBusinessConstraints(state, projectId) {
  const overallRunId = projectReducer.getOverallRunId(state, projectId);
  const setupLabels = getSetupLabels(state, overallRunId);

  return {
    primary: getPrimaryBusinessConstraint(state, projectId, overallRunId),
    secondary: getSecondaryBusinessConstraint(state, projectId, overallRunId),
    unitType: setupLabels.unitType
  };
}

export function getAllBusinessConstraints(state, projectId) {
  const drilldownValues = (projectReducer.getModelGroups(state, projectId) || {}).drilldownValues;

  if (drilldownValues === undefined) {
    return [];
  }

  return drilldownValues.map((s, i) => {
    const jobId = s.runId;
    const setupLabels = getSetupLabels(state, jobId);
    return {
      subgroup: s.label,
      color: s.color,
      primary: getPrimaryBusinessConstraint(state, projectId, jobId),
      secondary: getSecondaryBusinessConstraint(state, projectId, jobId),
      unitType: setupLabels.unitType
    };
  });
}

export function getBusinessConstraintsLegacy(state, id) {
  const setup = getSetup(state, id);

  return Object.values(setup.businessConstraints || {}).map((constraint) => {
    if (constraint.mode === undefined) {
      return {
        ...constraint,
        mode: BusinessConstraintMode.Number
      };
    } else {
      return constraint;
    }
  });
}

export function introspectCountsFromData(id, column, successValue) {
  return (dispatch, getState) => {
    const datasetId = getDatasetId(getState(), id);

    const job = runReducer.getJob(getState(), id);

    const insightFactory = homeReducer.getInsightFactory(getState(), datasetId, job);

    const insight = insightFactory.getInsight({ column });

    const data = insight.chart.data;

    const success = data.find(({ name }) => name == successValue).summary;
    const failure = data.find(({ name }) => name != successValue).summary;

    const tp = success.sum;
    const fp = failure.sum;
    const tn = failure.n - failure.sum;
    const fn = success.n - success.sum;

    const cm = new ConfusionMatrix(tp, tn, fp, fn);

    dispatch(updateCountsFromConfusionMatrix(id, cm));
  };
}

export function getImpliedBusinessConstraints(state, projectId, id) {
  return [
    {
      type: getImpliedBusinessConstraintType(state, projectId, id)
    }
  ];
}

export function getImpliedBusinessConstraintType(state, projectId, id) {
  const cm = projectReducer.getCurrentConfusionMatrix(state, id);

  const { tp, fp, tn, fn } = cm;

  const total = tp + fp + tn + fn;
  const pr = (tp + fn) / total;
  const tpr = tp + fp == 0 ? 1 : Math.max(tp / (tp + fp), pr);
  const coef = (tpr - pr) / (1 - pr);
  const processed = (tp + fp) / total;
  const impact = projectReducer.getCurrentProfit(state, projectId, id);
  let max_impact = impact;
  let max_processed = processed;

  const tp_value = getBaselineConversionGain(state, id);
  const fp_value = getBaselineConversionCost(state, id);
  const tn_value = getBaselineConversionSaved(state, id);
  const fn_value = getBaselineConversionMissed(state, id);

  let current_tp = 0;
  let current_fp = 0;
  let current_tn = tn + fp;
  let current_fn = tp + fn;
  let current_impact = current_tp * tp_value + current_fp * fp_value + current_tn * tn_value + current_fn * fn_value;
  if (current_impact > max_impact) {
    max_impact = current_impact;
    max_processed = processed;
  }

  let chunk_size = total / 50;
  while (current_fn > 0) {
    const remaining = current_tn + current_fn;
    const chunk = Math.min(chunk_size, remaining);
    const perfect = Math.min(chunk, current_fn);
    const random = chunk * (current_fn / remaining);
    const tp_delta = random + (perfect - random) * coef;
    const fp_delta = chunk - tp_delta;
    current_tp = current_tp + tp_delta;
    current_fp = current_fp + fp_delta;
    current_fn = current_fn - tp_delta;
    current_tn = current_tn - fp_delta;
    current_impact = current_tp * tp_value + current_fp * fp_value + current_tn * tn_value + current_fn * fn_value;
    const current_processed = (current_tp + current_fp) / total;
    if (current_impact > max_impact) {
      max_impact = current_impact;
      max_processed = current_processed;
    }
  }
  //enable when live
  let decayFunction = 0.01;
  const revenue = Math.abs(fp_value) + tp_value;

  let matrixError = -1;
  let byoFrontier;
  let cumulativeDecay;
  let baseDecay;
  const roundedPursue = Math.round(pr * 100) / 100;

  let byoProfit = tp * tp_value + fp * fp_value + tn * tn_value + fn * fn_value;

  while (matrixError < 0 && decayFunction <= 1) {
    byoFrontier = [];
    cumulativeDecay = 0;
    baseDecay = 1;
    for (let i = 0; i < 101; i++) {
      const populationPercentage = i;
      const convertedPercentage = i / 100;
      let currentDecay = 1;
      if (i !== 0) {
        if (convertedPercentage <= roundedPursue) {
          currentDecay = baseDecay - decayFunction / (roundedPursue * 100);
        } else {
          currentDecay = baseDecay - (1 - decayFunction) / ((1 - roundedPursue) * 100);
        }

        baseDecay = currentDecay;
      }
      cumulativeDecay += currentDecay;
      byoFrontier.push({
        populationPercentage,
        convertedPercentage,
        currentDecay
      });
    }

    for (let z = 1; z < 101; z++) {
      if (z === 1) {
        byoFrontier[z].ccrDecay = byoFrontier[z - 1].currentDecay / cumulativeDecay;
      } else {
        byoFrontier[z].ccrDecay = byoFrontier[z - 1].currentDecay / cumulativeDecay + byoFrontier[z - 1].ccrDecay;
      }

      byoFrontier[z].truePositive = byoFrontier[z].ccrDecay * total * pr;
      byoFrontier[z].cost = byoFrontier[z].convertedPercentage * total * fp_value;
      byoFrontier[z].revenue = byoFrontier[z].truePositive * revenue;

      byoFrontier[z].profit =
        byoFrontier[z].truePositive * tp_value +
        fp_value * (byoFrontier[z].convertedPercentage * total - byoFrontier[z].truePositive) +
        (total * pr - byoFrontier[z].truePositive) * fn_value +
        (total - total * byoFrontier[z].convertedPercentage - (total * pr - byoFrontier[z].truePositive)) * tn_value;
    }

    const foundMatrixError = byoFrontier.find((populationRate) => populationRate.convertedPercentage === roundedPursue);
    matrixError = foundMatrixError && foundMatrixError.profit !== undefined ? foundMatrixError.profit - byoProfit : 1;

    if (matrixError < 0) {
      decayFunction += 0.01;
    }
  }

  const maxProfitCase = byoFrontier.reduce(function (prev, current) {
    return prev.profit > current.profit ? prev : current;
  });

  //use maxProfitCase.profit and maxProfitCase.convertedPercentage
  if (maxProfitCase.profit >= max_impact - Math.abs(max_impact) * 0.1) {
    return SegmentMetric.Profit;
  } else if (max_processed > maxProfitCase.convertedPercentage) {
    return SegmentMetric.Cost;
  } else {
    return SegmentMetric.Revenue;
  }
  // if (impact >= max_impact - Math.abs(max_impact) * 0.1) {
  //   return SegmentMetric.Profit;
  // } else if (max_processed > processed) {
  //   return SegmentMetric.Cost;
  // } else {
  //   return SegmentMetric.Revenue;
  // }
}

export function getConstraintTypeLabelMap(state, id) {
  return {
    [SegmentMetric.Cost]: getAdvancedCostLabel(state, id),
    [SegmentMetric.Revenue]: getAdvancedRevenueLabel(state, id),
    [SegmentMetric.Profit]: getNetImpactLabel(state, id)
  };
}

export function getBusinessConstraints(state, projectId, id) {
  const setup = getSetup(state, id);

  const raw = Array.isArray(setup.businessConstraints)
    ? setup.businessConstraints
    : getBusinessConstraintsLegacy(state, id) || [];

  const converter = projectReducer.getBusinessConstraintConverter(state, projectId, id);

  const metricTypeLabelMap = getConstraintTypeLabelMap(state, id);

  return raw.map((constraint) => {
    const converted = converter(constraint);

    return {
      ...constraint,
      direction: getBusinessConstraintDirection(constraint),
      value: getBusinessConstraintValue(constraint),
      convertedValue: getBusinessConstraintValue(converted),
      typeLabel: metricTypeLabelMap[constraint.type]
    };
  });
}

export function getBusinessConstraintByType(state, projectId, id, type) {
  return getBusinessConstraints(state, projectId, id).find((c) => c.type == type);
}

export function getUnitType(state, id) {
  const label = getSetup(state, id).unitType;
  return label === undefined ? DEFAULT_LABELS.unitType : label;
}

/* Local Storage Nonsense */
export function setLocal(id, key, value) {
  const localCosts = localStorage.getItem("localCosts");
  if (localCosts !== undefined && localCosts !== null) {
    const localCostsJson = JSON.parse(localCosts);

    if (localCostsJson[id]) {
      localCostsJson[id][key] = value;
    } else {
      localCostsJson[id] = {
        [key]: value
      };
    }

    localStorage.setItem("localCosts", JSON.stringify(localCostsJson));
  } else {
    const localCostsJson = {
      [id]: {
        [key]: value
      }
    };

    localStorage.setItem("localCosts", JSON.stringify(localCostsJson));
  }
}

export function getLocal(id, key) {
  const localCosts = localStorage.getItem("localCosts");
  if (localCosts !== undefined && localCosts !== null) {
    const localCostsJson = JSON.parse(localCosts);

    if (localCostsJson[id]) {
      if (localCostsJson[id][key] !== undefined) {
        return Number(localCostsJson[id][key]);
      }
    }
  }

  return undefined;
}

/* Actions */
export function resetNewSetup() {
  return (dispatch, getState) => {
    return dispatch(updateSetup("new", { ...defaultSetup }));
  };
}

export function updateSetup(id, setup) {
  return (dispatch, getState) => {
    if (setup && setup.transform && Object.keys(setup.transform).length > 0) {
      dispatch(transformReducer.updateTransform(id, { ...setup.transform }));
    }

    return dispatch({ type: UPDATE_SETUP, id: id, data: setup });
  };
}

export function updateSetups(data) {
  return (dispatch, getState) => {
    let retTransforms = {};

    data &&
      Object.keys(data).forEach((setupId) => {
        const setup = data[setupId];

        if (setup && setup.transform && Object.keys(setup.transform).length > 0) {
          retTransforms[setupId] = { ...setup.transform };
          //dispatch(transformReducer.updateTransform(setupId, { ...setup.transform }));
        }
      });

    if (retTransforms && Object.keys(retTransforms).length > 0) {
      dispatch(transformReducer.updateTransforms(retTransforms));
    }

    return dispatch({ type: UPDATE_SETUPS, data: data });
  };
}

export function updateUnitType(id, unitType) {
  return { type: UPDATE_FIELD, id: id, data: unitType, key: "unitType" };
}

export function updateOutcome(id, outcome) {
  return { type: UPDATE_FIELD, id: id, data: outcome, key: "outcome" };
}

export function updateGoal(id, goal) {
  return { type: UPDATE_FIELD, id: id, data: goal, key: "goal" };
}

export function updateLabel(id, label) {
  return { type: UPDATE_FIELD, id: id, data: label, key: "label" };
}

export function updateDescription(id, description) {
  return { type: UPDATE_FIELD, id: id, data: description, key: "description" };
}

export function updateComparisonOptions(id, comparisonOptions) {
  return { type: UPDATE_FIELD, id: id, data: comparisonOptions, key: "comparisonOptions" };
}

export function updateComparisonOptionsField(id, field, value) {
  return (dispatch, getState) => {
    const comparisonOptions = getComparisonOptions(getState(), id);

    const retComparisonOptions = {
      ...comparisonOptions,
      [field]: value
    };

    return dispatch(updateComparisonOptions(id, retComparisonOptions));
  };
}

export function updateComparisonOptionsDateField(id, field) {
  return (dispatch, getState) => {
    return dispatch(updateComparisonOptionsField(id, "dateField", field));
  };
}

export function updateMadLibs(id, key, value) {
  return { type: UPDATE_FIELD, id: id, data: value, key: key };
}

export function updateBaselineCountContinuous(id, baselineCountContinuous) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineCountContinuous,
    key: "baselineCountContinuous"
  };
}

export function updateBaselineValueContinuous(id, baselineValueContinuous) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineValueContinuous,
    key: "baselineValueContinuous"
  };
}

export function updateExpectedCountContinuous(id, expectedCountContinuous) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: expectedCountContinuous,
    key: "expectedCountContinuous"
  };
}

export function updateExpectedValueContinuous(id, expectedValueContinuous) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: expectedValueContinuous,
    key: "expectedValueContinuous"
  };
}

export function updateCurrentProfit(id, currentProfit) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: currentProfit,
    key: "currentProfit"
  };
}

export function setCustomValues(id, data) {
  return { type: UPDATE_FIELD, id: id, data: data, key: "customValues" };
}

export function setCustomMappings(id, data) {
  return { type: UPDATE_FIELD, id: id, data: data, key: "customMappings" };
}

export function updateFrontierPercentProcessedMin(id, frontierPercentProcessedMin) {
  frontierPercentProcessedMin = convertIfNotNumeric(frontierPercentProcessedMin);
  frontierPercentProcessedMin = Math.max(0, frontierPercentProcessedMin);

  return {
    type: UPDATE_FIELD,
    id: id,
    data: frontierPercentProcessedMin,
    key: "frontierPercentProcessedMin"
  };
}

export function updateFrontierPercentProcessedMax(id, frontierPercentProcessedMax) {
  frontierPercentProcessedMax = convertIfNotNumeric(frontierPercentProcessedMax);
  frontierPercentProcessedMax = Math.min(1, frontierPercentProcessedMax);

  return {
    type: UPDATE_FIELD,
    id: id,
    data: frontierPercentProcessedMax,
    key: "frontierPercentProcessedMax"
  };
}

export function deleteFrontierPercentProcessedMin(id) {
  return {
    type: DELETE_FIELD,
    id: id,
    key: "frontierPercentProcessedMin"
  };
}

export function deleteFrontierPercentProcessedMax(id) {
  return {
    type: DELETE_FIELD,
    id: id,
    key: "frontierPercentProcessedMax"
  };
}

export function setFrontierPercentProcessed(projectId, value, useScenarioDefaults = false) {
  return (dispatch, getState) => {
    const state = getState();
    const selectedScenario = (projectReducer.getSelectedScenario(state, projectId) || {}).scenario;
    Object.keys(selectedScenario || [])
      .filter((v) => v !== "Overall")
      .forEach((v) => {
        let retValue = value;
        const currScenario = selectedScenario[v] || {};
        const currRunId = currScenario.runId;
        if (useScenarioDefaults) {
          const scenarioSetup = currScenario.setup || {};
          const frontierMin = getFrontierPercentProcessedMin(currRunId, scenarioSetup.frontierPercentProcessedMin);
          const frontierMax = getFrontierPercentProcessedMax(currRunId, scenarioSetup.frontierPercentProcessedMax);
          retValue = [frontierMin, frontierMax];
        }
        if (retValue !== undefined) {
          dispatch(updateFrontierPercentProcessedMin(currRunId, retValue[0]));
          dispatch(updateFrontierPercentProcessedMax(currRunId, retValue[1]));
        } else {
          dispatch(deleteFrontierPercentProcessedMin(currRunId));
          dispatch(deleteFrontierPercentProcessedMax(currRunId));
        }
      });
  };
}

export function updateUniqueId(id, uniqueId) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: uniqueId,
    key: "uniqueId"
  };
}

export function updateEndState(id, endState) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: endState,
    key: "endState"
  };
}

export function updateValueReplace(id, valueReplace) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: valueReplace,
    key: "valueReplace"
  };
}

export function updateShowBusiness(id, showBusiness) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: showBusiness,
    key: "showBusiness"
  };
}

export function updateIsBYOMModal(id, isBYOMModal) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: isBYOMModal,
    key: "isBYOMModal"
  };
}

export function updateBYOM(id, BYOM) {
  return (dispatch, getState) => {
    const datasetId = getDatasetId(getState(), id);

    const prefix = getIsContinuous(getState(), id) ? "external-regressor-" : "external-classifier-";

    return DatasetAPI.getExternalModelURL(datasetId).then((response) => {
      const BYOMURL = response.data_url.includes("azure://")
        ? response.presigned_url
        : response.presigned_url.fields.key;

      DatasetAPI.externalModelUrl(response, prefix + BYOM.name, BYOM.name, BYOM.url, BYOM.username, BYOM.password);

      return dispatch({
        type: UPDATE_FIELD,
        id: id,
        data: BYOMURL,
        key: "BYOMURL"
      });
    });
  };
}

export function updateOptimizeBy(id, optimizeBy) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: optimizeBy,
    key: "optimize_by"
  };
}

export function updateOptimizeGoal(id, optimizeGoal) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: optimizeGoal,
    key: "optimize_goal"
  };
}

export function updateNOpts(id, nOpts) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: nOpts,
    key: "n_opts"
  };
}

export function updatePredictionConstraints(id, predictionConstraints) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: predictionConstraints,
    key: "predictionConstraints"
  };
}

export function updateMinimumBound(id, minimumBound) {
  return (dispatch, getState) => {
    return dispatch(
      updatePredictionConstraints(id, {
        ...getPredictionConstraints(getState(), id),
        min: minimumBound
      })
    );
  };
}

export function updateMaximumBound(id, maximumBound) {
  return (dispatch, getState) => {
    return dispatch(
      updatePredictionConstraints(id, {
        ...getPredictionConstraints(getState(), id),
        max: maximumBound
      })
    );
  };
}

export function updateShowImpact(id, showImpact) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: showImpact,
    key: "showImpact"
  };
}

export function updateDataTransforms(id, transforms) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: transforms,
    key: "dataTransforms"
  };
}

export function addDataTransform(id, transform) {
  return (dispatch, getState) => {
    const setup = getSetup(getState(), id);

    // This is called from a dataset where id = datasetId
    if (setup === undefined || Object.keys(setup).length === 0) {
      dispatch(initNewSetup(id, id));
    }

    const transforms = getDataTransforms(getState(), id);

    const json = JSON.stringify(transform);

    const existing = transforms.find((t) => JSON.stringify(t) == json);

    if (existing !== undefined) {
      return existing;
    }

    dispatch(updateDataTransforms(id, [...transforms, transform]));

    return transform;
  };
}

export function removeDataTransform(id, transform) {
  return (dispatch, getState) => {
    const transforms = getDataTransforms(getState(), id);

    const json = JSON.stringify(transform);

    const filtered = transforms.filter((t) => JSON.stringify(t) != json);

    if (filtered.length == transforms.length) {
      return filtered;
    }

    dispatch(updateDataTransforms(id, filtered));

    return filtered;
  };
}

export function addValueReplace(id, column) {
  return (dispatch, getState) => {
    const valueReplace = { ...getValueReplace(getState(), id) };

    valueReplace[Object.keys(valueReplace).length] = {
      id: Object.keys(valueReplace).length,
      column: column,
      to: "",
      from: "",
      delete: false
    };

    return dispatch(updateValueReplace(id, valueReplace));
  };
}

export function updateFieldTypes(id, field, type) {
  return { type: UPDATE_FIELD_TYPE, id: id, key: field, data: type };
}

export function editValueReplace(id, vId, type, data) {
  return (dispatch, getState) => {
    const valueReplace = { ...getValueReplace(getState(), id) };

    if (valueReplace[vId] !== undefined && valueReplace[vId][type] !== undefined) {
      valueReplace[vId][type] = data;
    }

    return dispatch(updateValueReplace(id, valueReplace));
  };
}

export function updateSelectedFields(id, selectedFields) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: { ...getSelectedFields(getState(), id), ...selectedFields },
      key: "selectedFields"
    });
  };
}

export function updateHumanUnderstandable(id, humanUnderstandable) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: humanUnderstandable,
    key: "humanUnderstandable"
  };
}

export function updateAuditable(id, auditable) {
  return { type: UPDATE_FIELD, id: id, data: auditable, key: "auditable" };
}

export function updateRealTimeResults(id, realTimeResults) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: realTimeResults,
    key: "realTimeResults"
  };
}

export function updateCostSliderRatio(id, costSliderRatio) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: costSliderRatio,
    key: "costSliderRatio"
  };
}

export function updateSuccess(id, success) {
  return (dispatch, getState) => {
    const dataset = getDatasetFromSetup(getState(), id);

    let value = success?.value;

    if (!isNaN(Number(value))) {
      value = Number(value);
    }

    if (value !== undefined) {
      dispatch(
        galleryReducer.updateOutcomeSuccess(dataset?.id, {
          [dataset?.outcome]: {
            name: value,
            value: value
          }
        })
      );
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: success,
      key: "success"
    });
  };
}

export function resetImpacts(id) {
  return (dispatch, getState) => {
    const state = getState();
    if (getConversionGain(state, id) !== 1) {
      dispatch(updateConversionGain(id, 1));
    }
    if (getConversionCost(state, id) !== -1) {
      dispatch(updateConversionCost(id, -1));
    }
    if (getConversionMissed(state, id) !== -1) {
      dispatch(updateConversionMissed(id, -1));
    }
    if (getConversionSaved(state, id) !== 1) {
      dispatch(updateConversionSaved(id, 1));
    }
    const customValues = getCustomValues(state, id);
    if (customValues && Object.keys(customValues).length) {
      dispatch(setCustomValues(id, {}));
    }
  };
}

export function resetForAcademic(id, datasetId) {
  return (dispatch, getState) => {
    const datasetId = datasetId ? datasetId : getDatasetFromSetup(getState(), id).id;

    dispatch(introspectCounts(id, datasetId));
  };
}

export function checkCounts(id, dId, successOverride) {
  return (dispatch, getState) => {
    const state = getState();

    const success = successOverride !== undefined ? successOverride : getSuccess(state, id);

    const blueprint = getBlueprintFromSetup(state, id);

    if (
      blueprint &&
      blueprint.wizard &&
      blueprint.wizard.setup &&
      (blueprint.wizard.setup.pursuedRatio !== undefined ||
        Number(blueprint.wizard.setup.truePositive) !== Number(blueprint.wizard.setup?.success?.count))
    ) {
      return;
    }

    let count = 0;

    if (success !== undefined) {
      if (Array.isArray(success)) {
        for (const s of success) {
          count += s.count;
        }
      } else {
        count += success.count;
      }
    }

    const datasetId = dId ? dId : getDatasetFromSetup(state, id)?.id;

    if (count !== 0) {
      const dataset = getDataset(state, datasetId);
      if (dataset !== undefined) {
        dispatch(updateTruePositive(id, count));
        dispatch(updateFalsePositive(id, dataset.rowCount - count));
      }
    }
  };
}

export function getOutcomeWinRate(state, id) {
  const success = getSuccess(state, id);

  let count = 0;

  if (success !== undefined) {
    if (Array.isArray(success)) {
      for (const s of success) {
        count += s.count;
      }
    } else {
      count += success.count;
    }
  }

  const datasetId = getDatasetFromSetup(state, id) ? getDatasetFromSetup(state, id).id : undefined;

  if (count !== 0 && datasetId !== undefined) {
    const dataset = getDataset(state, datasetId);
    if (dataset !== undefined) {
      return ((count / dataset.rowCount) * 100).toFixed(2);
    }
  }

  return 0;
}

export function introspectCounts(id, dId, success) {
  return (dispatch, getState) => {
    const state = getState();
    const success = getSuccess(state, id);

    let count = 0;

    if (success !== undefined) {
      if (Array.isArray(success)) {
        for (const s of success) {
          count += s.count;
        }
      } else {
        count += success.count;
      }
    }

    const datasetId = dId ? dId : getDatasetFromSetup(state, id).id;

    console.log("wat is datasetId count", datasetId, count);

    if (count !== 0) {
      const dataset = getDataset(state, datasetId);
      if (dataset !== undefined) {
        dispatch(updateTruePositive(id, count));
        dispatch(updateFalsePositive(id, dataset.rowCount - count));
      }
    }
  };
}

export function updateProcessLimit(id, processLimit, skipProject = false) {
  return (dispatch, getState) => {
    if (getAimToHit(getState(), id) > processLimit) {
      dispatch(updateAimToHit(id, processLimit));
    }

    const conversionCost = getConversionCost(getState(), id);
    const projectId = projectReducer.getProjectIdFromRunId(getState(), id);

    const nBudget = Math.abs(processLimit * conversionCost);

    if (skipProject === false) {
      dispatch(projectReducer.setSegmentMaxBudgetByRunId(projectId, id, nBudget));
    }

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: processLimit ? Math.round(processLimit) : processLimit,
      key: "processLimitCount"
    });

    return Promise.resolve();
  };
}

export function updateProcessLimitPct(id, processLimitPct) {
  return (dispatch, getState) => {
    const processLimit = getProcessLimit(getState(), id);

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: processLimit * processLimitPct,
      key: "processLimit"
    });
  };
}

export function updateAimToHit(id, aimToHit) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: aimToHit,
    key: "aimToHit"
  };
}

export function updateBlueprintId(id, blueprintId) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: blueprintId,
    key: "blueprintId"
  };
}

export function updateOutcomePercentage(id, outcomePercentage) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: outcomePercentage,
    key: "outcomePercentage"
  };
}

export function updateScaledTotal(projectId, id, scaledTotal, skipCountsUpdate = false) {
  return (dispatch, getState) => {
    if (!skipCountsUpdate) {
      const scaleFactor = scaledTotal / getTotal(getState(), id);

      const cm = new ConfusionMatrix(
        Math.round(getTruePositive(getState(), id) * scaleFactor),
        Math.round(getTrueNegative(getState(), id) * scaleFactor),
        Math.round(getFalsePositive(getState(), id) * scaleFactor),
        Math.round(getFalseNegative(getState(), id) * scaleFactor)
      );

      dispatch(updateCountsFromConfusionMatrix(id, cm));
    }

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: scaledTotal,
      key: "scaledTotal"
    });

    id !== "new" && dispatch(projectReducer.resetBudget(projectId));
  };
}

export function updateAITrainingBudget(id, aiTrainingBudget) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: aiTrainingBudget,
    key: "aiTrainingBudget"
  };
}

export function updateConversionRate(id, conversionRate) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: conversionRate,
    key: "conversionRate"
  };
}

export function updateThreshold(id, threshold) {
  const retThreshold = threshold ? [threshold[0] > 0 ? 0 : threshold[0], threshold[1] < 0 ? 0 : threshold[1]] : [0, 0];

  return {
    type: UPDATE_FIELD,
    id: id,
    data: retThreshold,
    key: "threshold"
  };
}

export function updateIsBlueprint(id, isBlueprint) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: isBlueprint,
    key: "isBlueprint"
  };
}

export function updateMaximumConversionRate(id, maximumConversionRate) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: maximumConversionRate,
    key: "maximumConversionRate"
  };
}

export function resetCosts(id) {
  return (dispatch, getState) => {
    const localCosts = localStorage.getItem("localCosts");

    if (localCosts !== undefined && localCosts !== null) {
      const localCostsJson = JSON.parse(localCosts);

      delete localCostsJson[id];

      localStorage.setItem("localCosts", JSON.stringify(localCostsJson));
    }

    return Promise.resolve();
  };
}

export function updateCostErrors(id, errors) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: errors,
    key: "costErrors"
  };
}

export function updateLinkedCount(id, linkedCount) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: linkedCount,
    key: "linkedCount"
  };
}

export function updateLinkedSubgroup(id, sId, linkedRunId, percentage, projectId) {
  return (dispatch, getState) => {
    const project = projectReducer.getProject(getState(), projectId);
    let linkedRuns = JSON.parse(JSON.stringify(getLinkedRuns(getState(), id)));

    const modelGroups = projectReducer.getModelGroups(getState(), projectId);

    const usedPercent =
      modelGroups && modelGroups.drilldownValues
        ? modelGroups.drilldownValues
            .filter((a) => a.value !== "Overall" && a.runId !== id)
            .reduce((a, b) => a + b.setup.linkedRuns[sId].percentage, 0)
        : 0;

    const availablePercent = 100 - usedPercent;

    if (availablePercent < percentage) {
      toast(
        "Cannot allocate " +
          percentage +
          "% of " +
          linkedRuns[sId].name +
          " since there is only " +
          availablePercent +
          "% remaining.",
        {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        }
      );
    }
    const retPercent = Math.min(availablePercent, percentage);

    if (linkedRuns && linkedRuns[sId] !== undefined) {
      linkedRuns[sId] = {
        ...linkedRuns[sId],
        percentage: percentage === undefined ? linkedRuns[sId].percentage : Number(retPercent)
      };
    }

    return dispatch(updateLinkedRuns(id, linkedRuns));
  };
}

export function updateLinkedRunId(id, linkedRunId) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: linkedRunId,
    key: "linkedRunId"
  };
}

export function updateIgnoredModels(id, ignoredModels) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: ignoredModels,
    key: "ignoredModels"
  };
}

export function updateIgnoredModel(id, modelId, value = true) {
  return (dispatch, getState) => {
    const ignoredModels = { ...getIgnoredModels(getState(), id) };
    let retModelIds = [];
    if (typeof modelId === "string") {
      retModelIds = [modelId];
    } else if (modelId) {
      retModelIds = modelId;
    }
    let hasUpdate = false;

    retModelIds?.forEach((mId) => {
      if (Boolean(value) !== Boolean(ignoredModels[mId])) {
        hasUpdate = true;
      }
      if (value) {
        ignoredModels[mId] = true;
      } else {
        delete ignoredModels[mId];
      }
    });

    if (!hasUpdate) {
      return;
    }
    dispatch(updateIgnoredModels(id, ignoredModels));

    const projectId = projectReducer.getProjectIdFromRunId(getState(), id);
    dispatch(projectReducer.resetBudget(projectId));
    dispatch(runReducer.saveFrontier(id));
    let bestModelId = undefined;
    const currSegment = projectReducer.getSegments(getState(), projectId)?.find((seg) => seg?.runId === id);
    bestModelId = currSegment?.model?.id;

    if (bestModelId !== undefined) {
      dispatch(runReducer.updateFeatureSelection(id, bestModelId, true));
    }
  };
}

export function editCostError(id, fieldName, value) {
  return (dispatch, getState) => {
    const errors = { ...getCostErrors(id) };
    if (!value) {
      delete errors[fieldName];
    } else {
      errors[fieldName] = value;
    }
    dispatch(updateCostErrors(id, errors));
  };
}

export function updateErrorCosts(id, { conversionGain, conversionCost, conversionSaved, conversionMissed }) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: {
        conversionGain,
        conversionCost,
        conversionSaved,
        conversionMissed
      },
      key: "errorCosts"
    });
  };
}

export function updateCosts(id, { conversionGain, conversionCost, conversionSaved, conversionMissed }) {
  return (dispatch, getState) => {
    const setup = getSetup(getState(), id);

    if (setup) {
      return dispatch({
        type: UPDATE_COSTS,
        id: id,
        conversionGain: conversionGain !== undefined ? conversionGain : setup.conversionGain,
        conversionCost: conversionCost !== undefined ? conversionCost : setup.conversionCost,
        conversionSaved: conversionSaved !== undefined ? conversionSaved : setup.conversionSaved,
        conversionMissed: conversionMissed !== undefined ? conversionMissed : setup.conversionMissed
      });
    }

    return Promise.resolve();
  };
}

export function updateCounts(id, { truePositive, falsePositive, trueNegative, falseNegative }) {
  return (dispatch, getState) => {
    const setup = getSetup(getState(), id);

    if (setup) {
      return dispatch({
        type: UPDATE_COUNTS,
        id: id,
        truePositive: truePositive !== undefined ? truePositive : setup.truePositive,
        falsePositive: falsePositive !== undefined ? falsePositive : setup.falsePositive,
        trueNegative: trueNegative !== undefined ? trueNegative : setup.trueNegative,
        falseNegative: falseNegative !== undefined ? falseNegative : setup.falseNegative
      });
    }

    return Promise.resolve();
  };
}

export function updateConversionGain(
  id,
  conversionGain,
  useLocalCosts = true,
  skipConvert = false,
  projectId = undefined
) {
  if (!skipConvert) {
    conversionGain = convertIfNotNumeric(conversionGain);
  }

  if (useLocalCosts === true) {
    setLocal(id, "conversionGain", conversionGain);
  }

  return {
    type: UPDATE_FIELD,
    id: id,
    data: conversionGain,
    key: "conversionGain"
  };
}

export function updateConversionCost(
  id,
  conversionCost,
  useLocalCosts = true,
  skipConvert = false,
  projectId = undefined
) {
  if (!skipConvert) {
    conversionCost = convertIfNotNumeric(conversionCost);
  }

  if (useLocalCosts === true) {
    setLocal(id, "conversionCost", conversionCost);
  }

  return {
    type: UPDATE_FIELD,
    id: id,
    data: conversionCost,
    key: "conversionCost"
  };
}

export function updateConversionMissed(
  id,
  conversionMissed,
  useLocalCosts = true,
  skipConvert = false,
  projectId = undefined
) {
  if (!skipConvert) {
    conversionMissed = convertIfNotNumeric(conversionMissed);
  }

  if (useLocalCosts === true) {
    setLocal(id, "conversionMissed", conversionMissed);
  }

  return {
    type: UPDATE_FIELD,
    id: id,
    data: conversionMissed,
    key: "conversionMissed"
  };
}

export function updateConversionSaved(
  id,
  conversionSaved,
  useLocalCosts = true,
  skipConvert = false,
  projectId = undefined
) {
  if (!skipConvert) {
    conversionSaved = convertIfNotNumeric(conversionSaved);
  }

  if (useLocalCosts === true) {
    setLocal(id, "conversionSaved", conversionSaved);
  }

  return {
    type: UPDATE_FIELD,
    id: id,
    data: conversionSaved,
    key: "conversionSaved"
  };
}

export function updateBaselineConversionGain(id, baselineConversionGain) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineConversionGain,
    key: "baselineConversionGain"
  };
}

export function updateBaselineConversionCost(id, baselineConversionCost) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineConversionCost,
    key: "baselineConversionCost"
  };
}

export function updateBaselineConversionMissed(id, baselineConversionMissed) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineConversionMissed,
    key: "baselineConversionMissed"
  };
}

export function updateBaselineConversionSaved(id, baselineConversionSaved) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineConversionSaved,
    key: "baselineConversionSaved"
  };
}

export function updateBaselineAdvancedRevenue(id, baselineAdvancedRevenue) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineAdvancedRevenue,
    key: "baselineAdvancedRevenue"
  };
}

export function updateBaselineAdvancedCost(id, baselineAdvancedCost) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineAdvancedCost,
    key: "baselineAdvancedCost"
  };
}

export function updateAdvancedRevenue(id, revenue) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: revenue,
    key: "advancedRevenue"
  };
}

export function updateAdvancedCost(id, cost) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: cost,
    key: "advancedCost"
  };
}

export function updateContinuousCount(id, continuousCount) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: continuousCount,
    key: "continuousCount"
  };
}

export function updateContinuousAveragePrice(id, continuousAveragePrice) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: continuousAveragePrice,
    key: "continuousAveragePrice"
  };
}

export function updateContinuousPercent(id, continuousPercent) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: continuousPercent,
    key: "continuousPercent"
  };
}

export function updatePrimaryBusinessConstraintType(projectId, id, type) {
  return (dispatch, getState) => {
    const isOverallSegment = projectReducer.getIsOverallSegment(getState(), projectId, id, true) === true;

    if (isOverallSegment && type === undefined) {
      type = SegmentMetric.Revenue;
    }

    if (type == undefined) {
      return dispatch(resetBusinessConstraints(projectId, id));
    }

    const primary = {
      type: type,
      mode: BusinessConstraintMode.Percent
    };

    if (primary.type === SegmentMetric.Revenue || primary.type === SegmentMetric.Cost) {
      primary.min = 1;
    }

    const constraints = [primary];

    return dispatch(updateAllBusinessConstraints(projectId, id, constraints));
  };
}

export function updateSecondaryBusinessConstraintType(projectId, id, type) {
  return (dispatch, getState) => {
    const current = getBusinessConstraints(getState(), projectId, id);

    if (current.length == 0) {
      return dispatch(updatePrimaryBusinessConstraintType(projectId, id, type));
    }

    const updated = [current[0]];

    if (type === undefined) {
      return dispatch(updateAllBusinessConstraints(projectId, id, updated));
    } else if (type != updated[0].type) {
      updated.push({
        type: type,
        mode: BusinessConstraintMode.Percent,
        min: 1
      });

      return dispatch(updateAllBusinessConstraints(projectId, id, updated));
    }
  };
}

export function resetBusinessConstraints(projectId, id) {
  return (dispatch, getState) => {
    dispatch(updateAllBusinessConstraints(projectId, id, []));
  };
}

export function updateBusinessConstraint(projectId, id, constraint) {
  return (dispatch, getState) => {
    const constraints = getBusinessConstraints(getState(), projectId, id);

    const updated = [];

    let hasConstraint = false;

    for (let current of constraints) {
      if (current.type == constraint.type) {
        updated.push(constraint);
        hasConstraint = true;
      } else {
        updated.push(current);
      }
    }

    if (!hasConstraint) {
      updated.push(constraint);
    }

    return dispatch(updateAllBusinessConstraints(projectId, id, updated));
  };
}

export function updateBusinessConstraintMode(projectId, id, type, mode) {
  return (dispatch, getState) => {
    const constraint = getBusinessConstraintByType(getState(), projectId, id, type);

    if (constraint !== undefined) {
      const converter = projectReducer.getBusinessConstraintConverter(getState(), projectId, id, mode, true);

      const updated = converter(constraint);

      dispatch(updateBusinessConstraint(projectId, id, updated));
    }
  };
}

export function updateBusinessConstraintValue(projectId, id, type, value) {
  return (dispatch, getState) => {
    const constraint = getBusinessConstraintByType(getState(), projectId, id, type);

    if (constraint !== undefined) {
      const updated = { ...constraint };

      switch (constraint.direction) {
        case BusinessConstraintDirection.Above:
          updated.min = value;
          break;

        case BusinessConstraintDirection.Below:
          updated.max = value;
          break;
      }

      dispatch(updateBusinessConstraint(projectId, id, updated));
    }
  };
}

export function updateBusinessConstraintDirection(projectId, id, type, direction) {
  return (dispatch, getState) => {
    const constraint = getBusinessConstraintByType(getState(), projectId, id, type);

    if (constraint !== undefined) {
      const updated = {
        type: constraint.type,
        mode: constraint.mode
      };

      if (constraint.direction != direction) {
        if (constraint.direction == BusinessConstraintDirection.Between) {
          switch (constraint.direction) {
            case BusinessConstraintDirection.Above:
              updated.min = constraint.min;
              break;

            case BusinessConstraintDirection.Below:
              updated.max = constraint.max;
              break;

            case BusinessConstraintDirection.Between:
            case BusinessConstraintDirection.None:
            default:
          }
        } else {
          const value = constraint.value;

          switch (direction) {
            case BusinessConstraintDirection.Above:
              updated.min = value;
              break;

            case BusinessConstraintDirection.Below:
              updated.max = value;
              break;

            case BusinessConstraintDirection.Between:
              updated.min = value;
              updated.max = value;
              break;

            case BusinessConstraintDirection.None:
            default:
          }
        }

        dispatch(updateBusinessConstraint(projectId, id, updated));
      }
    }
  };
}

export function getBusinessConstraintValue(constraint) {
  switch (getBusinessConstraintDirection(constraint)) {
    case BusinessConstraintDirection.Above:
      return constraint.min;

    case BusinessConstraintDirection.Below:
      return constraint.max;

    default:
      return undefined;
  }
}

export function getBusinessConstraintDirection(constraint) {
  if (constraint === undefined) {
    return undefined;
  }

  if (constraint.min !== undefined && constraint.max !== undefined) {
    return BusinessConstraintDirection.Between;
  } else if (constraint.min !== undefined) {
    return BusinessConstraintDirection.Above;
  } else if (constraint.max !== undefined) {
    return BusinessConstraintDirection.Below;
  } else {
    return BusinessConstraintDirection.None;
  }
}

const ALLOWED_CONSTRAINT_KEYS = {
  type: true,
  mode: true,
  min: true,
  max: true
};

export function updateAllBusinessConstraints(projectId, id, constraints) {
  return (dispatch, getState) => {
    const cleanedConstraints = constraints.map((constraint) =>
      Object.keys(constraint)
        .filter((key) => ALLOWED_CONSTRAINT_KEYS[key])
        .reduce((obj, key) => {
          obj[key] = constraint[key];
          return obj;
        }, {})
    );

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: cleanedConstraints,
      key: "businessConstraints"
    });

    return dispatch(projectReducer.resetBudget(projectId));
  };
}

export function updateBusinessObjective(projectId, runId, businessObjective, skipChild = false) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_FIELD,
      id: runId,
      data: businessObjective,
      key: "businessObjective"
    });

    if (skipChild === false) {
      const childIds = projectReducer.getChildRunIdsFromRunId(getState(), runId);

      childIds &&
        childIds.length > 0 &&
        childIds.forEach((c) => dispatch(updateBusinessObjective(projectId, c, businessObjective, true)));
    }

    return dispatch(projectReducer.resetBudget(projectId));
  };
}

export function updateEnableFairnessMetrics(id, enableFairnessMetrics) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: enableFairnessMetrics,
    key: "enableFairnessMetrics"
  };
}

export function updateFairnessObjective(id, fairnessObjective, skipBackwardsCompatibility = false) {
  return (dispatch, getState) => {
    // fallback for older fairness projects
    const currentFairnessEnable = getEnableFairnessMetrics(getState(), id, true);
    if (!skipBackwardsCompatibility && !currentFairnessEnable) {
      dispatch(updateEnableFairnessMetrics(id, true));
    }
    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: fairnessObjective,
      key: "fairnessObjective"
    });
  };
}

export function updateFairnessToleranceBound(id, fairnessToleranceBound) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: fairnessToleranceBound,
    key: "fairnessToleranceBound"
  };
}

export function updateBiasReduction(id, biasReduction) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: biasReduction,
    key: "biasReduction"
  };
}

export function updateHasLinkedProject(id, hasLinkedProject) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: hasLinkedProject,
    key: "hasLinkedProject"
  };
}

export function updateTargetPositiveAllocation(id, targetPositiveAllocation) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: targetPositiveAllocation,
    key: "targetPositiveAllocation"
  };
}

export function updateTargeted(id, targeted) {
  return { type: UPDATE_FIELD, id: id, data: targeted, key: "targeted" };
}

export function updateTotal(id, total) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: total,
    key: "total"
  };
}

function convertIfNotNumeric(x) {
  if (isNaN(x) || x === "") {
    return 0;
  }

  return Number(x);
}

export function updateCountsFromConfusionMatrix(id, cm, useLocalCosts) {
  if (!cm) {
    return;
  }

  const cmValues = {
    truePositive: cm.getTruePositive(),
    trueNegative: cm.getTrueNegative(),
    falsePositive: cm.getFalsePositive(),
    falseNegative: cm.getFalseNegative()
  };

  if (useLocalCosts) {
    Object.keys(cmValues).forEach((key) => {
      setLocal(id, key, cmValues[key]);
    });
  }

  return {
    type: UPDATE_FIELDS,
    id: id,
    data: cmValues
  };
}

export function updateTruePositive(id, truePositive, useLocalCosts = false) {
  truePositive = convertIfNotNumeric(truePositive);

  if (useLocalCosts === true) {
    setLocal(id, "truePositive", truePositive);
  }

  return {
    type: UPDATE_FIELD,
    id: id,
    data: truePositive,
    key: "truePositive"
  };
}

export function updateTrueNegative(id, trueNegative, useLocalCosts = false) {
  trueNegative = convertIfNotNumeric(trueNegative);

  if (useLocalCosts === true) {
    setLocal(id, "trueNegative", trueNegative);
  }

  return {
    type: UPDATE_FIELD,
    id: id,
    data: trueNegative,
    key: "trueNegative"
  };
}

export function updateFalsePositive(id, falsePositive, useLocalCosts = false) {
  falsePositive = convertIfNotNumeric(falsePositive);

  if (useLocalCosts === true) {
    setLocal(id, "falsePositive", falsePositive);
  }

  return {
    type: UPDATE_FIELD,
    id: id,
    data: falsePositive,
    key: "falsePositive"
  };
}

export function updateFalseNegative(id, falseNegative, useLocalCosts = false) {
  falseNegative = convertIfNotNumeric(falseNegative);

  if (useLocalCosts === true) {
    setLocal(id, "falseNegative", falseNegative);
  }

  return {
    type: UPDATE_FIELD,
    id: id,
    data: falseNegative,
    key: "falseNegative"
  };
}

export function updatePursuedCount(id, pursuedCount) {
  return (dispatch, getState) => {
    const isRealityHammer = getIsRealityHammer(getState(), id);

    const baselineCounts = { ...getBaselineCounts(getState(), id) };

    const truePositive =
      isRealityHammer && baselineCounts?.truePositive !== undefined
        ? baselineCounts.truePositive
        : getTruePositive(getState(), id);
    const falsePositive =
      isRealityHammer && baselineCounts?.falsePositive !== undefined
        ? baselineCounts.falsePositive
        : getFalsePositive(getState(), id);

    const pursuedRatio = getPursuedRatio(getState(), id);

    const ratio =
      pursuedRatio !== undefined
        ? pursuedRatio / 100
        : truePositive + falsePositive === 0
          ? 0
          : truePositive / (truePositive + falsePositive);

    if (isRealityHammer) {
      baselineCounts.truePositive = Math.round(ratio * pursuedCount);
      baselineCounts.falsePositive = Math.round((1 - ratio) * pursuedCount);
      dispatch(updateBaselineCounts(id, baselineCounts));
    } else {
      dispatch(updateTruePositive(id, Math.round(ratio * pursuedCount)));
      dispatch(updateFalsePositive(id, Math.round((1 - ratio) * pursuedCount)));
    }
  };
}

export function updateNotPursuedCount(id, notPursuedCount) {
  return (dispatch, getState) => {
    const isRealityHammer = getIsRealityHammer(getState(), id);

    const baselineCounts = { ...getBaselineCounts(getState(), id) };

    const falseNegative =
      isRealityHammer && baselineCounts !== undefined ? baselineCounts.falseNegative : getFalseNegative(getState(), id);
    const trueNegative =
      isRealityHammer && baselineCounts !== undefined ? baselineCounts.trueNegative : getTrueNegative(getState(), id);

    const notPursuedRatio = getNotPursuedRatio(getState(), id);

    const ratio =
      notPursuedRatio !== undefined
        ? notPursuedRatio / 100
        : falseNegative + trueNegative === 0
          ? 0
          : falseNegative / (trueNegative + falseNegative);

    if (isRealityHammer) {
      baselineCounts.falseNegative = Math.round(ratio * notPursuedCount);
      baselineCounts.trueNegative = Math.round((1 - ratio) * notPursuedCount);
      dispatch(updateBaselineCounts(id, baselineCounts));
    } else {
      dispatch(updateFalseNegative(id, Math.round(ratio * notPursuedCount)));
      dispatch(updateTrueNegative(id, Math.round((1 - ratio) * notPursuedCount)));
    }
  };
}

export function updatePursuedRatio(id, pursuedRatio) {
  return (dispatch, getState) => {
    const isRealityHammer = getIsRealityHammer(getState(), id);

    const baselineCounts = { ...getBaselineCounts(getState(), id) };

    const truePositive =
      isRealityHammer && baselineCounts !== undefined
        ? baselineCounts.truePositive
        : Number(getTruePositive(getState(), id));
    const falsePositive =
      isRealityHammer && baselineCounts !== undefined
        ? baselineCounts.falsePositive
        : Number(getFalsePositive(getState(), id));

    const total = truePositive + falsePositive;
    const ratio = pursuedRatio / 100;

    if (isRealityHammer) {
      baselineCounts.truePositive = Math.round(ratio * total);
      baselineCounts.falsePositive = Math.round((1 - ratio) * total);
      dispatch(updateBaselineCounts(id, baselineCounts));
      dispatch(rescaleCounts(id));
    } else {
      dispatch(updateTruePositive(id, Math.round(ratio * total)));
      dispatch(updateFalsePositive(id, Math.round((1 - ratio) * total)));
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: pursuedRatio,
      key: "pursuedRatio"
    });
  };
}

export function rescaleCounts(id) {
  return (dispatch, getState) => {
    const baselineCounts = getBaselineCounts(getState(), id);

    if (baselineCounts) {
      const total =
        baselineCounts.truePositive +
        baselineCounts.falsePositive +
        baselineCounts.trueNegative +
        baselineCounts.falseNegative;

      const scaledTotal = getScaledTotal(getState(), id) !== undefined ? getScaledTotal(getState(), id) : total;

      const scaleFactor = scaledTotal / total;

      let truePositive = Math.round(baselineCounts.truePositive * scaleFactor);
      let falsePositive = Math.round(baselineCounts.falsePositive * scaleFactor);
      let trueNegative = Math.round(baselineCounts.trueNegative * scaleFactor);
      let falseNegative = Math.round(baselineCounts.falseNegative * scaleFactor);

      const dPercentage =
        (truePositive + falseNegative) / (truePositive + falsePositive + trueNegative + falseNegative);

      const outcomePercentage = getOutcomePercentage(getState(), id) ? outcomePercentage : dPercentage;

      const cm = new ConfusionMatrix(truePositive, trueNegative, falsePositive, falseNegative);

      if (cm) {
        const newActualPositive = Math.max(0, Math.round(cm.getTotal() * outcomePercentage));

        const newCm = cm.copyAndConstrainActualPositives(newActualPositive);

        truePositive = newCm.getTruePositive();
        falsePositive = newCm.getFalsePositive();
        trueNegative = newCm.getTrueNegative();
        falseNegative = newCm.getFalseNegative();

        //dispatch(runReducer.updateCapValue(runId, id));
      }

      return dispatch(
        updateCounts(id, {
          truePositive: truePositive,
          falsePositive: falsePositive,
          trueNegative: trueNegative,
          falseNegative: falseNegative
        })
      );
    }
  };
}

export function updateNotPursuedRatio(id, notPursuedRatio) {
  return (dispatch, getState) => {
    const isRealityHammer = getIsRealityHammer(getState(), id);

    const baselineCounts = { ...getBaselineCounts(getState(), id) };

    const falseNegative =
      isRealityHammer && baselineCounts !== undefined
        ? baselineCounts.falseNegative
        : Number(getFalseNegative(getState(), id));
    const trueNegative =
      isRealityHammer && baselineCounts !== undefined
        ? baselineCounts.trueNegative
        : Number(getTrueNegative(getState(), id));

    const total = falseNegative + trueNegative;
    const ratio = notPursuedRatio / 100;

    if (isRealityHammer) {
      baselineCounts.falseNegative = Math.round(ratio * total);
      baselineCounts.trueNegative = Math.round((1 - ratio) * total);
      dispatch(updateBaselineCounts(id, baselineCounts));
      dispatch(rescaleCounts(id));
    } else {
      dispatch(updateFalseNegative(id, Math.round(ratio * total)));
      dispatch(updateTrueNegative(id, Math.round((1 - ratio) * total)));
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: notPursuedRatio,
      key: "notPursuedRatio"
    });
  };
}

export function updateTryRatio(id, tryRatio) {
  return (dispatch, getState) => {
    const truePositive = Number(getTruePositive(getState(), id));
    const falsePositive = Number(getFalsePositive(getState(), id));
    const trueNegative = Number(getTrueNegative(getState(), id));
    const falseNegative = Number(getFalseNegative(getState(), id));

    const total = truePositive + falsePositive + trueNegative + falseNegative;
    const ratio = tryRatio / 100;

    if (getPursuedRatio(getState(), id) === undefined) {
      dispatch(updatePursuedRatio(id, (truePositive / (truePositive + falsePositive)) * 100));
    }

    const pursuedRatio = getPursuedRatio(getState(), id) === 0 ? 0.01 : getPursuedRatio(getState(), id) / 100;

    /*
    const tpRatio =
      truePositive + falsePositive === 0
        ? pursuedRatio
        : truePositive / (falsePositive + truePositive);
    const fpRatio =
      truePositive + falsePositive === 0
        ? 1 - pursuedRatio
        : falsePositive / (falsePositive + truePositive);
    const tnRatio =
      trueNegative + falseNegative === 0
        ? fpRatio
        : trueNegative / (trueNegative + falseNegative);
    const fnRatio =
      trueNegative + falseNegative === 0
        ? tpRatio
        : falseNegative / (trueNegative + falseNegative);
    */

    const tpRatio = pursuedRatio;
    const fpRatio = 1 - pursuedRatio;
    const tnRatio = trueNegative + falseNegative === 0 ? fpRatio : trueNegative / (trueNegative + falseNegative);
    const fnRatio = trueNegative + falseNegative === 0 ? tpRatio : falseNegative / (trueNegative + falseNegative);

    let tp = Math.round(ratio * tpRatio * total);
    let fp = Math.round(ratio * fpRatio * total);
    let tn = Math.round((1 - ratio) * tnRatio * total);
    let fn = Math.round((1 - ratio) * fnRatio * total);

    const delta = total - (tp + fp + tn + fn);

    if (delta !== 0) {
      if (Math.max(tp, fp, tn, fn) === tp) {
        tp += delta;
      } else if (Math.max(tp, fp, tn, fn) === fp) {
        fp += delta;
      } else if (Math.max(tp, fp, tn, fn) === tn) {
        tn += delta;
      } else if (Math.max(tp, fp, tn, fn) === fn) {
        fn += delta;
      }
    }

    const cm = new ConfusionMatrix(tp, tn, fp, fn);
    dispatch(updateCountsFromConfusionMatrix(id, cm));

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: tryRatio,
      key: "tryRatio"
    });
  };
}

export function updateBaselineCounts(id, baselineCounts) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineCounts,
    key: "baselineCounts"
  };
}

export function updateIsRealityHammer(id, isRealityHammer) {
  return (dispatch, getState) => {
    if (isRealityHammer === true) {
      const baselineCounts = {
        truePositive: getTruePositive(getState(), id),
        falsePositive: getFalsePositive(getState(), id),
        trueNegative: getTrueNegative(getState(), id),
        falseNegative: getFalseNegative(getState(), id)
      };

      dispatch(updateBaselineCounts(id, baselineCounts));

      const scaledTotal = getScaledTotal(getState(), id);
      if (
        scaledTotal !==
        baselineCounts.truePositive +
          baselineCounts.falsePositive +
          baselineCounts.trueNegative +
          baselineCounts.falseNegative
      ) {
        // Forgive me
        dispatch(updateScaledTotal(undefined, id, scaledTotal));
      }

      const outcomePercentage = getOutcomePercentage(getState(), id);

      if (outcomePercentage !== undefined) {
        // For have I sinned
        dispatch(projectReducer.updateOutcomePercentage(undefined, id, outcomePercentage));
      }
    } else {
      const baselineCounts = getBaselineCounts(getState(), id);

      if (baselineCounts !== undefined) {
        const cm = new ConfusionMatrix(
          baselineCounts.truePositive,
          baselineCounts.trueNegative,
          baselineCounts.falsePositive,
          baselineCounts.falseNegative
        );
        dispatch(updateCountsFromConfusionMatrix(id, cm));
      }
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: isRealityHammer,
      key: "isRealityHammer"
    });
  };
}

export function updateCountType(id, countType) {
  return (dispatch, getState) => {
    const blueprint = getBlueprintFromSetup(getState(), id);
    const dataset = getDatasetFromSetup(getState(), id);
    const setup = getSetup(getState(), id);

    let TP = setup.truePositive;
    let FP = setup.falsePositive;
    let FN = setup.falseNegative;
    let TN = setup.trueNegative;

    if (blueprint === undefined) {
      const state = getState();
      const success = getSuccess(state, id);

      let count = 0;

      if (success !== undefined) {
        if (Array.isArray(success)) {
          for (const s of success) {
            count += s.count;
          }
        } else {
          count += success.count;
        }
      }

      if (count !== 0) {
        if (dataset !== undefined) {
          if (countType === "ALL") {
            TP = count;
            FP = dataset.rowCount - count;
            FN = 0;
            TN = 0;
          } else if (countType === "SOME") {
            TP = 0;
            FP = 0;
            FN = 0;
            TN = 0;
          } else if (countType === "NONE") {
            TP = 0;
            FP = 0;
            FN = count;
            TN = dataset.rowCount - count;
          }
        }
      }
    } else {
      const positives =
        blueprint && blueprint.wizard && blueprint.wizard.setup
          ? Number(blueprint.wizard.setup.truePositive) + Number(blueprint.wizard.setup.falseNegative)
          : 0;

      const negatives =
        blueprint && blueprint.wizard && blueprint.wizard.setup
          ? Number(blueprint.wizard.setup.falsePositive) + Number(blueprint.wizard.setup.trueNegative)
          : 0;
      if (countType === "ALL") {
        TP = positives;
        FP = negatives;
        FN = 0;
        TN = 0;
      } else if (countType === "SOME") {
        TP = blueprint && blueprint.wizard && blueprint.wizard.setup ? blueprint.wizard.setup.truePositive : 0;
        FP = blueprint && blueprint.wizard && blueprint.wizard.setup ? blueprint.wizard.setup.falsePositive : 0;
        FN = blueprint && blueprint.wizard && blueprint.wizard.setup ? blueprint.wizard.setup.falseNegative : 0;
        TN = blueprint && blueprint.wizard && blueprint.wizard.setup ? blueprint.wizard.setup.trueNegative : 0;
      } else if (countType === "NONE") {
        TP = 0;
        FP = 0;
        FN = positives;
        TN = negatives;
      }
    }

    if (setup && setup.isRealityHammer && setup.baselineCounts) {
      dispatch(
        updateBaselineCounts(id, {
          truePositive: Number(TP),
          falsePositive: Number(FP),
          falseNegative: Number(FN),
          trueNegative: Number(TN)
        })
      );
    } else {
      dispatch(updateTruePositive(id, Number(TP)));
      dispatch(updateFalsePositive(id, Number(FP)));
      dispatch(updateFalseNegative(id, Number(FN)));
      dispatch(updateTrueNegative(id, Number(TN)));
    }
  };
}

export function updateNPR(id, NPR) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: defaultSetup,
    key: "defaultSetup"
  };
}

export function updateDefaultSetup(id, defaultSetup) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: defaultSetup,
    key: "defaultSetup"
  };
}

export function updateParentLabel(id, parentLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: parentLabel,
    key: "parentLabel"
  };
}

export function updateDefaultTruePositive(id, defaultTruePositive) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: defaultTruePositive,
    key: "defaultTruePositive"
  };
}

export function updateDefaultFalsePositive(id, defaultFalsePositive) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: defaultFalsePositive,
    key: "defaultFalsePositive"
  };
}

export function updateOverestimateLabel(id, overestimateLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: overestimateLabel,
    key: "overestimateLabel"
  };
}

export function updateUnderestimateLabel(id, underestimateLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: underestimateLabel,
    key: "underestimateLabel"
  };
}

export function updateFalseNegativeLabel(id, falseNegativeLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: falseNegativeLabel,
    key: "falseNegativeLabel"
  };
}

export function updateFalsePositiveLabel(id, falsePositiveLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: falsePositiveLabel,
    key: "falsePositiveLabel"
  };
}

export function updateTrueNegativeLabel(id, trueNegativeLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: trueNegativeLabel,
    key: "trueNegativeLabel"
  };
}

export function updateTruePositiveLabel(id, truePositiveLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: truePositiveLabel,
    key: "truePositiveLabel"
  };
}

export function updateAdvancedRevenueLabel(id, advancedRevenueLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: advancedRevenueLabel,
    key: "advancedRevenueLabel"
  };
}

export function updateAdvancedCostLabel(id, advancedCostLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: advancedCostLabel,
    key: "advancedCostLabel"
  };
}

export function updateNetImpactLabel(id, netImpactLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: netImpactLabel,
    key: "netImpactLabel"
  };
}

export function updateCapacityPct(id, capactiyPct) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: capactiyPct,
    key: "capactiyPct"
  };
}

export function updateParentProjectId(id, parentProjectId) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: parentProjectId,
    key: "parentProjectId"
  };
}

export function updateSetupLabels(id, labels) {
  return {
    type: UPDATE_FIELDS,
    id: id,
    data: labels
  };
}

export function updateTrueAction(id, trueAction) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: trueAction,
    key: "trueAction"
  };
}

export function updateFalseAction(id, falseAction) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: falseAction,
    key: "falseAction"
  };
}

export function updateOutcomeLabel(id, outcomeLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: outcomeLabel,
    key: "outcomeLabel"
  };
}

export function updateTransactionLabel(id, transactionLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: transactionLabel,
    key: "transactionLabel"
  };
}

export function updateNotPursuedTransactionLabel(id, notPursuedTransactionLabel) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: notPursuedTransactionLabel,
    key: "notPursuedTransactionLabel"
  };
}

export function updateNarrativeMadlib(id, parameter, madlib) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: madlib,
    key: "narrativeMadlibs"
  };
}

// Use the same time period across the entire project
export function updateTimePeriod(id, timePeriod, overrideOverall = false) {
  if (overrideOverall || !isFinite(id)) {
    return {
      type: UPDATE_FIELD,
      id: id,
      data: timePeriod,
      key: "timePeriod"
    };
  }

  return (dispatch, getState) => {
    const projectId = projectReducer.getProjectIdFromRunId(getState(), id);
    const overallRunId = projectReducer.getOverallRunId(getState(), projectId);
    return dispatch(updateTimePeriod(overallRunId, timePeriod, true));
  };
}

export function updateNonTargetedConversionRate(id, nonTargetedRate) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: nonTargetedRate,
    key: "nonTargetedRate"
  };
}

export function updateNonTargetedConversionCount(id, nonTargetedCount) {
  return (dispatch, getState) => {
    const nonTargetedRate = nonTargetedCount / getNonTargeted(getState(), id);

    return {
      type: UPDATE_FIELD,
      id: id,
      data: nonTargetedRate,
      key: "nonTargetedRate"
    };
  };
}

export function updateHideCosts(id, hideCosts) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: hideCosts,
    key: "hideCosts"
  };
}

export function updateDatasetIdWithScroll(id, datasetId, blueprintId) {
  return (dispatch, getState) => {
    const blueprint = blueprintReducer.getBlueprint(getState(), blueprintId);

    const noScroll =
      blueprintId === "new" ||
      blueprintId === undefined ||
      (blueprint && blueprint.wizard && blueprint.wizard.dataset && blueprint.wizard.dataset.id === undefined)
        ? true
        : false;

    wizardReducer.scrollToDataset(noScroll ? 0 : 250);

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: datasetId,
      key: "datasetId"
    });
  };
}

export function updateDatasetId(
  id,
  datasetId,
  noUpdate = false,
  stepIndex,
  blueprintId,
  checkIgnored = false,
  hasProject = undefined,
  skipBlueprintSet = false,
  nextId
) {
  return (dispatch, getState) => {
    const retBlueprintId = blueprintId ? blueprintId : getBlueprintId(getState(), id);

    const oldDatasetId = getDatasetId(getState(), id);
    const oldSetupLabel = getSetupLabel(getState(), id);

    if (noUpdate !== true) {
      const dataset = getDataset(getState(), datasetId);

      const blueprint = blueprintReducer.getBlueprint(getState(), retBlueprintId);

      const hasTransform =
        blueprint &&
        blueprint.wizard &&
        blueprint.wizard.setup &&
        blueprint.wizard.setup.transform &&
        Object.keys(blueprint.wizard.setup.transform).length > 0;

      if (hasTransform !== true) {
        if (dataset && dataset.rulefit && dataset.rulefit.length > 0) {
          dispatch(transformReducer.addInteractions(id, datasetId, false));
        } else {
          dispatch(transformReducer.updateTransform(id, undefined));
        }
      }

      if (blueprint && blueprint.wizard && blueprint.wizard.setup && blueprint.wizard.setup.success) {
        const success = blueprint.wizard.setup.success;

        if (
          dataset &&
          dataset.outcome &&
          dataset.fields &&
          dataset.fields[dataset.outcome] &&
          dataset.fields[dataset.outcome].data &&
          dataset.fields[dataset.outcome].data.filter((d) => d.value === success.label).length > 0
        ) {
          dispatch(updateSuccess(id, success, true));
        } else {
          dispatch(updateSuccess(id, undefined, true));
        }
      } else {
        dispatch(updateSuccess(id, undefined, true));
      }
    }

    const dataset = getDataset(getState(), datasetId);
    if (
      dataset &&
      dataset.outcome &&
      dataset.fields &&
      dataset.fields[dataset.outcome] &&
      dataset.fields[dataset.outcome].type === "number"
    ) {
      if (getTruePositiveLabel(getState(), id) === defaultSetup.truePositiveLabel) {
        dispatch(updateTruePositiveLabel(id, "Overestimate"));
      }
      if (getFalsePositiveLabel(getState(), id) === defaultSetup.falsePositiveLabel) {
        dispatch(updateFalsePositiveLabel(id, "Underestimate"));
      }
      if (getConversionCost(getState(), id) === defaultSetup.conversionCost) {
        dispatch(updateConversionCost(id, 1));
      }
      if (getConversionGain(getState(), id) === defaultSetup.conversionGain) {
        dispatch(updateConversionGain(id, 1));
      }
    } else if (
      dataset &&
      dataset.outcome &&
      dataset.fields &&
      dataset.fields[dataset.outcome] &&
      dataset.fields[dataset.outcome].type === "categorical"
    ) {
      if (getTruePositiveLabel(getState(), id) === "Overestimate") {
        dispatch(updateTruePositiveLabel(id, defaultSetup.truePositiveLabel));
      }
      if (getFalsePositiveLabel(getState(), id) === "Underestimate") {
        dispatch(updateFalsePositiveLabel(id, defaultSetup.falsePositiveLabel));
      }
      if (getConversionCost(getState(), id) === 1) {
        dispatch(updateConversionCost(id, defaultSetup.conversionCost));
      }
      if (getConversionGain(getState(), id) === 1) {
        dispatch(updateConversionGain(id, defaultSetup.conversionGain));
      }
    }

    dispatch(updateSplitColumn(id, undefined));

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: datasetId,
      key: "datasetId"
    });

    if (nextId) {
      dispatch(nextReducer.updateDatasetId(nextId, datasetId));
    }

    if (checkIgnored === true) {
      // Reset Ignored Fields when choosing a new dataset;
      const newIgnored = getNewDatasetIgnoredFromBp(getState(), datasetId, blueprintId);
      dispatch(updateCurrentIgnored(id, newIgnored));
    }

    if (noUpdate !== true) {
      if ((retBlueprintId === undefined || retBlueprintId === "template") && id === "new") {
        dispatch(wizardReducer.buildSteps(id, "template"));
      }

      if (stepIndex !== undefined) {
        dispatch(wizardReducer.updateStepIndex(stepIndex));
      }
    }

    const realBlueprintId = getBlueprintId(getState(), id);

    if (!hasProject) {
      if (realBlueprintId !== undefined && !skipBlueprintSet) {
        dispatch(blueprintReducer.setBlueprint(id, realBlueprintId, true, true));
      } else {
        if (id === "new") {
          const currState = getState();
          const oldGenName = generateName(currState, oldDatasetId);
          if (oldSetupLabel === defaultSetup.label || oldGenName === oldSetupLabel) {
            dispatch(generateNameAndUpdate("new", dataset && dataset.id));
          }

          const aiReadyOutcomes = homeReducer.getAiReadyOutcomes(getState(), dataset && dataset.id);
          const isForecastReady = homeReducer.getIsForecastReady(getState(), dataset && dataset.id);

          if (!(aiReadyOutcomes && aiReadyOutcomes.length) && isForecastReady && !getIsForecast(getState(), id)) {
            dispatch(updateIsForecastH(id, true));
          } else if (getIsForecast(currState, id)) {
            dispatch(updateIsForecastH(id, false));
          }

          if (getIsForecast(getState(), id)) {
            const forecastOptions = homeReducer.getForecastOptions(getState(), dataset && dataset.id);
            if (forecastOptions && forecastOptions.length === 1) {
              dispatch(updateForecastTimeCol(id, forecastOptions[0].value));
            }
          } else if (getForecastTimeCol(currState, id)) {
            dispatch(updateForecastTimeCol(id, undefined));
          }
        }
      }
    }

    return Promise.resolve();
  };
}

export function updateSelectedMiniBlueprint(id, blueprint) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: blueprint,
    key: "selectedMiniBlueprint"
  };
}

export function updateSelectedBlueprintId(id, bpId) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: bpId,
    key: "selectedBlueprintId"
  };
}

export function updateAcademicResultsStep(id, step) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: step,
    key: "academicResultsStep"
  };
}

export function updateIsForceOptimized(id, newVal) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: newVal,
    key: "isForceOptimized"
  };
}

export function updateBusinessSubgroups(id, subgroups) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: subgroups,
    key: "businessSubgroups"
  };
}

export function updateMinBudget(id, minBudget) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: minBudget,
    key: "minBudget"
  };
}

export function updateMaxBudget(id, maxBudget) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: maxBudget,
    key: "maxBudget"
  };
}

export function updateBudgetRange(id, projectId, budgetRange) {
  return (dispatch, getState) => {
    const conversionCost = getConversionCost(getState(), id);

    const total = getTotal(getState(), id);

    const minBudget = Math.abs((budgetRange[0] / 100) * total * conversionCost);
    const maxBudget = Math.abs((budgetRange[1] / 100) * total * conversionCost);

    dispatch(updateMinBudget(id, minBudget));
    dispatch(updateMaxBudget(id, maxBudget));

    dispatch(projectReducer.setSegmentMinBudgetByRunId(projectId, id, minBudget));
    dispatch(projectReducer.setSegmentMaxBudgetByRunId(projectId, id, maxBudget));

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: budgetRange,
      key: "budgetRange"
    });
  };
}

export function updateLinkedRuns(id, linkedRuns) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: linkedRuns,
    key: "linkedRuns"
  };
}

export function updateLinkedScenarioId(id, linkedScenarioId) {
  return (dispatch, getState) => {
    /*
    const linkedProjectId = getLinkedProjectId(getState(), id);
    const linkedProject = projectReducer.getProject(
      getState(),
      linkedProjectId
    );

    const activeScenario =
      linkedProject &&
      linkedProject.settings &&
      linkedProject.settings.originalScenarios &&
      linkedProject.settings.originalScenarios.filter(
        (s) => s.id === linkedScenarioId
      ).length > 0
        ? linkedProject.settings.originalScenarios.filter(
            (s) => s.id === linkedScenarioId
          )[0]
        : undefined;

    console.log(activeScenario);

    if (linkedProject && linkedProject.segments) {
      const linkedRuns = { ...getLinkedRuns(getState(), id) };

      Object.keys(linkedRuns).forEach((r) => {
        console.log(linkedRuns[r]);
        linkedRuns[r] = { ...linkedRuns[r], linkedRunId: 0 };
      });

      dispatch(setupReducer.updateLinkedRuns(id, linkedRuns));
    }
    */

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: linkedScenarioId,
      key: "linkedScenarioId"
    });
  };
}

export function updateLinkedProjectReportId(id, linkedProjectId) {
  return (dispatch, getState) => {
    const reportId = nextReducer.getNextIdFromProjectId(getState(), linkedProjectId);

    if (reportId || linkedProjectId === undefined) {
      return dispatch({
        type: UPDATE_FIELD,
        id: id,
        data: reportId,
        key: "linkedProjectReportId"
      });
    }
  };
}

export function updateNextId(id, nextId) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: nextId,
      key: "nextId"
    });
  };
}

export function updateLinkedProjectId(id, linkedProjectId) {
  return (dispatch, getState) => {
    dispatch(updateLinkedRuns(id, undefined));

    dispatch(updateLinkedProjectReportId(id, linkedProjectId));

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: linkedProjectId,
      key: "linkedProjectId"
    });
  };
}

export function updateLinkedProjectOutputMode(id, linkedProjectOutputMode) {
  return (dispatch, getState) => {
    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: linkedProjectOutputMode,
      key: "linkedProjectOutputMode"
    });
  };
}

export function updateBusinessSubgroup(id, index, subgroup) {
  return (dispatch, getState) => {
    const currSubgroups = getBusinessSubgroups(getState(), id) || [{}, {}];
    let retSubgroups = [{}, {}];
    if (currSubgroups) {
      retSubgroups.forEach((elem, i) => {
        if (i === index) {
          retSubgroups[i] = { ...subgroup };
        } else {
          retSubgroups[i] = { ...currSubgroups[i] };
        }
      });
    }
    return dispatch(updateBusinessSubgroups(id, retSubgroups));
  };
}

export function updateBusinessSubgroupProp(id, index, key, propValue) {
  return (dispatch, getState) => {
    const subgroups = getBusinessSubgroups(getState(), id) || [{}, {}];
    const subgroup = subgroups && subgroups[index];
    const retSubgroup = {
      ...subgroup,
      [key]: propValue
    };
    return dispatch(updateBusinessSubgroup(id, index, retSubgroup));
  };
}

export function generateName(state, datasetId, nameModifier, overrideDefault) {
  if (overrideDefault) {
    return nameModifier;
  }

  let retName = "No Dataset Selected";
  if (datasetId) {
    const dataset = getDataset(state, datasetId);
    retName = dataset?.label + (nameModifier ? " - " + nameModifier : "");
  }
  return retName;
}

export function generateNameAndUpdate(id, datasetId, nameModifier = "", overrideDefault = false) {
  return (dispatch, getState) => {
    if (globalReducer.getCreateFromProject(getState())) {
      return;
    }

    const retName = generateName(getState(), datasetId, nameModifier, overrideDefault);

    dispatch(updateLabel(id, retName));
    return retName;
  };
}

export function initCustomBlueprint(id, blueprintId) {
  return (dispatch, getState) => {
    const bpSetup = blueprintReducer.getOriginalSetup(getState(), blueprintId) || defaultSetup;
    const currSetup = getSetup(getState(), id);
    const retSetup = {
      ...currSetup,
      ...bpSetup,
      label: currSetup.label,
      blueprintId: currSetup.blueprintId,
      datasetId: currSetup.datasetId,
      success: undefined
    };
    dispatch({
      type: UPDATE_SETUP,
      id: id,
      data: retSetup
    });
    return dispatch(blueprintReducer.checkEvaluator(blueprintId, id));
  };
}

export function checkValueWarning(expectedVal, originalVal, isFieldMapping) {
  expectedVal = Number(expectedVal);
  originalVal = Number(originalVal);
  if (isFieldMapping) {
    return expectedVal !== originalVal ? true : undefined;
  }
  if (expectedVal / (originalVal || 1) >= 3 || originalVal / (expectedVal || 1) >= 3) {
    return true;
  }
  return undefined;
}

export function getWarningMessage(expectedVal, originalVal, expectedLabel, originalLabel, fieldMapping) {
  expectedVal = Number(expectedVal);
  originalVal = Number(originalVal);

  let warning;

  originalLabel = originalLabel || "Current";
  expectedLabel = expectedLabel || "Expected";

  if (checkValueWarning(expectedVal, originalVal, Boolean(fieldMapping))) {
    if (fieldMapping) {
      warning = `The observed average from the dataset is ${originalVal}. When evaluating a prediction, Aible will use the value in the ${fieldMapping} column.`;
    } else if (originalVal > expectedVal) {
      warning =
        "Your " +
        (expectedLabel || "Expected") +
        " is 1/" +
        Math.round(originalVal / (expectedVal || 1)) +
        " the value of the " +
        (originalLabel || "Current") +
        " (" +
        originalVal +
        ").";
    } else {
      warning =
        "Your " +
        (expectedLabel || "Expected") +
        " is " +
        Math.round(expectedVal / (originalVal || 1)) +
        "X higher than the " +
        (originalLabel || "Current") +
        " (" +
        originalVal +
        ").";
    }
  }

  return warning;
}

export function updateBaselineCustomValues(id, baselineCustomValues) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: baselineCustomValues,
    key: "baselineCustomValues"
  };
}

export function initBaselineValues(id) {
  return (dispatch, getState) => {
    const currCustomValues = getCustomValues(getState(), id);
    if (currCustomValues && Object.keys(currCustomValues)?.length) {
      const currBaselineValues = getBaselineCustomValues(getState(), id);
      if (
        Object.keys(currBaselineValues)?.every((k) => {
          return currBaselineValues[k] !== undefined;
        })
      ) {
        return;
      }
      const retBaselineValues = {
        ...currCustomValues,
        ...currBaselineValues
      };
      return dispatch(updateBaselineCustomValues(id, retBaselineValues));
    } else {
      const currValues = {
        baselineConversionGain: getConversionGain(getState(), id),
        baselineConversionCost: getConversionCost(getState(), id),
        baselineConversionMissed: getConversionMissed(getState(), id),
        baselineConversionSaved: getConversionSaved(getState(), id)
      };
      const currBaselineValues = getBaselineCosts(getState(), id);

      if (
        Object.keys(currBaselineValues)?.every((k) => {
          return currBaselineValues[k] !== undefined;
        })
      ) {
        return;
      }

      const retBaselineValues = {};

      Object.keys(currValues)?.forEach((k) => {
        if (currBaselineValues[k] !== undefined) {
          retBaselineValues[k] = currBaselineValues[k];
        } else {
          retBaselineValues[k] = currValues[k];
        }
      });

      dispatch({
        type: UPDATE_FIELDS,
        id: id,
        data: retBaselineValues
      });
    }
  };
}

export function updateIsBaselineRealityHammer(id, isBaselineRealityHammer, skipBaselineInit = false) {
  return (dispatch, getState) => {
    if (!skipBaselineInit && isBaselineRealityHammer) {
      dispatch(initBaselineValues(id));
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: isBaselineRealityHammer,
      key: "isBaselineRealityHammer"
    });
  };
}

export function removeBaselineCustomValues(id, blueprintId) {
  return (dispatch, getState) => {
    dispatch(updateBaselineCustomValues(id, {}));

    console.log(blueprintId);

    return dispatch(blueprintReducer.checkEvaluator(blueprintId, id, true));
  };
}

export function removeBaselineCustomValuesMultipleSegments(ids, blueprintId, overallRunId) {
  return (dispatch, getState) => {
    ids.forEach((id) => {
      dispatch({
        type: UPDATE_FIELD,
        id: id,
        data: {},
        key: "baselineCustomValues"
      });
    });

    console.log(blueprintId);

    return dispatch(blueprintReducer.checkEvaluator(blueprintId, overallRunId, true));
  };
}

export function updateBaselineCustomValue(id, blueprintId, customFieldId, customFieldValue) {
  return (dispatch, getState) => {
    customFieldValue = convertIfNotNumeric(customFieldValue);

    dispatch({
      type: UPDATE_BASELINE_CUSTOM_VALUE,
      id: id,
      data: customFieldValue,
      key: customFieldId
    });

    return dispatch(blueprintReducer.checkEvaluator(blueprintId, id, true));
  };
}

export function updateCustomValue(id, blueprintId, customFieldId, customFieldValue) {
  return (dispatch, getState) => {
    customFieldValue = convertIfNotNumeric(customFieldValue);

    dispatch({
      type: UPDATE_CUSTOM_VALUE,
      id: id,
      data: customFieldValue,
      key: customFieldId
    });

    return dispatch(blueprintReducer.checkEvaluator(blueprintId, id));
  };
}

export function updateCustomValues(id, values) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "customValues",
    data: values
  };
}

export function validateCustomValues(id, blueprintId) {
  return (dispatch, getState) => {
    const customValues = getCustomValues(getState(), id);
    const customFields =
      (blueprintId !== undefined
        ? blueprintReducer.getCustomFieldsTransformed(getState(), blueprintId)
        : getCustomFieldsTransformed(getState(), id)) || [];

    const retValues = {};

    customFields.forEach((field) => {
      if (field && field.id !== undefined) {
        retValues[field.id] = customValues[field.id];
      }
    });

    return dispatch(updateCustomValues(id, retValues));
  };
}

export function updateCustomMapping(id, customFieldId, value) {
  return {
    type: UPDATE_CUSTOM_MAPPING,
    id: id,
    key: customFieldId,
    data: value
  };
}

export function deleteCustomMapping(id, customFieldId) {
  return {
    type: DELETE_CUSTOM_MAPPING,
    id: id,
    key: customFieldId
  };
}

export function toggleCustomMapping(id, blueprintId, customFieldId) {
  return (dispatch, getState) => {
    const currMapping = getCustomMapping(getState(), id, customFieldId);
    if (currMapping) {
      return dispatch(deleteCustomMapping(id, customFieldId));
    } else {
      return dispatch(updateCustomMapping(id, customFieldId, true));
    }
  };
}

export function setCustomMapping(id, blueprintId, customFieldId, datasetColumn) {
  return (dispatch, getState) => {
    const state = getState();
    const datasetId = getDatasetId(state, id);
    const fields = homeReducer.getFields(state, datasetId);
    const colData = fields[datasetColumn];
    const retValue = colData && !isNaN(colData.mean) ? colData.mean : 0;
    dispatch(updateCustomMapping(id, customFieldId, datasetColumn));
    return dispatch(updateCustomValue(id, blueprintId, customFieldId, retValue));
  };
}

export function updateLabelFromDatasetId(id, datasetId) {
  return (dispatch, getState) => {
    const dataset = getDataset(getState(), datasetId);

    if (dataset && dataset.label) {
      return dispatch(updateLabel(id, dataset.label));
    }
  };
}

export function updateUseCase(id, useCase) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: useCase,
    key: "useCase"
  };
}

export function updateCurrentIgnored(id, ignored) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: ignored,
    key: "ignored"
  };
}

export function getUpdatedIgnoredObject(state, id, column, isIgnored) {
  const currIgnored = getIgnored(state, id);
  const defaultIgnored = getIgnored(state, id, undefined, true);
  const retIgnored = {
    ...currIgnored
  };

  let retColumns;
  if (typeof column === "string") {
    retColumns = [column];
  } else {
    retColumns = [...column];
  }

  retColumns.forEach((c) => {
    if (isIgnored) {
      retIgnored[c] = isIgnored;
    } else {
      if (defaultIgnored[c]) {
        retIgnored[c] = false;
      } else {
        delete retIgnored[c];
      }
    }
  });

  return retIgnored;
}

export function updateIgnored(id, column, isIgnored) {
  return (dispatch, getState) => {
    const projectId = projectReducer.getProjectIdFromRunId(getState(), id);
    if (projectReducer.getIsOverallSegment(getState(), projectId, id)) {
      const projectRunIds = projectReducer
        .getProjectSegments(getState(), projectId)
        .map((segment) => {
          return projectReducer.getSegmentRunId(getState(), projectId, segment.id);
        })
        .filter((runId) => runId && runId !== id);
      projectRunIds.forEach((currRunId) => {
        dispatch(updateIgnored(currRunId, column, isIgnored));
      });
    }

    dispatch(createDefaultSetup(id));

    const splitColumn = getSplitColumn(getState(), id);

    if (
      column !== undefined &&
      splitColumn !== undefined &&
      (column === splitColumn || (typeof column !== "string" && column.includes(splitColumn)))
    ) {
      dispatch(updateSplitColumn(id, undefined));
    }

    const retIgnored = getUpdatedIgnoredObject(getState(), id, column, isIgnored);

    if (isNaN(id)) {
      return dispatch(updateCurrentIgnored(id, retIgnored));
    } else {
      return dispatch(runReducer.updateCurrentIgnored(id, retIgnored));
    }
  };
}

export function createDefaultSetup(id) {
  return (dispatch, getState) => {
    const { defaultSetup, ...retDefaultSetup } = getSetup(getState(), id);
    if (!defaultSetup) {
      dispatch({
        type: UPDATE_FIELD,
        id: id,
        key: "defaultSetup",
        data: JSON.parse(JSON.stringify({ data: retDefaultSetup }))
      });
    }
  };
}

export function getUpdatedIgnoredValueObject(state, id, column, value, isIgnored) {
  const currIgnoredValue = getIgnoredValue(state, id);
  const defaultIgnoredValue = getIgnoredValue(state, id, undefined, true);
  const retIgnoredValue = {
    ...currIgnoredValue
  };

  if (!retIgnoredValue[column]) {
    retIgnoredValue[column] = {};
  }

  const retValues = Array.isArray(value) ? value : [value];

  if (isIgnored) {
    if (!retIgnoredValue[column]) {
      retIgnoredValue[column] = {};
    }
    retValues.forEach((currValue) => {
      retIgnoredValue[column][currValue] = true;
    });
  } else {
    retValues.forEach((currValue) => {
      if (defaultIgnoredValue[column] && defaultIgnoredValue[column][currValue]) {
        if (!retIgnoredValue[column]) {
          retIgnoredValue[column] = {};
        }
        retIgnoredValue[column][currValue] = false;
      } else {
        if (retIgnoredValue[column]) {
          delete retIgnoredValue[column][currValue];
        }
      }
    });

    if (!Object.keys(retIgnoredValue[column] || {}).length) {
      delete retIgnoredValue[column];
    }
  }

  return retIgnoredValue;
}

export function updateIgnoredValue(id, column, value, isIgnored) {
  return (dispatch, getState) => {
    const projectId = projectReducer.getProjectIdFromRunId(getState(), id);
    if (projectReducer.getIsOverallSegment(getState(), projectId, id)) {
      const projectRunIds = projectReducer
        .getProjectSegments(getState(), projectId)
        .map((segment) => {
          return projectReducer.getSegmentRunId(getState(), projectId, segment.id);
        })
        .filter((runId) => runId && runId !== id);
      projectRunIds.forEach((currRunId) => {
        dispatch(updateIgnoredValue(currRunId, column, value, isIgnored));
      });
    }

    dispatch(createDefaultSetup(id));

    const retIgnoredValue = getUpdatedIgnoredValueObject(getState(), id, column, value, isIgnored);

    if (isNaN(id)) {
      return dispatch(updateIgnoredValues(id, retIgnoredValue));
    } else {
      return dispatch(runReducer.updateCurrentIgnoredValue(id, retIgnoredValue));
    }
  };
}

export function updateIgnoredValues(id, ignoredValues) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "ignoredValue",
    data: ignoredValues
  };
}

export function updateIsQuickRun(id, isQuickRun) {
  return (dispatch, getState) => {
    if (isQuickRun === true) {
      dispatch(updateConversionGain(id, 1));
      dispatch(updateConversionMissed(id, -1));
    }

    return dispatch(updateRunType(id, "QuickRun"));
  };
}

export function updateIsTwoMinute(id, isQuickRun) {
  return updateRunType(id, "TwoMinute");
}

export function updatePriceColumn(id, column) {
  return { type: UPDATE_FIELD, id: id, data: column, key: "priceColumn" };
}

export function updateSplitColumn(id, column) {
  return (dispatch, getState) => {
    if (isNaN(id) && !column) {
      dispatch(updateEnableFairnessMetrics(id, undefined));
    }
    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: column,
      key: "splitColumn"
    });
  };
}

export function addSplitColumn(id) {
  return (dispatch, getState) => {
    const dataset = getDatasetFromSetup(getState(), id);

    const splitColumn = { ...getSplitColumn(getState(), id) };
    const column = splitColumn ? splitColumn.column : undefined;

    if (splitColumn) {
      splitColumn.splitGroups = {
        ...getSplitColumn(getState(), id).splitGroups
      };

      const keys = Object.keys(splitColumn.splitGroups);
      const newKey = keys.reduce((maxKey, key) => Math.max(maxKey, parseInt(key) + 1), keys.length);

      splitColumn.splitGroups[newKey] = {
        id: newKey,
        fields: {}
      };

      if (dataset && dataset.fields && dataset.fields[column] && dataset.fields[column].data) {
        for (const field of dataset.fields[column].data) {
          splitColumn.splitGroups[newKey].fields[field.value] = {
            ...field,
            isIncluded: false
          };
        }
      }
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: splitColumn,
      key: "splitColumn"
    });
  };
}

export function editSplitColumn(id, splitId, sColumn, isAdd) {
  return (dispatch, getState) => {
    const splitColumn = { ...getSplitColumn(getState(), id) };

    if (splitColumn) {
      splitColumn.splitGroups = {
        ...getSplitColumn(getState(), id).splitGroups
      };
      splitColumn.splitGroups[splitId].fields[sColumn].isIncluded = isAdd;
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: splitColumn,
      key: "splitColumn"
    });
  };
}

export function deleteSplitColumn(id, splitId) {
  return (dispatch, getState) => {
    const splitColumn = { ...getSplitColumn(getState(), id) };

    if (splitColumn) {
      splitColumn.splitGroups = {
        ...getSplitColumn(getState(), id).splitGroups
      };
      delete splitColumn.splitGroups[splitId];
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: splitColumn,
      key: "splitColumn"
    });
  };
}

function getFieldsCount(fieldsArr) {
  return (fieldsArr || []).reduce((total, elem) => total + elem.count, 0);
}

/* old
export function createSetupFromSplit(id, splitColumn, splitGroup) {
  return (dispatch, getState) => {
    const state = getState();
    const dataset = getDatasetFromSetup(state, id);
    const sColumn = getSplitColumn(state, id);
    const success = getSuccess(state, id);

    const setup = JSON.parse(JSON.stringify(getSetup(state, id)));
    setup.defaultSetup = {};
    setup.models = {};

    const processLimit = getProcessLimit(state, id);
    const falseNegative = getFalseNegative(state, id);
    const falsePositive = getFalsePositive(state, id);
    const trueNegative = getTrueNegative(state, id);
    const truePositive = getTruePositive(state, id);
    const label = getLabel(state, id);

    const datasetFields =
      (dataset && dataset.fields && dataset.fields[splitColumn] && dataset.fields[splitColumn].data) || [];
    const totalPop = getFieldsCount(datasetFields);
    const splitPop = getFieldsCount(
      datasetFields.filter(
        elem =>
          splitGroup &&
          splitGroup.fields &&
          splitGroup.fields[elem.value] &&
          splitGroup.fields[elem.value].isIncluded !== false
      )
    );

    const includedFields = Object.values(splitGroup.fields)
      .map(elem => (elem.isIncluded ? elem.value : ""))
      .filter(elem => elem);

    const newLimit = parseInt((processLimit / totalPop) * splitPop);
    const newFalseNegative = parseInt((falseNegative / totalPop) * splitPop);
    const newFalsePositive = parseInt((falsePositive / totalPop) * splitPop);
    const newTrueNegative = parseInt((trueNegative / totalPop) * splitPop);
    const newTruePositive = parseInt((truePositive / totalPop) * splitPop);

    dispatch(resetSetup(id));

    dispatch(updateSetup(id, setup));

    dispatch(updateDatasetId(id, dataset.id));

    dispatch(updateSuccess(id, success, true));

    dispatch(updateRelatedId(id, id, true));

    // hate myself
    dispatch({ type: UPDATE_FIELD, id: id, data: sColumn, key: "splitColumn" });

    for (const fKey of Object.keys(splitGroup.fields)) {
      const field = splitGroup.fields[fKey];

      if (field.isIncluded === false) {
        dispatch(updateIgnoredValue(id, splitColumn, field.value, true));
      }
    }

    dispatch(updateProcessLimit(id, newLimit));
    dispatch(updateTruePositive(id, newTruePositive));
    dispatch(updateTrueNegative(id, newTrueNegative));
    dispatch(updateFalsePositive(id, newFalsePositive));
    dispatch(updateFalseNegative(id, newFalseNegative));
    dispatch(updateLabel(id, label + " - " + includedFields.join(", ")));

    return dispatch(updateStep(300));
  };
}
*/

export function createSetupFromSpecial(id, specialVals) {
  return (dispatch, getState) => {
    const state = getState();

    const setup = { ...getSetup(state, id), businessSubgroups: [{}, {}] };

    const dataset = getDatasetFromSetup(state, id);

    const processLimit = getProcessLimit(state, id);
    const falseNegative = getFalseNegative(state, id);
    const falsePositive = getFalsePositive(state, id);
    const trueNegative = getTrueNegative(state, id);
    const truePositive = getTruePositive(state, id);
    const label = getLabel(state, id);
    const parentLabel = getParentLabel(state, id);

    let splitPop = 0;
    let totalPop = 0;

    let labelVals = [];
    let labelField = "";

    let includeVals = {};

    for (const s of specialVals) {
      if (includeVals[s.column] === undefined) {
        includeVals[s.column] = [s.value];
      } else {
        includeVals[s.column].push(s.value);
      }
    }

    for (const include of Object.keys(includeVals).map((i) => ({
      field: i,
      vals: includeVals[i]
    }))) {
      if (dataset && dataset.fields && dataset.fields[include.field]) {
        const f = dataset.fields[include.field];

        const ignoredValue = JSON.parse(JSON.stringify(getIgnoredValue(getState(), id, undefined, undefined, true)));

        if (f.data) {
          let fieldCount = 0;
          let fieldSplit = 0;
          for (const val of f.data) {
            if (include.vals.indexOf(val.value) === -1) {
              if (ignoredValue[include.field] === undefined) {
                ignoredValue[include.field] = {
                  [val.value]: true
                };
              } else {
                ignoredValue[include.field][val.value] = true;
              }
              // Creating total pop count
              fieldCount += val.count || 0;
            } else {
              // Creating total pop count and split count
              fieldCount += val.count || 0;
              fieldSplit += val.count || 0;
              include.field && (labelField = include.field);
              val.value && labelVals.push(val.value);

              if (ignoredValue[include.field] === undefined) {
                ignoredValue[include.field] = {
                  [val.value]: false
                };
              } else {
                ignoredValue[include.field][val.value] = false;
              }
            }
          }
          splitPop = Math.max(fieldSplit, splitPop);
          totalPop = Math.max(fieldCount, totalPop);
        }

        if (isNaN(id)) {
          dispatch(updateIgnoredValues(id, ignoredValue));
        } else {
          dispatch(runReducer.updateCurrentIgnoredValue(id, ignoredValue));
        }
      }
    }

    totalPop = totalPop || 0;

    const newLimit = Math.round((processLimit / totalPop) * splitPop) || 0;
    const newFalseNegative = Math.round((falseNegative / totalPop) * splitPop) || 0;
    const newFalsePositive = Math.round((falsePositive / totalPop) * splitPop) || 0;
    const newTrueNegative = Math.round((trueNegative / totalPop) * splitPop) || 0;
    const newTruePositive = Math.round((truePositive / totalPop) * splitPop) || 0;

    dispatch(updateProcessLimit(id, newLimit, true));
    const newCm = new ConfusionMatrix(newTruePositive, newTrueNegative, newFalsePositive, newFalseNegative);
    dispatch(updateCountsFromConfusionMatrix(id, newCm));

    dispatch(updatePrimaryBusinessConstraintType(undefined, id, undefined));
    if (labelField && labelVals.length > 0) {
      dispatch(updateLabel(id, (parentLabel ? parentLabel + " " : "") + labelField + "=" + labelVals.join(",")));
    }

    const rSetup = {
      ...getSetup(getState(), id),
      businessSubgroups: [{}, {}],
      defaultSetup: undefined
    };

    delete rSetup.defaultSetup;

    dispatch(
      updateSetup(id, {
        ...rSetup,
        defaultSetup: {
          data: rSetup
        }
      })
    );

    dispatch(updateParentProjectId(id, id));

    return dispatch(updateStep(300));
  };
}

export function updateRunType(id, runType) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: runType,
    key: "runType"
  };
}

export function setDefaults(id) {
  return (dispatch, getState) => {
    const state = getState();

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: getConversionGain(state, id),
      key: "defaultTruePositiveImpact"
    });

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: getConversionCost(state, id),
      key: "defaultFalsePositiveImpact"
    });

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: getConversionSaved(state, id),
      key: "defaultTrueNegativeImpact"
    });

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: getConversionMissed(state, id),
      key: "defaultFalseNegativeImpact"
    });
  };
}

export function resetDefaults(id) {
  return (dispatch, getState) => {
    const state = getState();

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: getDefaultTruePositiveImpact(state, id),
      key: "conversionGain"
    });

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: getDefaultFalsePositiveImpact(state, id),
      key: "conversionCost"
    });

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: getDefaultTrueNegativeImpact(state, id),
      key: "conversionSaved"
    });

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: getDefaultFalseNegativeImpact(state, id),
      key: "conversionMissed"
    });
  };
}

export function getIsDefaultImpacts(state, id) {
  return (
    Number(getConversionGain(state, id)) === Number(getDefaultTruePositiveImpact(state, id)) &&
    Number(getConversionCost(state, id)) === Number(getDefaultFalsePositiveImpact(state, id)) &&
    Number(getConversionSaved(state, id)) === Number(getDefaultTrueNegativeImpact(state, id)) &&
    Number(getConversionMissed(state, id)) === Number(getDefaultFalseNegativeImpact(state, id))
  );
}

export function updateImpact(id, impactType, impact) {
  return (dispatch, getState) => {
    if (impactType === 0) {
      return dispatch(updateConversionGain(id, impact));
    } else if (impactType === 1) {
      return dispatch(updateConversionCost(id, impact));
    } else if (impactType === 2) {
      return dispatch(updateConversionSaved(id, impact));
    } else if (impactType === 3) {
      return dispatch(updateConversionMissed(id, impact));
    }
  };
}

export function updateMode(id, mode) {
  return { type: UPDATE_FIELD, id: id, data: mode, key: "mode" };
}

export function updateIsSimpleCosts(id, isSimpleCosts) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: isSimpleCosts,
    key: "isSimpleCosts"
  };
}

export function updateIsHideCounts(id, isHideCounts) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: isHideCounts,
    key: "isHideCounts"
  };
}

export function updateModels(id, models) {
  return { type: UPDATE_FIELD, id: id, data: models, key: "models" };
}

export function updateModelOptions(id, modelId, isIncluded) {
  return {
    type: UPDATE_MODELS,
    id: id,
    key: modelId,
    data: { isIncluded: isIncluded }
  };
}

export function updateModelOptionsField(id, modelOptions) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: modelOptions,
    key: "modelOptions"
  };
}

export function updateTransform(id, transform) {
  return { type: UPDATE_FIELD, id: id, data: transform, key: "transform" };
}

export function updateUseModelList(id, useModelList) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: useModelList,
    key: "useModelList"
  };
}

export function resetConstraints(id, isFullPopulation = false) {
  return (dispatch, getState) => {
    const state = getState();

    dispatch(updateAimToHit(id, getTruePositive(state, id)));
    return dispatch(
      updateProcessLimit(
        id,
        isFullPopulation ? getTotal(state, id) : getTruePositive(state, id) + getFalsePositive(state, id),
        true
      )
    );
  };
}

export function openFreshSetup() {
  return (dispatch, getState) => {
    dispatch(updateSetup("new", defaultSetup));
    dispatch(updateStep(0));

    return dispatch(push("create/new"));
  };
}

export function initSetup(id, forceRefresh = false) {
  return (dispatch, getState) => {
    const state = getState();

    const oldSetup = getSetup(state, id);
    const dataset = getDatasetFromSetup(state, id);

    const modelOptions = modelOptionsReducer.getModelOptions(state);

    if (modelOptions && Object.keys(modelOptions).length === 0) {
      dispatch(modelOptionsReducer.fetchModelOptions()).then((response) =>
        dispatch(updateModelOptionsField(id, modelOptionsReducer.getModelOptions(getState())))
      );
    }

    if (Object.keys(oldSetup).length <= 1 || forceRefresh) {
      return dispatch(
        updateSetup(id, {
          ...defaultSetup,
          datasetId: oldSetup.datasetId,
          label: dataset ? dataset.label : "New Run",
          ignoredValue: oldSetup.ignoredValue,
          isBlueprint: oldSetup.isBlueprint,
          modelOptions: modelOptions
        })
      );
    }
  };
}

export function resetSetupToDefault(id, skipDataset = false, generateName = false) {
  return (dispatch, getState) => {
    const currSetup = getSetup(getState(), id);
    const retSetup = {
      ...defaultSetup
    };
    if (skipDataset) {
      retSetup.datasetId = currSetup.datasetId;
      retSetup.success = currSetup.success;
    }
    dispatch(updateSetup("new", retSetup));
    if (generateName) {
      dispatch(generateNameAndUpdate("new", currSetup.datasetId));
    }
    if (skipDataset) {
      dispatch(updateDatasetId(id, currSetup.datasetId, true));
    }
  };
}

export function initNewSetup(id, datasetId) {
  return (dispatch, getState) => {
    const retSetup = { ...defaultSetup, datasetId: datasetId };

    const dataset = homeReducer.getDataset(getState(), datasetId);

    let success;

    if (
      dataset?.target !== undefined &&
      dataset?.target !== null &&
      dataset?.target_value &&
      homeReducer.getOutcomeType(getState(), datasetId) !== "number"
    ) {
      let successValue = String(dataset.target_value[0]);
      const currSuccess = getSuccess(getState(), id);
      const currDatasetSuccess =
        currSuccess && dataset?.fields?.[dataset.target]?.data?.find((e) => e.value === currSuccess?.value);
      if (currSuccess && currDatasetSuccess) {
        success = currDatasetSuccess;
      } else {
        success = dataset?.fields?.[dataset.target]?.data?.find((e) => e.value === successValue);
      }
    }

    if (dataset?.transformations?.projections) {
      const projections = dataset?.transformations?.projections ?? [];

      retSetup.fieldTypes = { ...retSetup.fieldTypes };

      for (const p of projections) {
        if (typeof p != "string" && "column" in p && ["categorical", "category"].includes(p["col_type"])) {
          retSetup.fieldTypes[p["column"]] = "Categorical";
        }
      }
    }

    if (success) {
      retSetup.success = {
        ...success,
        label: success.value
      };
    }

    dispatch(updateSetup(id, retSetup));

    return getSetup(getState(), id);
  };
}

export function resetSetupFromDefault(id) {
  return (dispatch, getState) => {
    const setup = getSetup(getState(), id);

    dispatch(
      updateSetup(id, {
        ...setup.defaultSetup.data,
        defaultSetup: { data: setup.defaultSetup.data }
      })
    );
  };
}

export function resetSetup(id = "new") {
  return (dispatch, getState) => {
    if (id === "new") {
      const oldSetup = getSetup(getState(), id);

      dispatch(updateSetup(id, oldSetup && oldSetup.defaultSetup ? { ...oldSetup.defaultSetup } : { ...defaultSetup }));
    } else {
      dispatch(updateSetup(id, defaultSetup));
    }

    return Promise.resolve();
  };
}

export function initModifiedRun(id, blueprintId, checkForceOptimized = false) {
  return (dispatch, getState) => {
    const state = getState();
    return dispatch(initJobFromWizard(id, blueprintId));

    // Removing forced rerun into optimized
    // initJobFromWizard with `isForceName` set to be dependent on whether this is a quickRun(Academic)
  };
}

export function calculateRatio({ gain, saved, cost, missed }) {
  const Pbe = (saved - cost) / (saved - cost + gain - missed);
  const benefitConst = 100000;
  const costConst = benefitConst / (1 - Pbe) - benefitConst;

  const denominator = Math.min(benefitConst, costConst) || 1;
  const gainRatio = benefitConst / denominator;
  const costRatio = costConst / denominator;
  return {
    costRatio,
    gainRatio
  };
}

export function initJobFromWizard(
  id,
  blueprintId,
  // options passed through as an object for keyword args
  {
    isForceAible = false,
    isForceName = false,
    isSplitting = false,
    isNoRedirect = false,
    isUpdateDataset = false,
    skipBusinessCheck = false,
    isFastTrain = false,
    retStep = undefined,
    source = undefined,
    skipStrategyReset = false,
    nextId = undefined,
    forecastPeriodsOverride = undefined,
    forceSetup = undefined
  } = {}
) {
  return (dispatch, getState) => {
    const state = getState();

    const comparisonOptions = getComparisonOptions(state, id);

    const businessSubgroups = getBusinessSubgroups(state, id);

    const tags = getTags(state, id);

    dispatch(updateCostSliderRatio(id, undefined));

    const datasetId = getDatasetFromSetup(state, id).id;

    const outcome = getDataset(state, datasetId).outcome;

    const dataset = getDataset(state, datasetId);
    const datasetAccountId = homeReducer.getDatasetAccountId(state, datasetId);

    const isForecast = getIsForecast(state, id);

    const isContinuous = getOutcomeType(getState(), id) === "number";

    const success = isContinuous ? undefined : getSuccess(state, id);

    let name = getLabel(state, id);

    const priceColumn = getPriceColumn(state, id);

    const outcomeSuccess = success
      ? Array.isArray(success)
        ? success.map((s) => s.label)
        : (success.rawValue ?? success.value)
      : undefined;

    if (blueprintId && blueprintId !== "new") {
      dispatch(updateBlueprintId(id, blueprintId));
    }

    const transform = transformReducer.getTransform(getState(), id);

    dispatch(updateTransform(id, transform));

    const valueReplace = getValueReplace(getState(), id);
    const endState = getEndState(getState(), id);

    const setup = forceSetup !== undefined ? forceSetup : getSetup(getState(), id);

    const ignored = getIgnored(state, id, undefined, undefined, true);
    const ignoredValue = getIgnoredValue(state, id, undefined, undefined, true);

    const aibleMode = wizardReducer.getAibleMode(state);

    if (isForceAible !== true && (aibleMode === wizardReducer.AIBLE_MODES.ACADEMIC || blueprintId === "academic")) {
      // This is where the forcing of cost ratio to 1:1 happens, if second arg is set to true
      dispatch(updateIsQuickRun(id, false));
    }

    if (getSetupLabel(getState(), id)) {
      name = getSetupLabel(getState(), id);
    }

    const retSetup = {
      ...getSetup(getState(), id),
      isFastTrain: isFastTrain,
      ignored: ignored,
      ignoredValue: ignoredValue
    };

    if (retSetup.blueprintId) {
      const advancedInputs = blueprintReducer.getAdvancedInputs(getState(), retSetup.blueprintId);
      const customFields = blueprintReducer.getCustomFields(getState(), retSetup.blueprintId);
      retSetup.advancedInputs = advancedInputs;
      retSetup.customFields = customFields;
    }

    if (isForceAible) {
      retSetup.runType = undefined;
      retSetup.blueprintId = "AIBLE";
    }

    if (isForceName) {
      const oldLabel = retSetup.label + "";
      const modifier =
        oldLabel.length > ACADEMIC_LABEL.length
          ? oldLabel.substring(oldLabel.length - ACADEMIC_LABEL.length, oldLabel.length)
          : "";

      if (modifier && modifier === ACADEMIC_LABEL) {
        let label = oldLabel.substring(0, oldLabel.length - ACADEMIC_LABEL.length);
        label += OPTIMIZED_LABEL;
        retSetup.label = label;
        name = label;
      }
    }

    retSetup.replace_column_values = {};

    if (valueReplace !== undefined) {
      for (const v of Object.keys(valueReplace)) {
        const vReplace = valueReplace[v];

        if (retSetup.replace_column_values[vReplace.column] === undefined) {
          retSetup.replace_column_values[vReplace.column] = {
            [vReplace.to]: [vReplace.from]
          };
        } else {
          if (retSetup.replace_column_values[vReplace.column][vReplace.to] === undefined) {
            retSetup.replace_column_values[vReplace.column][vReplace.to] = [vReplace.from];
          } else {
            retSetup.replace_column_values[vReplace.column][vReplace.to].push(vReplace.from);
          }
        }
      }
    }

    if (true || isUpdateDataset) {
      const { defaultSetup, ...retDSetup } = retSetup;
      retSetup.defaultSetup = { data: { ...retDSetup } };
    }

    const defaultToRevenueStrategy = false;

    if (!isSplitting && !skipStrategyReset && defaultToRevenueStrategy) {
      retSetup.primaryBusinessConstraintType = projectOptimizer.SegmentMetric.Revenue;
      retSetup.businessConstraints = {
        [projectOptimizer.SegmentMetric.Revenue]: {
          type: projectOptimizer.SegmentMetric.Revenue,
          mode: BusinessConstraintMode.Percent,
          min: 1
        }
      };
    }

    let testTrainSplitCondition = undefined;
    let testTrainValidationCondition = undefined;
    if (comparisonOptions?.dateField) {
      const dateField = comparisonOptions.dateField;
      const dates = dataset?.fields?.[dateField]?.data ?? [];

      const lastIndex = Math.max(0, dates.length - 1);
      const secondLastIndex = Math.max(0, dates.length - 2);

      const referenceStart = comparisonOptions?.referenceStart ?? dates[0]?.value;
      const referenceEnd = comparisonOptions?.referenceEnd ?? dates[secondLastIndex]?.value;
      const currentStart = comparisonOptions?.currentStart ?? dates[secondLastIndex]?.value;
      const currentEnd = comparisonOptions?.currentEnd ?? dates[lastIndex]?.value;

      if (referenceStart && referenceEnd) {
        testTrainSplitCondition = {
          type: ConditionType.And,
          conditions: [
            comparisonCondition(
              dateField,
              referenceEnd,
              referenceEnd !== currentStart ? ConditionType.LessThanOrEqual : ConditionType.LessThan
            ),
            comparisonCondition(dateField, referenceStart, ConditionType.GreaterThanOrEqual)
          ]
        };
      }
      if (currentStart && currentEnd) {
        testTrainValidationCondition = {
          type: ConditionType.And,
          conditions: [
            comparisonCondition(dateField, currentEnd, ConditionType.LessThanOrEqual),
            comparisonCondition(dateField, currentStart, ConditionType.GreaterThanOrEqual)
          ]
        };
      }
    } else if (comparisonOptions) {
      delete retSetup.comparisonOptions;
      if (retSetup?.defaultSetup?.data?.comparisonOptions) {
        delete retSetup.defaultSetup.data.comparisonOptions;
      }
    }

    const setupState = JSON.stringify(retSetup);

    dispatch(updateEnableButton(false));

    const threshold = getThreshold(getState(), id);

    let errorCostRatio = getConversionCost(getState(), id) / getConversionGain(getState(), id);
    let valueMatrix = [];
    if (isContinuous) {
      const retGain = getConversionGain(getState(), id);
      const retCost = getConversionCost(getState(), id);

      const costRatio = 1;
      const gainRatio = retGain / retCost;

      errorCostRatio = costRatio / gainRatio;
      valueMatrix = [
        -1,
        threshold ? Math.abs(threshold[0]) + "%" : "25%",
        threshold ? Math.abs(threshold[1]) + "%" : "75%",
        -2,
        Math.abs(costRatio),
        Math.min(10, Math.abs(gainRatio))
      ];
    } else {
      const retGain = getConversionGain(getState(), id);
      const retCost = getConversionCost(getState(), id);
      const retSaved = getConversionSaved(getState(), id);
      const retMissed = getConversionMissed(getState(), id);

      const { costRatio, gainRatio } = calculateRatio({
        gain: retGain,
        cost: retCost,
        saved: retSaved,
        missed: retMissed
      });

      const retCostRatio = 1;
      const retGainRatio = Math.max(10, gainRatio);

      errorCostRatio = retCostRatio / retGainRatio;
      valueMatrix = [retGainRatio, -1 * retCostRatio, retCostRatio, -1 * retGainRatio];
    }

    const nonTargeted = getFalseNegative(state, id) + getTrueNegative(state, id);
    const currentCapacity = getTruePositive(state, id) + getFalsePositive(state, id);

    const dropColumns = Object.keys(ignored)
      .filter((ignoredColumn) => ignored[ignoredColumn] === true)
      .filter((ignoredColumn) => dataset && dataset.fields && dataset.fields[ignoredColumn] !== undefined)
      .map((ignoredColumn) => ignoredColumn);

    let selectedFields = getSelectedFields(state, id);

    const OPERATION_TRANSFORM_MAP = {
      "+": "BinarySumTransform",
      "-": "BinaryDifferenceTransform",
      "*": "BinaryProductTransform",
      "/": "BinaryRatioTransform",
      document: "DocumentTransform",
      sentiment: "SentimentTransform",
      image: "ImageTransform",
      image_text: "ImageTextTransform",
      translate: "TextTranslationTransform",
      speech_audio: "AudioTextTransform"
    };

    const derivedColumns = transformReducer
      .getTransformRequest(state, id)
      .map((f) => {
        const firstField = f[0];
        const secondField = f[1];

        if (dataset && dataset.fields[firstField] && dataset.fields[secondField]) {
          return f;
        }

        const fields = Object.keys(selectedFields) || [];

        return [fields.find(firstField), fields.find(secondField), f[2]];
      })
      .filter((f) => f[0] !== undefined && f[1] !== undefined)
      .map((t) => {
        switch (t[2]) {
          case "+":
          case "-":
          case "*":
          case "/":
            return {
              type: OPERATION_TRANSFORM_MAP[t[2]],
              vname1: t[0],
              vname2: t[1]
            };

          case "document":
          case "sentiment":
          case "image":
          case "image_text":
          case "translate":
          case "speech_audio":
            return {
              type: OPERATION_TRANSFORM_MAP[t[2]],
              variable: t[0]
            };
        }

        return undefined;
      })
      .filter((t) => t !== undefined && t.type !== undefined);

    console.log("derived columns test", derivedColumns);

    getDataTransforms(state, id)
      .filter((t) => derivedColumns.find((x) => t.type == x.type && t.variable == x.variable) === undefined)
      .forEach((x) => derivedColumns.push(x));

    const dropRows = Object.keys(getIgnoredValue(state, id))
      .filter((column) => dataset.fields[column]?.data !== undefined)
      .map((column) => {
        const hasOther = dataset.fields[column].data.some((x) => x.value == "{Other}");
        const otherIsIgnored = getIgnoredValue(state, id)[column]["{Other}"] == true;

        if (!hasOther || !otherIsIgnored) {
          return {
            key: column,
            type: "categorical",
            labels: Object.keys(getIgnoredValue(state, id)[column])
              .filter((v) => getIgnoredValue(state, id)[column][v] === true)
              .map((v) => (v == "{Missing}" ? null : v))
          };
        }

        const values = dataset.fields[column].data
          .map((x) => x.value)
          .filter((value) => getIgnoredValue(state, id)[column][value] !== true)
          .map((v) => (v == "{Missing}" ? null : v));

        return {
          key: column,
          type: "categorical",
          operation: "include",
          labels: values
        };
      });

    const fieldTypes = getFieldTypes(state, id);

    const colTypes = {};

    Object.keys(fieldTypes).map((field) => {
      colTypes[field] = fieldTypes[field].toLowerCase();
    });

    if (id !== "new" && nextId === undefined) {
      const setup = getSetup(getState(), id);

      setup &&
        setup.defaultSetup &&
        setup.defaultSetup.data &&
        dispatch(
          updateSetup(id, {
            ...setup.defaultSetup.data,
            defaultSetup: { ...setup.defaultSetup }
          })
        );
    }

    const specialIndex = getSpecialIndex(getState(), id);

    const modelOptions = getModelOptionsArr(getState(), id);

    const modelNames =
      getUseModelList(getState(), id) === true ? modelOptions && modelOptions.map((m) => m.name) : undefined;

    const splitColumn = getSplitColumn(getState(), id);

    console.log("SPLIT COLUMN", splitColumn);
    console.log("RUNID CREATE", id);

    let forecastColumn;
    if (isContinuous) {
      forecastColumn = getForecastTimeCol(getState(), id);
    }
    const BYOMURL = getBYOMURL(getState(), id);

    const executorCheck = () => {
      if (isForecast && false) {
        return dispatch(deploymentsReducer.getExecutorEndpoint(datasetAccountId));
      } else {
        return Promise.resolve();
      }
    };

    return executorCheck()
      .then(() =>
        runModels(
          errorCostRatio,
          setupState,
          datasetId,
          datasetAccountId,
          outcome,
          outcomeSuccess !== undefined ? outcomeSuccess : "",
          true,
          valueMatrix,
          nonTargeted,
          currentCapacity,
          dropColumns,
          dropRows,
          derivedColumns,
          tags,
          name,
          priceColumn,
          isForecast,
          modelNames,
          colTypes,
          forecastColumn,
          BYOMURL,
          testTrainSplitCondition,
          testTrainValidationCondition,
          forecastPeriodsOverride
        )
      )
      .then((response) => {
        const oldSetup = getSetup(getState(), id);

        dispatch(updateSetup(response.id, retSetup));

        const runId = response.id;

        return dispatch(pollJob(runId));
      })
      .then((job) => {
        const runId = job.id;

        if (isUpdateDataset) {
          return runId;
        } else if (isSplitting !== true) {
          if (splitColumn !== undefined) {
            return dispatch(projectReducer.createSplitsFromRunId(runId, splitColumn, nextId)).then(
              (projectId) => projectId
            );
          }

          return dispatch(projectReducer.createProject(runId, retSetup, nextId)).then((project) => {
            dispatch(pollJob(runId));

            dispatch(updateEnableButton(true));

            if (isSplitting !== true) {
              dispatch(updateLastProjectRunId(runId));
              if (project) {
                dispatch(updateJob(runId, { isCreating: true }));
                dispatch(globalReducer.updateCurrentProjectId(project.id));

                if (nextId !== undefined) {
                  console.log("next create");
                  return Promise.resolve(project.id);
                } else if (isEnabled("aible-business-simple") || (source && source === "business")) {
                  dispatch(push("/business/" + project.id));
                } else {
                  dispatch(push("/project/" + project.id));
                }
              }

              dispatch(updateLastRunId(runId));
            }

            if (retStep !== undefined) {
              dispatch(unifiedProjectReducer.selectStep(project.id, retStep));
            }

            if (nextId !== undefined) {
              return Promise.resolve(project.id);
            }

            return Promise.resolve(runId);
          });
        } else {
          // dispatch(pollJob(runId));

          dispatch(updateEnableButton(true));

          return Promise.resolve(runId);
        }
      });
  };
}

export default setupReducer;
